import useAPI from "@toothfairy/shared-api/useApi";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppCard from "@toothfairy/shared-ui/AppCard";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTab from "@toothfairy/shared-ui/AppTab";
import AppInputLabel from "@toothfairy/shared-ui/AppInputLabel";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppSwitch from "@toothfairy/shared-ui/AppSwitch";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import axios from "axios";
import dayjs from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  TouchableOpacity,
  useWindowDimensions,
  ActivityIndicator,
} from "react-native";
import { Popable } from "react-native-popable";
import { isOllamaRunning } from "../../../../../../App";
import envConfig from "../../../../../../envConfig";
import authorisationsHelper from "../../../../../API/Authorisations/authorisationsHelper";
import entitySnapshotsHelper from "../../../../../API/EntitySnapshots/entitySnapshotsHelper";
import Files from "../../../../../API/Files";
import Media from "../../../../../API/Media";
import Agents from "../../../../../API/Agents";
import hostingsHelper from "../../../../../API/Hostings/hostingsHelper";
import messagesHelper from "../../../../../API/Messages/messagesHelper";
import jobsHelper from "../../../../../API/Jobs/jobsHelper";
import triggersHelper from "../../../../../API/Triggers/triggersHelper";
import alertsHelper from "../../../../../API/Alerts/alertsHelper";
import sipAPI from "../../../../../API/Channels/sipAPI";
import trainingAPI from "../../../../../API/Training/trainingAPI";
import trainingLogHelper from "../../../../../API/TrainingLog/trainingLogHelper";
import AudioPlaceholder from "../../../../../Components/AudioPlaceholder";
import AudioRecorder from "../../../../../Components/AudioRecorder";
import ClonedVoiceControl from "../../../../../Components/ClonedVoiceControl";
import VoiceCloningModal from "../../../../../Components/VoiceCloningModal";
import ImagePlaceholder from "../../../../../Components/ImagePlaceholder";
import ModelPricingInfo from "../../../../../Components/ModelPricingInfo";
import FineTunedModelStatusChip from "../../../../../Components/ToothFairyComponents/FineTunedModelStatusChip";
import FineTunedModelPricingInfo from "../../../../../Components/ToothFairyComponents/FineTunedModelPricingInfo";
import ToothFairyButton from "../../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ColorPickerField from "../../../../../Components/ToothFairyComponents/ColorPickerField";
import Constants, { default as AppConfig } from "../../../../../Constants";
import useOllamaModels from "../../../../../Hooks/useOllamaModels";
import useWorkspaces from "../../../../../Hooks/useWorkspaces";
import useSettingsChipHref from "../../../../../Hooks/useSettingsChipHref";
import useKnowledgeHubChipHref from "../../../../../Hooks/useKnowledgeHubChipHref";
import { useHistory } from "../../../../../Router";
import Utility, { generateRowId } from "../../../../../Utility";
import Color from "../../../../../Utility/Color";
import { convertImageToPng } from "../../../../../Utility/imageUtils";
import IdCopyView from "../../../../../Components/ToothFairyComponents/IdCopyView";
import AgentSchedulingPanel from "../../SubComponents/AgentSchedulingPanel";
import { getErrorSections } from "../../SubComponents/BuilderSections";

const AGENT_SECTION_FIELD_MAP = {
  profile: ["label", "description", "subject", "icon", "icoUrl", "largeLogo"],
  instructions: [
    "goals",
    "interpolationString",
    "pertinencePassage",
    "inhibitionPassage",
    "defaultAnswer",
    "noKnowledgeDefaultAnswer",
  ],
  knowledge: ["maxTopics"],
  internet: ["internetSearchLocation"],
  planning: [
    "maxPlanningSteps",
    "planningInstructions",
    "reviewInstructions",
    "plannerExecutionAttempts",
  ],
  capabilities: ["customToolingInstructions"],
  desktop: [
    "agentDesktopCredentials",
    "agentDesktopBrowser",
    "maxBrowserSteps",
  ],
  advanced: [
    "temperature",
    "maxTokens",
    "qaUrl",
    "dynamicModelSelectionInstructions",
  ],
};
import AgentWidgetPreview from "../../../../Agents/Components/AgentWidgetPreview";
import {
  getHookOptionDescription,
  getToolOptionDescription,
  makeOptionLabelWithDescription,
} from "./ToolOptionLabel";
import ToolsInsightPanel from "./ToolsInsightPanel";
import {
  useModels,
  useExternalModels,
  useRegionalModelIds,
} from "../../../../Common/baseModels";
import colorsMappings from "../../../../Common/colorsMappings";
import {
  ALL_VOICES,
  AUDIO_GENERATION_TTS_MODELS,
  LLM_VOICE_MODELS,
  STT_MODELS,
  TTS_MODELS,
  formatVoiceDisplayName,
  getLanguagesByModel,
  getSpeechTagsMentions,
} from "../../../../Common/voiceModels";
import { isLargeScreen } from "../../../../MainViewContainer";
import AgentWorkflowBuilder from "../../../../../Components/AgentWorkflowBuilder";
import objectDiffUtils from "../../../../../Utils/objectDiffUtils";
import semverHelper from "../../../../../Utils/semverHelper";
import {
  getEffectiveRegion,
  isVoiceAgentsAvailableInRegion,
  isLiveModeAvailableInRegion,
} from "../../../../../Utils/regionWebUrl";
import VersionDiffSummary from "../../../../../Components/VersionComparison/VersionDiffSummary";
import VersionDiffView from "../../../../../Components/VersionComparison/VersionDiffView";

// Regex to check if model should show reasoner dropdown
// Matches GPT-5, Sonnet, Opus, and Gemini models
const shouldShowReasonerDropdown = (modelId) => {
  if (!modelId) return false;
  const reasonerPattern = /gpt[-_]?5|sonnet|opus|gemini/i;
  return reasonerPattern.test(modelId);
};

// Deterministic stringify for form values: stable key ordering and skips
// undefined fields so programmatic rewrites don't produce false diffs.
const stableFormValuesStringify = (obj) => {
  if (obj === undefined) return "";
  if (obj === null || typeof obj !== "object") return JSON.stringify(obj);
  if (Array.isArray(obj))
    return "[" + obj.map(stableFormValuesStringify).join(",") + "]";
  return (
    "{" +
    Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort()
      .map((k) => JSON.stringify(k) + ":" + stableFormValuesStringify(obj[k]))
      .join(",") +
    "}"
  );
};

// DEBUG helper: list the top-level fields that differ between the baseline and
// the current values, so we can see exactly what marks the form dirty.
const diffValueKeys = (baselineObj, currentObj) => {
  if (!baselineObj || !currentObj) return [];
  const keys = new Set([
    ...Object.keys(baselineObj),
    ...Object.keys(currentObj),
  ]);
  const changed = [];
  for (const k of keys) {
    const a = stableFormValuesStringify(baselineObj[k]);
    const b = stableFormValuesStringify(currentObj[k]);
    if (a !== b) {
      changed.push(k);
    }
  }
  return changed;
};

const diffPreview = (v) => {
  try {
    const s = stableFormValuesStringify(v);
    return s?.length > 140 ? s.slice(0, 140) + `…(${s.length})` : s;
  } catch (e) {
    return String(v);
  }
};

const TF_DIRTY_DEBUG = true;
let _dirtySeq = 0;
const dirtyDebug = (tag, ...args) => {
  if (!TF_DIRTY_DEBUG) return;
  try {
    console.log(`[TF_DIRTY] #${++_dirtySeq} ${tag}`, ...args);
  } catch (e) {}
};

const BuilderSection = ({
  title,
  icon,
  children,
  style,
  onExpandedChange,
  isFullWidth,
  hideHeader,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  useEffect(() => {
    onExpandedChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <View style={[{ width: "100%" }, style]}>
      {!hideHeader && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingBottom: 10,
            marginBottom: 14,
            borderBottomWidth: 1,
            borderBottomColor: currentTheme?.theme?.lightGrey,
            width: "100%",
          }}
        >
          {icon && (
            <AppIcon
              icon={icon}
              size={16}
              color={currentTheme?.theme?.primary}
              style={{ marginRight: 9 }}
            />
          )}
          <AppText fontWeight="600" style={{ fontSize: 16 }}>
            {title}
          </AppText>
        </View>
      )}
      <View
        style={
          isFullWidth
            ? { width: "100%" }
            : { width: "100%", maxWidth: 600, alignSelf: "center" }
        }
      >
        {children}
      </View>
    </View>
  );
};

// Placeholder shown inside a builder section whose content is not available
// for the agent's current mode (e.g. Tools for a chatter/casual agent).
const SectionUnavailable = ({ icon, title, message, suggestedModes }) => {
  const { currentTheme } = AppTheme.useTheme();
  return (
    <View
      style={{
        width: "100%",
        maxWidth: 600,
        alignSelf: "center",
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 42,
        paddingHorizontal: 24,
      }}
    >
      {icon && (
        <AppIcon
          icon={icon}
          size={30}
          color={currentTheme?.theme?.mediumGrey}
        />
      )}
      <AppText
        fontWeight="600"
        style={{ marginTop: 14, textAlign: "center", fontSize: 15 }}
      >
        {title}
      </AppText>
      <AppText
        style={{
          marginTop: 8,
          textAlign: "center",
          fontSize: 13,
          lineHeight: 19,
          maxWidth: 420,
        }}
        color={currentTheme?.theme?.darkGrey}
      >
        {message}
        {suggestedModes?.length > 0
          ? ` Supported modes: ${suggestedModes.join(", ")}.`
          : ""}
      </AppText>
    </View>
  );
};

const fieldSwitchWrapper = {
  paddingHorizontal: 10,
  paddingVertical: 12,
  marginTop: 4,
  marginBottom: 4,
};

const columns = [
  {
    title: "settings.qa.benchmark",
    width: 0.4,
  },
  {
    title: "settings.qa.score",
    width: 0.3,
  },
  {
    title: "settings.qa.datetime",
    width: 0.3,
  },
];

const feedbackColumns = [
  {
    title: "settings.qa.message",
    width: 0.45,
  },
  {
    title: "settings.qa.feedback",
    width: 0.35,
  },
  {
    title: "settings.qa.datetime",
    width: 0.2,
  },
  {
    title: "common.actions",
    isAction: true,
    width: 0.00001,
  },
];

export const _tools = [
  // ===== KNOWLEDGE & SEARCH TOOLS =====
  {
    id: "rag",
    name: "settings.qa.rag",
    description: "settings.qa.ragDesc",
    icon: "rag",
    mode: "retriever",
    category: "knowledge",
  },
  {
    id: "deep_rag",
    name: "settings.qa.deepRag",
    description: "settings.qa.deepRagDesc",
    icon: "rag",
    mode: "retriever",
    category: "knowledge",
  },
  {
    id: "browse_knowledge_hub",
    name: "settings.qa.browseKnowledgeHub",
    description: "settings.qa.browseKnowledgeHubDesc",
    icon: "rag",
    mode: "retriever",
    category: "knowledge",
  },
  {
    id: "read_knowledge_doc",
    name: "settings.qa.readKnowledgeDoc",
    description: "settings.qa.readKnowledgeDocDesc",
    icon: "rag",
    mode: "retriever",
    category: "knowledge",
  },
  {
    id: "internet_search",
    name: "settings.qa.internetSearch",
    description: "settings.qa.internetSearchDesc",
    icon: "search",
    mode: "retriever",
    category: "search",
  },
  {
    id: "deep_internet_search",
    name: "settings.qa.deepInternetSearch",
    description: "settings.qa.deepInternetSearchDesc",
    icon: "search",
    mode: "retriever",
    category: "search",
  },
  // ===== MEDIA GENERATION TOOLS =====
  {
    id: "image_generation",
    name: "settings.qa.imageGeneration",
    description: "settings.qa.imageGenerationDesc",
    icon: "image",
    mode: "chatter",
    category: "media_generation",
  },
  {
    id: "image_adaptation",
    name: "settings.qa.imageAdaptation",
    description: "settings.qa.imageAdaptationDesc",
    icon: "image",
    mode: "chatter",
    category: "media_generation",
  },
  {
    id: "video_generation",
    name: "settings.qa.videoGeneration",
    description: "settings.qa.videoGenerationDesc",
    icon: "video",
    mode: "chatter",
    category: "media_generation",
  },
  {
    id: "audio_generation",
    name: "settings.qa.audioGeneration",
    description: "settings.qa.audioGenerationDesc",
    icon: "audio",
    mode: "chatter",
    category: "media_generation",
  },
  {
    id: "3d_model_generation",
    name: "settings.qa.model3dGeneration",
    description: "settings.qa.model3dGenerationDesc",
    icon: "3d",
    mode: "chatter",
    category: "media_generation",
  },
  // ===== MEDIA ANALYSIS TOOLS =====
  {
    id: "analyze_image",
    name: "settings.qa.analyzeImage",
    description: "settings.qa.analyzeImageDesc",
    icon: "image",
    mode: "retriever",
    category: "media_analysis",
  },
  {
    id: "analyze_video",
    name: "settings.qa.analyzeVideo",
    description: "settings.qa.analyzeVideoDesc",
    icon: "video",
    mode: "retriever",
    category: "media_analysis",
  },
  {
    id: "analyze_audio",
    name: "settings.qa.analyzeAudio",
    description: "settings.qa.analyzeAudioDesc",
    icon: "audio",
    mode: "retriever",
    category: "media_analysis",
  },
  // ===== CODE EXECUTION & FILE OPERATIONS =====
  {
    id: "code_interpreter",
    name: "settings.qa.executeCode",
    description: "settings.qa.executeCodeDesc",
    icon: "code",
    mode: "coder",
    category: "code",
  },
  {
    id: "read_file",
    name: "settings.qa.readFile",
    description: "settings.qa.readFileDesc",
    icon: "file",
    mode: "all",
    category: "files",
  },
  {
    id: "write_file",
    name: "settings.qa.writeFile",
    description: "settings.qa.writeFileDesc",
    icon: "file",
    mode: "retriever",
    category: "files",
  },
  {
    id: "edit_file",
    name: "settings.qa.editFile",
    description: "settings.qa.editFileDesc",
    icon: "file",
    mode: "retriever",
    category: "files",
  },
  {
    id: "create_file",
    name: "settings.qa.createFile",
    description: "settings.qa.createFileDesc",
    icon: "file",
    mode: "retriever",
    category: "files",
  },
  {
    id: "list_files",
    name: "settings.qa.listFiles",
    description: "settings.qa.listFilesDesc",
    icon: "file",
    mode: "retriever",
    category: "files",
  },
  {
    id: "images_retrieval",
    name: "settings.qa.imagesRetrieval",
    description: "settings.qa.imagesRetrievalDesc",
    icon: "image",
    mode: "retriever",
    category: "files",
  },
  {
    id: "local_folder_access",
    name: "settings.qa.localFolderAccess",
    description: "settings.qa.localFolderAccessDesc",
    icon: "folder",
    mode: "retriever",
    category: "files",
  },
  // ===== CANVAS TOOLS =====
  {
    id: "canvas_write",
    name: "settings.qa.canvasWrite",
    description: "settings.qa.canvasWriteDesc",
    icon: "message",
    mode: "retriever",
    category: "canvas",
  },
  {
    id: "canvas_append",
    name: "settings.qa.canvasAppend",
    description: "settings.qa.canvasAppendDesc",
    icon: "message",
    mode: "retriever",
    category: "canvas",
  },
  {
    id: "canvas_replace",
    name: "settings.qa.canvasReplace",
    description: "settings.qa.canvasReplaceDesc",
    icon: "message",
    mode: "retriever",
    category: "canvas",
  },
  {
    id: "canvas_read",
    name: "settings.qa.canvasRead",
    description: "settings.qa.canvasReadDesc",
    icon: "message",
    mode: "retriever",
    category: "canvas",
  },
  // ===== PLANNING TOOLS =====
  {
    id: "create_plan",
    name: "settings.qa.createPlan",
    description: "settings.qa.createPlanDesc",
    icon: "scheduling",
    mode: "retriever",
    category: "planning",
  },
  {
    id: "update_task_status",
    name: "settings.qa.updateTaskStatus",
    description: "settings.qa.updateTaskStatusDesc",
    icon: "scheduling",
    mode: "retriever",
    category: "planning",
  },
  {
    id: "revise_plan",
    name: "settings.qa.revisePlan",
    description: "settings.qa.revisePlanDesc",
    icon: "scheduling",
    mode: "retriever",
    category: "planning",
  },
  {
    id: "submit_plan",
    name: "settings.qa.submitPlan",
    description: "settings.qa.submitPlanDesc",
    icon: "scheduling",
    mode: "retriever",
    category: "planning",
  },
  {
    id: "scheduling",
    name: "settings.qa.scheduling",
    description: "settings.qa.schedulingDesc",
    icon: "scheduling",
    mode: "retriever",
    category: "planning",
  },
  // ===== AGENT DELEGATION =====
  {
    id: "delegate_to_agent",
    name: "settings.qa.delegateToAgent",
    description: "settings.qa.delegateToAgentDesc",
    icon: "agent",
    mode: "retriever",
    category: "delegation",
  },
  // ===== MEMORY TOOLS =====
  {
    id: "extract_memory",
    name: "settings.qa.extractMemory",
    description: "settings.qa.extractMemoryDesc",
    icon: "memory",
    mode: "all",
    category: "memory",
  },
  {
    id: "recall_memory",
    name: "settings.qa.recallMemory",
    description: "settings.qa.recallMemoryDesc",
    icon: "memory",
    mode: "all",
    category: "memory",
  },
  {
    id: "long_term_memory",
    name: "settings.qa.longTermMemory",
    description: "settings.qa.longTermMemoryDesc",
    icon: "memory",
    mode: "all",
    category: "memory",
  },
  // ===== USER INTERACTION TOOLS =====
  {
    id: "notify_user",
    name: "settings.qa.notifyUser",
    description: "settings.qa.notifyUserDesc",
    icon: "message",
    mode: "all",
    category: "interaction",
  },
  {
    id: "ask_user_question",
    name: "settings.qa.askUserQuestion",
    description: "settings.qa.askUserQuestionDesc",
    icon: "question_icon",
    mode: "retriever",
    category: "interaction",
  },
  // ===== SKILLS =====
  {
    id: "use_skill",
    name: "settings.qa.useSkill",
    description: "settings.qa.useSkillDesc",
    icon: "skill",
    mode: "retriever",
    category: "skills",
  },
  // ===== BROWSER AUTOMATION =====
  {
    id: "browser",
    name: "settings.qa.browseWeb",
    description: "settings.qa.browseWebDesc",
    icon: "computerUser",
    mode: "retriever",
    category: "browser",
  },
  // ===== DESKTOP AUTOMATION =====
  {
    id: "control_desktop",
    name: "settings.qa.controlDesktop",
    description: "settings.qa.controlDesktopDesc",
    icon: "computerUser",
    mode: "retriever",
    category: "desktop",
  },
  // ===== INTROSPECTION =====
  {
    id: "introspection",
    name: "settings.qa.introspection",
    description: "settings.qa.introspectionDesc",
    icon: "settings",
    mode: "all",
    category: "introspection",
  },
  // ===== COMMUNICATION TOOLS =====
  {
    id: "email",
    name: "settings.qa.sendEmail",
    description: "settings.qa.sendEmailDesc",
    icon: "email",
    mode: "retriever",
    category: "communication",
  },
  {
    id: "sms",
    name: "settings.qa.sendSms",
    description: "settings.qa.sendSmsDesc",
    icon: "sms",
    mode: "retriever",
    category: "communication",
  },
  {
    id: "messaging",
    name: "settings.qa.sendWhatsApp",
    description: "settings.qa.sendWhatsAppDesc",
    icon: "messaging",
    mode: "retriever",
    category: "communication",
  },
  // ===== LEGACY / COMPATIBILITY TOOLS =====
  {
    id: "canvas",
    name: "settings.qa.canvas",
    description: "settings.qa.canvasDesc",
    icon: "message",
    mode: "retriever",
    category: "canvas",
  },
  {
    id: "charting",
    name: "settings.qa.charting",
    description: "settings.qa.chartingDesc",
    icon: "charting",
    mode: "all",
    category: "charting",
  },
  {
    id: "conversation_retrieval",
    name: "settings.qa.conversationRetrieval",
    description: "settings.qa.conversationRetrievalDesc",
    icon: "message",
    mode: "all",
    category: "knowledge",
  },
  {
    id: "doc_by_doc_analysis",
    name: "settings.qa.crossDocumentAnalysis",
    description: "settings.qa.crossDocumentAnalysisDesc",
    icon: "message",
    mode: "retriever",
    category: "knowledge",
  },
  {
    id: "deep_thinking",
    name: "settings.qa.deepThinking",
    description: "settings.qa.deepThinkingDesc",
    icon: "reasoning",
    mode: "all",
    category: "reasoning",
  },
  {
    id: "analyze",
    name: "settings.qa.analyze",
    description: "settings.qa.analyzeDesc",
    icon: "analyze",
    mode: "retriever",
    category: "analysis",
  },
  {
    id: "reasoning",
    name: "settings.qa.reasoning",
    description: "settings.qa.reasoningDesc",
    icon: "reasoning",
    mode: "retriever",
    category: "reasoning",
  },
];

const controller = new AbortController();
const internetSearchLocations = [
  {
    name: "Australia",
  },
  {
    name: "Canada",
  },
  {
    name: "New Zealand",
  },
  {
    name: "United States",
  },
  {
    name: "United Kingdom",
  },
  {
    name: "Japan",
  },
  {
    name: "Mexico",
  },
  {
    name: "Mumbai, Maharashtra, India",
  },
  {
    name: "Bangkok, Bangkok, Thailand",
  },
  {
    name: "Paris, Paris, Ile-de-France, France",
  },
];

const _proModels = [];

const convertContextWithFunctionCallingLabel = (mode, t) => {
  if (mode == "retriever") return t("settings.qa.llmToolsProvider");
  else if (mode == "planner") return t("settings.list.planning");
  else if (mode == "computer") return t("settings.list.llmToolsProvider");
  else {
    return t("settings.qa.llmToolsProvider");
  }
};

const getFilteredTools = (tools, values, t) => {
  return tools
    ?.filter((u) => {
      // ===== COMMUNICATION TOOLS =====
      // Email, SMS, WhatsApp require sender configuration
      if (u?.id === "email" && !values?.emailAsSender) return false;
      if (u?.id === "sms" && !values?.phoneNumberAsSender) return false;
      if (u?.id === "messaging" && !values?.whatsappNumberAsSender)
        return false;

      // ===== KNOWLEDGE & SEARCH TOOLS =====
      // RAG tools require topics configured (hasTopicsContext)
      if (
        [
          "rag",
          "deep_rag",
          "browse_knowledge_hub",
          "read_knowledge_doc",
        ].includes(u?.id)
      ) {
        if (!values?.hasTopicsContext && values?.mode !== "voice") return false;
      }
      // Internet search requires explicit enable
      if (u?.id === "internet_search" && !values?.allowInternetSearch)
        return false;
      if (u?.id === "deep_internet_search" && !values?.allowDeepInternetSearch)
        return false;

      // ===== MEDIA GENERATION TOOLS =====
      if (
        ["image_generation", "image_adaptation"].includes(u?.id) &&
        !values?.allowImagesGeneration
      )
        return false;
      if (u?.id === "video_generation" && !values?.allowVideosGeneration)
        return false;
      if (u?.id === "audio_generation" && !values?.allowAudioGeneration)
        return false;
      if (u?.id === "3d_model_generation" && !values?.allow3dModelGeneration)
        return false;

      // ===== MEDIA ANALYSIS TOOLS =====
      if (
        ["analyze_image", "analyze_video", "analyze_audio"].includes(u?.id) &&
        !values?.hasImages
      )
        return false;

      // ===== CODE EXECUTION =====
      if (u?.id === "code_interpreter" && !values?.hasCode) return false;

      // ===== FILE OPERATIONS =====
      // File read/write operations require either code execution or local folder binding
      if (
        [
          "read_file",
          "write_file",
          "edit_file",
          "create_file",
          "list_files",
        ].includes(u?.id)
      ) {
        const hasCodeExecution = values?.hasCode || values?.mode === "coder";
        const hasLocalFolder = !!window?.electronAPI;
        if (!hasCodeExecution && !hasLocalFolder) return false;
      }
      if (u?.id === "images_retrieval" && !values?.hasImages) return false;
      if (u?.id === "local_folder_access" && !window?.electronAPI) return false;

      // ===== CANVAS TOOLS =====
      // Canvas requires Agentic Tooling (Legacy or 2.0)
      if (
        [
          "canvas",
          "canvas_write",
          "canvas_append",
          "canvas_replace",
          "canvas_read",
        ].includes(u?.id)
      ) {
        if (!values?.agenticRAG && !values?.useInterleavedReasoning)
          return false;
      }

      // ===== PLANNING TOOLS =====
      // Planning tools require Agentic Tooling
      if (
        [
          "create_plan",
          "update_task_status",
          "revise_plan",
          "submit_plan",
          "scheduling",
        ].includes(u?.id)
      ) {
        if (!values?.agenticRAG && !values?.useInterleavedReasoning)
          return false;
      }

      // ===== AGENT DELEGATION =====
      if (
        u?.id === "delegate_to_agent" &&
        (!values?.agentsPool || values?.agentsPool?.length === 0)
      )
        return false;

      // ===== MEMORY TOOLS =====
      if (
        ["extract_memory", "recall_memory", "long_term_memory"].includes(u?.id)
      ) {
        if (!values?.longTermMemoryEnabled) return false;
      }

      // ===== BROWSER AUTOMATION =====
      // Browser requires Agentic Tooling AND VM configured
      if (u?.id === "browser") {
        if (!values?.agenticRAG && !values?.useInterleavedReasoning)
          return false;
        if (!values?.agentDesktopVM && !values?.vms) return false;
      }

      // ===== INTROSPECTION =====
      // Introspection requires Retriever mode + Agentic Tooling
      if (u?.id === "introspection") {
        if (values?.mode !== "retriever") return false;
        if (!values?.agenticRAG && !values?.useInterleavedReasoning)
          return false;
      }

      // ===== USER INTERACTION =====
      if (u?.id === "ask_user_question" && values?.allowAskQuestion === false)
        return false;
      // notify_user is always available - no filtering needed

      // ===== CHARTING =====
      if (u?.id === "charting" && !values?.charting) return false;

      // ===== MODE FILTERING =====
      // Filter by agent mode (tools with mode="all" are always available)
      if (u?.mode !== "all" && u?.mode !== values?.mode) return false;

      // ===== SPECIAL CASE: Deep Thinking =====
      // Deep thinking only for specific models or when reasoning is enabled
      if (u?.id === "deep_thinking") {
        const isReasonerModel = ["sorcerer_15", "mystica_15"].includes(
          values?.llmBaseModel,
        );
        if (!isReasonerModel && !values?.useInterleavedReasoning) return false;
      }

      // ===== SPECIAL CASE: RAG in voice mode =====
      // RAG is available in voice mode even without hasTopicsContext
      if (u?.id === "rag" && values?.mode === "voice") return true;

      return true;
    })
    ?.map((i) => {
      return {
        ...i,
        label: i.name,
      };
    })
    ?.sort((a, b) => a.label.localeCompare(b.label));
};

// Cloned voice IDs are UUIDs (Cartesia voice IDs) — never in the preset list.
const isClonedVoiceId = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    v || "",
  );

const QaSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  availableFunctions,
  agentsChannels,
  isChatBot,
  isBusinessAnalyst,
  initialMapValues,
  isContentCreator,
  customCodeExecutionEnvironments,
  availableFunctionsLoaded,
  customCodeExecutionEnvironmentsLoaded,
  clearMemory,
  isAsyncProcessInProgress,
  saveVersion,
  isAIGenerated,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const { height } = useWindowDimensions();
  const entityName = "Prompting";
  const AppToasty = AppToast.useToast();
  const [agentImage, setAgentImage] = React.useState(null);
  const [agentLogo, setAgentLogo] = React.useState(null);
  const [isAudioRecording, setIsAudioRecording] = useState(false);
  const [voiceCloneInProgress, setVoiceCloneInProgress] = useState(false);
  const [showVoiceCloningModal, setShowVoiceCloningModal] = useState(false);
  const [finetunedModels, setFinetunedModels] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [oLlamaModels, setOllamaModels] = useState([]);
  const [isAgentOllama, setIsAgentOllama] = useState(false);
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const {
    getTrainingLogsWithModels,
    getWorkspaceEntities,
    getPlanConfig,
    getActiveWorkspace,
    getWorkspaceUsers,
    loadAgents,
    getFinetunedModels,
    getBenchmarksByAgent,
    getUserName,
  } = useWorkspaces();
  const {
    error,
    loadedModel,
    downloadingModel,
    modelsAvailable,
    isLoading,
    loadModel,
    pullProgress,
    pullStatus,
  } = useOllamaModels();
  const {
    baseModels: _baseModels,
    functionCallingModels: _functionCallingModels,
    visionModels,
  } = useModels();
  const {
    externalModels: _externalModels,
    externalDeprecatedModels: _externalDeprecatedModels,
    externalProviders: _externalProviders,
  } = useExternalModels();
  const _regionalModelIds = useRegionalModelIds();
  const [_agents, _setAgents] = useState([]);
  const [_authorisations, _setAuthorisations] = useState([]);
  const [availableAlerts, setAvailableAlerts] = useState([]);
  const workspaceid = getActiveWorkspace()?.id;
  const chipHrefExecEnvs = useSettingsChipHref("EXEC_ENVS");
  const chipHrefFunctions = useSettingsChipHref("FUNCTIONS");
  const chipHrefAuthorisations = useSettingsChipHref("AUTHORISATIONS");
  const chipHrefRoles = useSettingsChipHref("ROLES_SETTINGS");
  const chipHrefChannels = useSettingsChipHref("CHANNELS");
  const chipHrefJobs = useSettingsChipHref("JOBS");
  const chipHrefTriggers = useSettingsChipHref("TRIGGERS");
  const chipHrefKnowledgeHubDoc = useKnowledgeHubChipHref();
  const [fileUploadInProgress, setFileUploadInProgress] = React.useState(false);
  const [fileUploadProgress, setFileUploadProgress] = React.useState(0);
  const [disableMultiModal, setDisableMultiModal] = React.useState(false);
  const [disableDueToTranslation, setDisableDueToTranslation] = React.useState(
    false,
  );
  const [agentFeedbackList, setAgentFeedbackList] = React.useState([]);
  const [codeUploadDisabled, setCodeUploadDisabled] = useState(false);
  const [errorsOnModerationRules, setErrorsOnModerationRules] = React.useState(
    [],
  );
  const [lastOpened, setLastOpened] = useState(Date.now());
  const [audioFileUploadProgress, setAudioFileUploadProgress] = useState(null);
  const [initialLoadCompleted, setInitialLoadCompleted] = React.useState(false);
  const [customHostings, setCustomHostings] = React.useState([]);
  const [filteredDocs, setFilteredDocs] = React.useState([]);
  const [useVisualWorkflowBuilder, setUseVisualWorkflowBuilder] = useState(
    false,
  );
  const [baseLlmModels, setBaseLlmModels] = React.useState([]);
  const [functionCallingModels, setFunctionCallingModels] = React.useState([]);
  const [baseLlmProviders, setBaseLlmProviders] = React.useState([]);
  const [baseImageModels, setBaseImageModels] = React.useState([]);
  const [agentVoice, setAgentVoice] = React.useState(null);
  const [filteredVoices, setFilteredVoices] = React.useState(ALL_VOICES);
  const [audioGenerationVoices, setAudioGenerationVoices] = React.useState([]);
  const [filteredVisionModels, setFilteredVisionModels] = React.useState([]);
  const [
    functionCallingProviders,
    setFunctionCallingProviders,
  ] = React.useState([]);
  const [adverserialProviders, setAdverserialProviders] = React.useState([]);
  const [adverserialModels, setAdverserialModels] = React.useState([]);
  const [agentSnapshots, setAgentSnapshots] = useState([]);
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);
  const [hasLoadedSnapshots, setHasLoadedSnapshots] = useState(false);
  const [showRollbackChoice, setShowRollbackChoice] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [rollbackData, setRollbackData] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [snapshotChangeCounts, setSnapshotChangeCounts] = useState({});
  const [showDiffSummary, setShowDiffSummary] = useState(false);
  const [showDiffView, setShowDiffView] = useState(false);
  const [currentDiffChanges, setCurrentDiffChanges] = useState(null);

  const handleVersionPress = (item, index) => {
    const snapshot = agentSnapshots[index];
    if (snapshot?.snapshotData) {
      try {
        const parsedData = JSON.parse(snapshot.snapshotData);

        const differences = objectDiffUtils.calculateDifferences(
          parsedData,
          values,
          {
            excludeFields: [
              "id",
              "createdAt",
              "updatedAt",
              "_version",
              "_deleted",
              "_lastChangedAt",
              "workspaceID",
            ],
          },
        );

        setRollbackData(parsedData);
        setSelectedSnapshot(snapshot);
        setCurrentDiffChanges(differences);
        setShowDiffSummary(true);
      } catch (error) {
        console.error("Failed to parse snapshot data:", error);
        AppToasty.show(t("toasts.history.loadFailed"), { type: "danger" });
      }
    }
  };

  const handleRollbackConfirm = () => {
    if (rollbackData) {
      const excludeFields = [
        "id",
        "createdAt",
        "updatedAt",
        "_version",
        "_deleted",
        "_lastChangedAt",
        "workspaceID",
      ];
      Object.keys(rollbackData).forEach((key) => {
        if (!excludeFields.includes(key)) {
          setFieldValue(key, rollbackData[key]);
        }
      });
      setHistoryExpanded(false);
      AppToasty.show(t("toasts.history.loaded"), {
        type: "info",
      });
    }
    setShowDiffSummary(false);
    setShowDiffView(false);
    setShowRollbackChoice(false);
    setRollbackData(null);
    setSelectedSnapshot(null);
    setCurrentDiffChanges(null);
  };

  const handleHistoryExpandChange = (isExpanded) => {
    setHistoryExpanded(isExpanded);
    handleSnapshotsExpandChange(isExpanded);
  };

  const handleSnapshotsExpandChange = async (isExpanded) => {
    if (isExpanded && !hasLoadedSnapshots && values?.id) {
      setIsLoadingSnapshots(true);
      try {
        const response = await entitySnapshotsHelper.getSnapshotsByEntity(
          values.id,
          {
            sortDirection: "DESC",
            limit: 50,
          },
        );
        console.log("RESPONSE", response);
        const snapshots = response?.data?.snapshotsByEntity?.items || [];
        setAgentSnapshots(snapshots);
        setHasLoadedSnapshots(true);

        const counts = {};
        snapshots.forEach((snapshot, index) => {
          if (snapshot?.snapshotData) {
            try {
              const parsedData = JSON.parse(snapshot.snapshotData);
              const differences = objectDiffUtils.calculateDifferences(
                parsedData,
                values,
                {
                  excludeFields: [
                    "id",
                    "createdAt",
                    "updatedAt",
                    "_version",
                    "_deleted",
                    "_lastChangedAt",
                    "workspaceID",
                  ],
                },
              );
              counts[index] = differences.totalChanges;
            } catch (e) {
              counts[index] = 0;
            }
          }
        });
        setSnapshotChangeCounts(counts);
      } catch (error) {
        console.error("Error loading agent snapshots:", error);
      }
      setIsLoadingSnapshots(false);
    }
  };

  const handleViewFullDiff = () => {
    setShowDiffSummary(false);
    setShowDiffView(true);
  };

  const handleDiffSummaryRollback = () => {
    setShowDiffSummary(false);
    handleRollbackConfirm();
  };

  const handleDiffSummaryClose = () => {
    setShowDiffSummary(false);
    setRollbackData(null);
    setSelectedSnapshot(null);
    setCurrentDiffChanges(null);
  };

  // Ref for AgentWorkflowBuilder to access current steps
  const workflowBuilderRef = useRef(null);

  // Handlers for tab switching with sync
  const switchToJsonEditor = () => {
    // Sync flow state to Formik before switching
    if (workflowBuilderRef.current) {
      workflowBuilderRef.current.syncToFormik();
    }
    setUseVisualWorkflowBuilder(false);
  };

  const switchToVisualBuilder = () => {
    // Just switch - visual builder will pick up Formik value automatically
    setUseVisualWorkflowBuilder(true);
  };

  // Memoized callbacks for AgentWorkflowBuilder to prevent re-renders
  const handleWorkflowChange = useCallback(
    (newValue) => {
      setFieldValue("predictedPlanJson", newValue);
    },
    [setFieldValue],
  );

  const workflowWrapperStyle = useMemo(
    () => ({
      paddingTop: 0,
    }),
    [],
  );

  // Jobs + Triggers that fire THIS agent. Loaded once per workspace so the
  // "Scheduled at" / "Triggered by" panel near the versioning section can show
  // what invokes the agent without the user switching to the Jobs/Triggers
  // sections. Mirrors DashboardScreenContainer's jobs/triggers load + dispatch.
  useEffect(() => {
    const loadScheduling = async () => {
      if (!workspaceid) return;
      try {
        if (!state?.jobs || state.jobs.length === 0) {
          let jobsResp = await jobsHelper.getJobsByWorkspaceID(workspaceid);
          const jobsItems = jobsResp?.data?.cronJobsByWorkspace?.items?.filter(
            (u) => u?._deleted !== true,
          );
          dispatch("SET_JOBS", jobsItems || []);
        }
        if (!state?.triggers || state.triggers.length === 0) {
          let triggersResp = await triggersHelper.getTriggersByWorkspaceID(
            workspaceid,
          );
          const triggersItems = triggersResp?.data?.triggersByWorkspace?.items?.filter(
            (u) => u?._deleted !== true,
          );
          dispatch("SET_TRIGGERS", triggersItems || []);
        }
      } catch (error) {
        console.error("Failed to load jobs/triggers for agent view:", error);
      }
    };
    loadScheduling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceid]);

  useEffect(() => {
    const loadAlerts = async () => {
      if (!workspaceid) return;
      try {
        const alerts = await alertsHelper.getAlertsByWorkspace(workspaceid);
        setAvailableAlerts(alerts || []);
      } catch (error) {
        console.error("Failed to load alerts for agent view:", error);
      }
    };
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceid]);

  // Shape into panel item format: { id, name, type, extra }.
  const agentJobs = useMemo(() => {
    const id = initialMapValues?.id;
    if (!id) return [];
    return (state?.jobs || [])
      .filter((j) => j?.agentID === id)
      .map((j) => ({ id: j.id, name: j.name || j.id, type: "job" }));
  }, [JSON.stringify(state?.jobs), initialMapValues?.id]);

  const agentTriggers = useMemo(() => {
    const id = initialMapValues?.id;
    if (!id) return [];
    return (state?.triggers || [])
      .filter((tr) => tr?.agentID === id)
      .map((tr) => ({
        id: tr.id,
        name: tr.name || tr.id,
        type: "trigger",
      }));
  }, [JSON.stringify(state?.triggers), initialMapValues?.id]);

  // Click a scheduling chip → navigate to the bound job/trigger's settings
  // edit view (`/workspaces/<id>/settings/jobs|triggers/<id>`), which now
  // auto-opens the edit modal via useRouteModalOpen wired in JobsList /
  // TriggersList. Lets users jump straight from the agent to its invoker.
  const handleSchedulingChipPress = useCallback(
    (item) => {
      if (!item?.id) return;
      const href =
        item?.type === "trigger" ? chipHrefTriggers(item) : chipHrefJobs(item);
      if (!href) return;
      // Open the bound job/trigger in a NEW BROWSER TAB (absolute URL built from
      // the current origin + the settings path), mirroring the static-docs /
      // agent-link redirection in this file (`window.open(..., "_blank")`).
      // Keeps the agent editor open in the original tab so the user can jump
      // to the invoker without losing their place.
      const absoluteUrl =
        typeof window !== "undefined" && window?.location?.origin
          ? `${window.location.origin}${href}`
          : href;
      if (typeof window !== "undefined" && typeof window.open === "function") {
        window.open(absoluteUrl, "_blank");
      } else if (history?.push) {
        history.push(href);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chipHrefJobs, chipHrefTriggers, history],
  );

  const validateWorkflow = useCallback((value) => {
    const errors = [];
    if (!value || typeof value !== "object") {
      errors.push({
        message: t("settings.qa.planMustBeValidObject"),
      });
      return errors;
    }
    let planSteps = [];
    if (Array.isArray(value)) {
      planSteps = value;
    } else if (value.plan && Array.isArray(value.plan)) {
      planSteps = value.plan;
    }

    if (planSteps.length === 0) {
      errors.push({
        message: t("settings.qa.planMustContainStep"),
      });
      return errors;
    }
    const requiredFields = ["step_id", "agent_id", "task_description"];
    planSteps.forEach((step, index) => {
      requiredFields.forEach((field) => {
        if (
          step[field] === undefined ||
          step[field] === null ||
          step[field] === ""
        ) {
          errors.push({
            message: t("settings.qa.stepMissingRequiredField", {
              step: index + 1,
              field,
            }),
          });
        }
      });
    });
    return errors;
  }, []);

  const generateModes = () => {
    const _baseModes = [];
    _baseModes.push({
      name: t("settings.qa.modes.operator"),
      id: "retriever",
      description: t("settings.qa.modes.operatorDesc"),
    });
    _baseModes.push({
      name: t("settings.qa.modes.programmer"),
      id: "coder",
      description: t("settings.qa.modes.programmerDesc"),
    });
    _baseModes.push({
      name: t("settings.qa.modes.assistant"),
      id: "chatter",
      description: t("settings.qa.modes.assistantDesc"),
    });
    _baseModes.push({
      name: t("settings.qa.modes.orchestrator"),
      id: "planner",
      description: t("settings.qa.modes.orchestratorDesc"),
    });

    if (
      getActiveWorkspace()?.subscriptionType == "enterprise" ||
      getActiveWorkspace()?.subscriptionType == "business"
    ) {
      // Voice agents are not available yet in the EU/US regions. The option
      // stays in the dropdown so users can see it exists, but its label/desc
      // tell them it isn't available and selecting it is blocked (see the mode
      // dropdown's onValueChange guard).
      const voiceUnavailableInRegion = !isVoiceAgentsAvailableInRegion();
      _baseModes.push({
        name: voiceUnavailableInRegion
          ? `${t("settings.qa.modes.voice")} - ${t(
              "settings.qa.modes.voiceUnavailableInRegion",
            )}`
          : t("settings.qa.modes.voice"),
        id: "voice",
        description: voiceUnavailableInRegion
          ? t("settings.qa.modes.voiceUnavailableInRegion")
          : t("settings.qa.modes.voiceDesc"),
      });
    }

    return _baseModes;
  };
  const {
    setFieldValue,
    values,
    initialValues,
    errors,
    submitCount,
  } = AppForms.useFormikContext();

  // Baseline-based dirty tracking. Several migration effects call
  // setFieldValue on load (agentVersion suggestion, agenticMode migration,
  // model sync...), which makes Formik's `dirty` true even when the user
  // changed nothing. Instead we snapshot the form values once those effects
  // have settled (initialLoadCompleted + a tick) and after every save, and we
  // diff the current values against that snapshot.
  // NOTE: the baseline is kept in state (baselineVersion) so the isFormDirty
  // memo actually recomputes when the baseline is reset — a ref alone would
  // leave the memo returning a stale "dirty" value after save.
  const [dirtyBaseline, setDirtyBaseline] = React.useState(null);
  const dirtyBaselineObjRef = React.useRef(null);
  const wasSavingRef = React.useRef(false);

  const setBaselineFromValues = (vals, tag) => {
    dirtyBaselineObjRef.current = vals;
    const s = stableFormValuesStringify(vals);
    setDirtyBaseline(s);
    dirtyDebug(tag, { length: s?.length });
  };

  useEffect(() => {
    if (!initialLoadCompleted) return;
    const timer = setTimeout(() => {
      setBaselineFromValues(values, "baseline:init");
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadCompleted]);

  useEffect(() => {
    if (wasSavingRef.current && !isAsyncProcessInProgress) {
      setBaselineFromValues(values, "baseline:asyncFlagOff");
    }
    wasSavingRef.current = isAsyncProcessInProgress;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAsyncProcessInProgress]);

  // Reset the dirty baseline as soon as a save completes. The save handler
  // bumps saveVersion right after resetForm restores the form to the saved
  // snapshot. resetForm sets Formik `initialValues` to that snapshot, so we
  // baseline against `initialValues` (not `values`, which may still hold the
  // pre-reset state in the same render) to avoid a race.
  useEffect(() => {
    if (saveVersion == null || saveVersion === 0) return;
    const clean = initialValues || values;
    setBaselineFromValues(clean, "baseline:saveVersion");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveVersion]);

  const isFormDirty = React.useMemo(() => {
    if (dirtyBaseline == null) return false;
    const current = stableFormValuesStringify(values);
    const dirty = current !== dirtyBaseline;
    if (dirty) {
      const changed = diffValueKeys(dirtyBaselineObjRef.current, values);
      dirtyDebug("DIRTY=true", {
        changedKeys: changed,
        baselineLength: dirtyBaseline.length,
        currentLength: current.length,
      });
      // Flat per-key lines so the exact field and its before/after are visible
      // without expanding a nested object in the console.
      changed.forEach((k) => {
        dirtyDebug(`CHANGED key="${k}"`, {
          baseline: diffPreview(dirtyBaselineObjRef.current?.[k]),
          current: diffPreview(values?.[k]),
        });
      });
    }
    return dirty;
  }, [values, dirtyBaseline]);

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;

  const SectionRenderer = isBuilderLayout ? BuilderSection : AppExpander;

  // Section ids containing fields that failed validation. Only surfaces after
  // a save attempt (submitCount > 0) so users aren't nagged mid-editing.
  const errorSections = React.useMemo(() => {
    if (!isBuilderLayout || !submitCount) return [];
    return getErrorSections(errors, AGENT_SECTION_FIELD_MAP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuilderLayout, submitCount, JSON.stringify(errors)]);

  useEffect(() => {
    if (!onBuilderContextChange) return;
    dirtyDebug("onBuilderContextChange", { isDirty: isFormDirty });
    onBuilderContextChange({
      mode: values?.mode,
      hasCode: values?.hasCode,
      agenticRAG: values?.agenticRAG,
      useInterleavedReasoning: values?.useInterleavedReasoning,
      restrictedAccess: values?.restrictedAccess,
      isDirty: isFormDirty,
      errorSections,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values?.mode,
    values?.hasCode,
    values?.agenticRAG,
    values?.useInterleavedReasoning,
    values?.restrictedAccess,
    isFormDirty,
    JSON.stringify(errorSections),
  ]);

  useEffect(() => {
    if (!isEdit) {
      if (!values?.agentVersion) setFieldValue("agentVersion", "0.1.0");
    } else if (!values?.agentVersion) {
      const suggested = semverHelper.suggestNext(
        initialMapValues?.agentVersion,
      );
      setFieldValue("agentVersion", suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initialMapValues?.agentVersion]);

  // Handler for outbound SIP toggle - creates/deletes trunk immediately
  const handleOutboundSipToggle = useCallback(
    async (newValue) => {
      // Update form value
      setFieldValue("enableOutboundSip", newValue);

      const sipChannel = values?.communicationServices?.find(
        (u) => u?.channel == "sip",
      );
      const sipPhoneNumber = values?.sipPhoneNumber;

      if (newValue) {
        // Turning ON - create outbound trunk
        if (!sipPhoneNumber || !sipChannel) {
          AppToasty.show(t("toasts.trunk.missingInfo"), {
            type: "danger",
          });
          return;
        }

        try {
          const channelConfig = sipChannel.config
            ? JSON.parse(sipChannel.config)
            : {};
          const twilioSid = channelConfig.twilio_sid || sipChannel.senderid;

          const result = await sipAPI.createSipOutboundTrunkForAgent({
            workspace_id: getActiveWorkspace()?.id,
            phone_number: sipPhoneNumber,
            twilio_sid: twilioSid,
            channel_id: sipChannel.id,
          });
        } catch (error) {
          AppToasty.show(t("toasts.trunk.createError"), {
            type: "danger",
          });
          console.error("Error creating outbound trunk:", error);
        }
      } else {
        // Turning OFF - delete outbound trunk
        try {
          const result = await sipAPI.deleteSipOutboundTrunkForAgent({
            workspace_id: getActiveWorkspace()?.id,
          });

          console.log("deleteSipOutboundTrunkForAgent result:", result);
        } catch (error) {
          AppToasty.show(t("toasts.trunk.deleteError"), {
            type: "danger",
          });
          console.error("Error deleting outbound trunk:", error);
        }
      }
    },
    [
      values?.communicationServices,
      values?.sipPhoneNumber,
      setFieldValue,
      getActiveWorkspace,
      t,
    ],
  );

  const canAgentRunLocally =
    values?.mode == "chatter" || values?.mode == "coder";
  const handleRecordingCompleted = async (blob) => {
    console.log("Recording completed", blob);
    setIsAudioRecording(false);
    const _file = new File([blob], "audio.wav", {
      type: "audio/wav",
    });
    const audioId = generateRowId();
    const result = await S3uploadUrlRequest({
      filename: `${getActiveWorkspace()?.id}/${audioId}.wav`,
      importType: "imported_audio_files",
      contentType: "wav",
    });
    const upload = await axios.request({
      method: "put",
      url: JSON.parse(result?.body)?.uploadURL,
      data: _file,
      headers: {
        "Content-Type": _file.type,
      },
      signal: controller.signal,
      onUploadProgress: (p) => {
        // if (controller.signal.aborted) return;
        setAudioFileUploadProgress(Math.round((p.loaded * 100) / p.total));
        if (p.loaded === p.total) {
          setAudioFileUploadProgress(100);
        }
      },
    });
    console.log("Upload result", upload);
    if (JSON.parse(result?.body).uploadURL) {
      setFieldValue(
        "voiceFileKey",
        `imported_audio_files/${getActiveWorkspace()?.id}/${audioId}.wav`,
      );
      // Clone the voice via Cartesia and store its voice ID — this is what
      // the livecall pipeline actually reads (voiceFileKey alone is only
      // used as reference audio for chat audio generation).
      await cloneVoiceFromClip(_file);
    }
  };

  const cloneVoiceFromClip = async (clipFile) => {
    try {
      setVoiceCloneInProgress(true);
      const response = await Media.cloneVoice({
        token: getActiveWorkspace()?.workspaceToken,
        workspaceid: getActiveWorkspace()?.id,
        clip: clipFile,
        name:
          values?.label ||
          `${values?.label || "Agent"} voice ${String(Date.now()).slice(-6)}`,
        description: `Cloned voice for agent ${values?.label || ""} (workspace ${
          getActiveWorkspace()?.id
        })`,
      });
      // cloneVoice uses raw fetch (multipart) — parse the JSON body
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(
          payload?.errorMessage ||
            payload?.message ||
            `Voice cloning failed (HTTP ${response.status})`,
        );
      }
      const voiceId =
        payload?.contents?.voice_id || payload?.voice_id || payload?.id;
      if (!voiceId) throw new Error("No voice_id returned from clone API");
      setFieldValue("voiceName", voiceId);
      setFieldValue("ttsModel", "cartesia");
      // Persist the cloned voice to the agent immediately — form state can
      // be reset by remounts/reinitialize before the user clicks Save.
      // Skip for unsaved agents (no id yet); their clone saves on create.
      if (values?.id) {
        try {
          await Agents.updateAgent(values.id, [
            { property: "voiceName", value: voiceId },
            { property: "ttsModel", value: "cartesia" },
          ]);
        } catch (persistError) {
          console.error(
            "Failed to persist cloned voice to agent",
            persistError,
          );
        }
      }
      AppToasty.show(t("settings.qa.voiceCloneSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.error("Voice cloning failed", error);
      AppToasty.show(
        error?.message || t("settings.qa.voiceCloneFailedCaption"),
        { type: "danger" },
      );
    } finally {
      setVoiceCloneInProgress(false);
    }
  };
  // Clear the cloned voice: drop the reference clip and reset voiceName so
  // the preset picker (and its filter effect) takes over again. Persisted
  // immediately so a remount can't resurrect the clone.
  const handleRemoveClonedVoice = async () => {
    const hadVoiceFile = !!values?.voiceFileKey;
    setFieldValue("voiceFileKey", null);
    if (isClonedVoiceId(values?.voiceName)) {
      // Leave voiceName empty — the ttsModel/language filter effect resets
      // it to the first available preset on the next render.
      setFieldValue("voiceName", null);
    }
    if (values?.id) {
      try {
        const updates = [];
        if (hadVoiceFile) {
          updates.push({ property: "voiceFileKey", value: null });
        }
        if (isClonedVoiceId(values?.voiceName)) {
          updates.push({ property: "voiceName", value: null });
        }
        if (updates.length > 0) {
          await Agents.updateAgent(values.id, updates);
        }
      } catch (persistError) {
        console.error("Failed to persist cloned voice removal", persistError);
      }
    }
    AppToasty.show(t("settings.qa.voiceCloneRemovedCaption"), {
      type: "success",
    });
  };
  const handleRecordingStarted = () => {
    setIsAudioRecording(true);
    console.log("Recording started");
  };
  const isTfModel =
    ["sorcerer", "mystica", "sorcerer_15", "mystica_15"].find(
      (u) => u == values?.llmBaseModel,
    ) != null || values?.llmBaseModel == null;
  const isKnowledgeEnabled =
    !values?.mode ||
    values?.mode == "retriever" ||
    values?.mode == "speed" ||
    values?.mode == "accuracy" ||
    values?.mode == "coder" ||
    values?.mode == "voice" ||
    !isChatBot;
  const isCharacterNeeded = isChatBot && values?.mode != "mathematician";
  // Voice pipeline configs (TTS/voice/STT/noise selection + timing sliders):
  // native voice agents always show them; regular agents get them when the
  // audio-generation capability is on (the live-mode voice segment reuses the
  // same pipeline) — but only in regions where live mode (LiveKit) is
  // available. Voice generation (Cartesia TTS) works everywhere, but that's
  // a different, simpler config and doesn't need these.
  const showVoicePipelineConfigs =
    values?.mode == "voice" ||
    (values?.allowAudiosGeneration == true && isLiveModeAvailableInRegion());
  const _modes = generateModes();
  const agentLink = `https://agent.${
    envConfig?.REACT_APP_ENV == "dev"
      ? "toothfairylab.link"
      : envConfig?.REACT_APP_REGION == "eu"
      ? "eu.toothfairyai.com"
      : envConfig?.REACT_APP_REGION == "us"
      ? "us.toothfairyai.com"
      : "toothfairyai.com"
  }/${workspaceid}/${initialMapValues?.id}${
    ["light", "dark"]?.includes(values?.forcedTheme)
      ? "/tf_" + values?.forcedTheme
      : ""
  }`;
  const widgetString = isEdit
    ? `<div id="tfChatWidget" style="position: fixed; bottom: 10px; right: 10px; z-index: 10;">
  <style>
    #tfChatWidget { border: none; background-color: transparent; }
    #tfChatWidget.open { border: 0.5px solid grey; background-color: white; }
    .tfChatBtn { border: none; border-radius: 5px; padding: 10px 15px; margin: 5px; cursor: pointer; transition: background 0.3s; }
    #tfWidget { display: none; width: 360px; height: 400px; border: none; }
  </style>
  <iframe id="tfWidget" src="${agentLink}"></iframe>
  <div style="display: flex; justify-content: space-between">
    <button id="tfOpenChat" onclick="tfToggle()" class="tfChatBtn">Chat with ${initialMapValues?.label}</button>
    <button id="tfStartNew" onclick="tfStartNewChat()" style="display: none;" class="tfChatBtn">New chat</button>
    <button id="tfCloseChat" onclick="tfClose()" style="display: none;" class="tfChatBtn">Close chat</button>
  </div>
</div>

<script>
  function tfToggleChat(open) {
    const widget = document.getElementById('tfChatWidget');
    widget.classList.toggle('open', open);
    document.getElementById('tfWidget').style.display = open ? 'block' : 'none';
    ['tfOpenChat', 'tfStartNew', 'tfCloseChat'].forEach(id =>
      document.getElementById(id).style.display = open ? (id === 'tfOpenChat' ? 'none' : 'block') : (id === 'tfOpenChat' ? 'block' : 'none')
    );
  }

  function tfClose() {
    if (confirm('Close this chat? The current conversation will be lost.')) {
      tfToggleChat(false);
    }
  }

  function tfToggle() {
    const widget = document.getElementById('tfChatWidget');
    const isOpen = widget.classList.contains('open');
    if (isOpen) {
      tfClose();
    } else {
      tfToggleChat(true);
    }
  }

  function tfStartNewChat() {
    document.getElementById('tfWidget').src += '';
    tfToggleChat(true);
  }
</script>`
    : t("settings.qa.codeGeneratedAfterSave");
  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);

  const _title = useCallback(() => {
    return isEdit ? t("common.save") : t("common.create");
  }, [entityName, isEdit, t]);
  const getUrl = async (path, isLargeLogo) => {
    const _url = await S3downloadUrlRequest({
      filename: `imported-image/${workspaceid}/${path}`,
      context: "pdf",
      workspaceid,
    });
    if (isLargeLogo && _url?.url) setAgentLogo(_url?.url);
    else if (_url?.url) setAgentImage(_url?.url);
    else if (isLargeLogo) setAgentLogo(null);
    else setAgentImage(null);
  };
  const getCustomHostings = async () => {
    try {
      const result = await hostingsHelper.getHostings(workspaceid);
      const _hostings =
        result?.data?.HostingsByWorkspace?.items?.filter(
          (u) => u?._deleted != true,
        ) || [];
      setCustomHostings(_hostings);
    } catch (error) {
      console.log(
        "Error getting hostings - attempting to switch to Ollama is present",
        error,
      );
    }
  };

  const getFeedbackForAgent = async () => {
    try {
      const _feedback = await messagesHelper.getChatMessagesByAgent(
        initialMapValues?.id,
      );
      setAgentFeedbackList(_feedback?.data?.messagesByAgent?.items);
    } catch (error) {
      console.log("Error getting feedback", error);
      setAgentFeedbackList([]);
    }
  };
  useEffect(() => {
    setTimeout(() => {
      setInitialLoadCompleted(true);
    }, [2000]);
  }, []);

  useEffect(() => {
    setLastOpened(Date.now());
  }, [values?.id]);
  useEffect(() => {
    if (values?.llmBaseModel) {
      const _maxTokens = _baseModels?.find((u) => u?.id == values?.llmBaseModel)
        ?.maxTokens;
      if (!_maxTokens) {
        setFieldValue("maxTokens", getMaxTokensBasedOnModels(values));
      } else if (values?.maxTokens > _maxTokens)
        setFieldValue("maxTokens", getMaxTokensBasedOnModels(values));
      if (
        values?.llmBaseModel == "sorcerer" ||
        values?.llmBaseModel == "mystica"
      )
        setFieldValue("reasoningEffort", "none");
      else if (
        values?.llmBaseModel == "sorcerer_15" ||
        values?.llmBaseModel == "mystica_15"
      )
        setFieldValue("reasoningEffort", "medium");
    }
  }, [values?.llmBaseModel, values?.maxTokens]);

  useEffect(() => {
    if (values?.agenticRAG != true && values?.mode == "retriever")
      setFieldValue("hasCode", false);
  }, [values?.agenticRAG, values?.mode]);

  useEffect(() => {
    if (values?.useInterleavedReasoning && !values?.hasCode)
      setFieldValue("hasCode", true);
  }, [values?.useInterleavedReasoning]);

  useEffect(() => {
    if (values?.mode == "retriever") {
      // Default Operator agents to Agentic Tooling 2.0
      // (agentic_operator_advanced). The legacy "Standard Operator" preset is
      // deprecated; a value is only honoured if the user explicitly chose it.
      // Translate a deprecated/empty agenticMode forward to Agentic Tooling 2.0
      // and keep useInterleavedReasoning/agenticRAG in sync.
      const currentMode = values?.agenticMode;
      if (!currentMode || currentMode == "operator") {
        setFieldValue("agenticMode", "agentic_operator_advanced");
        // Mirror the agenticMode dropdown's _onValueChange so the persisted
        // state on open is identical to a user explicitly selecting Agentic
        // Tooling 2.0. Previously only agenticMode + useInterleavedReasoning
        // were set, leaving agenticRAG false — so the dropdown showed 2.0
        // while every hooks/tools section (gated on agenticRAG) stayed hidden.
        setFieldValue("agenticRAG", true);
        setFieldValue("useInterleavedReasoning", true);
        setFieldValue("interleavedPreset", "medium");
        setFieldValue("dynamicPlaceholderMessageOnLoading", true);
        if (values?.maxHistory == null || values?.maxHistory < 50) {
          setFieldValue("maxHistory", 50);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.mode, values?.useInterleavedReasoning, values?.agenticMode]);

  useEffect(() => {
    // Migrate "standard" preset to "medium" for backward compatibility
    if (values?.interleavedPreset == null) {
      setFieldValue("interleavedPreset", "medium");
    }
  }, [values?.interleavedPreset]);

  useEffect(() => {
    // Enable advanced capabilities for Agentic Tooling (Advanced)
    if (values?.agenticMode === "agentic_operator_advanced") {
      if (!values?.allowImagesGeneration) {
        setFieldValue("allowImagesGeneration", true);
      }
      if (!values?.allowVideosGeneration) {
        setFieldValue("allowVideosGeneration", true);
      }
      if (!values?.allow3dModelGeneration) {
        setFieldValue("allow3dModelGeneration", true);
      }
    }
  }, [values?.agenticMode]);

  useEffect(() => {
    const pullAvailableModelsInOllama = async () => {
      const hostingsData = await hostingsHelper.getHostings(
        getActiveWorkspace()?.id,
        null,
      );
      const hostings = hostingsData?.data?.HostingsByWorkspace?.items;
      const _localHosting = hostings?.find(
        (u) => u?.name == values?.llmProvider,
      );
      if (_localHosting?.type == "ollama") {
        setIsAgentOllama(true);
        const model_response = await axios.get(
          `${_localHosting?.endpoint}/api/tags`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (model_response?.status == 200) {
          isOllamaRunning(true);
          setOllamaModels([
            {
              name: t("settings.qa.noModelSelected"),
              id: "none",
            },
            ...model_response?.data?.models?.map((u) => {
              return {
                name: u?.name,
                id: u?.model,
              };
            }),
          ]);
        }
      } else {
        setIsAgentOllama(false);
      }
    };

    if (canAgentRunLocally) pullAvailableModelsInOllama();
    else {
      setOllamaModels([]);
      setIsAgentOllama(false);
    }
  }, [canAgentRunLocally, values?.llmProvider]);

  useEffect(() => {
    if (values?.llmProvider && initialLoadCompleted) {
      if (
        baseLlmModels?.length > 0 &&
        !baseLlmModels?.find((u) => u?.id == values?.llmBaseModel)
      ) {
        setFieldValue("llmBaseModel", baseLlmModels[0]?.id);
      }
    }
  }, [
    values?.llmProvider,
    initialLoadCompleted,
    JSON.stringify(baseLlmModels),
  ]);
  useEffect(() => {
    if (workspaceid) getCustomHostings();
  }, [workspaceid, canAgentRunLocally]);
  useEffect(() => {
    if (initialMapValues?.id) getFeedbackForAgent();
  }, [initialMapValues?.id]);
  useEffect(() => {
    if (
      values?.baseImageGenerationModel == "black-forest-labs/flux-pro" ||
      values?.baseImageGenerationModel == "black-forest-labs/flux-1.1-pro" ||
      values?.baseImageGenerationModel == "black-forest-labs/flux-1.1-pro-ultra"
    )
      setFieldValue("maxImagesGenerated", 1);
  }, [values?.baseImageGenerationModel]);
  useEffect(() => {
    if (getActiveWorkspace()?.subscriptionType) {
      setBaseLlmProviders(
        matchProvidersWithSubscription(
          getActiveWorkspace()?.subscriptionType,
          false,
          customHostings,
        ),
      );
      let base_models = [
        // TF Branded Models (Top)
        {
          name: "Sorcerer Canvas",
          id: "sorcerer_canvas",
          tier: "medium",
          description: "Balanced image generation with editing",
        },
        {
          name: "Sorcerer Vision",
          id: "sorcerer_vision",
          tier: "large",
          description: "High-quality 4K generation & editing",
        },
        {
          name: "Mystica Vision",
          id: "mystica_vision",
          tier: "large",
          description: "Premium 4K generation & editing",
        },
        {
          name: "Mystica SD",
          id: "mystica_sd",
          tier: "medium",
          description: "Fast 2K generation with editing",
        },
        {
          name: "Mystica Realism SD",
          id: "mystica_realism_sd",
          tier: "medium",
          description: "High-quality generation & editing",
        },
        // ExtraSmall tier
        {
          name: "P-Image",
          id: "prunaai/p-image",
          tier: "extraSmall",
          description: "Ultra-fast budget image generation",
        },
        {
          name: "P-Image Edit",
          id: "prunaai/p-image-edit",
          tier: "extraSmall",
          description: "Ultra-fast budget image editing",
        },
        // Small tier
        {
          name: "FLUX Schnell",
          id: "black-forest-labs/flux-schnell",
          tier: "small",
          description: "Fastest option, good for quick iterations",
        },
        {
          name: "Stable Diffusion XL",
          id: "stability-ai/sdxl",
          tier: "small",
          description: "Classic SD model, wide compatibility",
        },
        // Medium tier
        {
          name: "FLUX Dev",
          id: "black-forest-labs/flux-dev",
          tier: "medium",
          description: "Development version of FLUX",
        },
        {
          name: "FLUX Dev Realism",
          id: "fofr/flux-dev-realism",
          tier: "medium",
          description: "Realism-tuned FLUX Dev",
        },
        {
          name: "Stable Diffusion 3",
          id: "stability-ai/stable-diffusion-3",
          tier: "medium",
          description: "Latest Stable Diffusion",
        },
        {
          name: "Nano Banana Pro",
          id: "google/nano-banana-pro",
          tier: "medium",
          description: "Google Nano Banana Pro, fast & high quality",
        },
        {
          name: "Nano Banana 2",
          id: "google/nano-banana-2",
          tier: "medium",
          description: "Google Nano Banana 2, 2K resolution",
        },
        {
          name: "Imagen 4 Fast",
          id: "google/imagen-4-fast",
          tier: "medium",
          description: "Google Imagen 4 Fast",
        },
        // Large tier
        {
          name: "FLUX Pro",
          id: "black-forest-labs/flux-pro",
          tier: "large",
          description: "Professional FLUX model",
        },
        {
          name: "FLUX 1.1 Pro",
          id: "black-forest-labs/flux-1.1-pro",
          tier: "large",
          description: "Faster, better FLUX Pro with excellent quality",
        },
        {
          name: "Seedream 4",
          id: "bytedance/seedream-4",
          tier: "large",
          description: "4K resolution, unified generation & editing",
        },
        {
          name: "Ideogram V3 Turbo",
          id: "ideogram-ai/ideogram-v3-turbo",
          tier: "large",
          description: "Best for text/logos in images, stunning realism",
        },
        {
          name: "Qwen Image",
          id: "qwen/qwen-image",
          tier: "large",
          description: "Excellent text rendering in images",
        },
        // Extra Large tier
        {
          name: "FLUX 1.1 Pro Ultra",
          id: "black-forest-labs/flux-1.1-pro-ultra",
          tier: "extraLarge",
          description: "Highest quality FLUX, premium option",
        },
        {
          name: "Imagen 4",
          id: "google/imagen-4",
          tier: "extraLarge",
          description: "Google Imagen 4, premium quality",
        },
      ];

      setBaseImageModels(base_models);
      setFunctionCallingProviders(
        matchProvidersWithSubscription(
          getActiveWorkspace()?.subscriptionType,
          true,
          customHostings,
        ),
      );
      setAdverserialProviders(
        matchProvidersWithSubscription(
          getActiveWorkspace()?.subscriptionType,
          true,
          customHostings,
        ),
      );
      const _allBaseLlm = matchModelsWithProviders(
        values?.llmProvider,
        getActiveWorkspace()?.subscriptionType,
        false,
        customHostings,
      );
      const _isRegionalFilter =
        values?.llmProvider === "tf" &&
        (values?.regionalEndpoint === true ||
          values?.regionalEndpoint === "true" ||
          getActiveWorkspace()?.enforceRegionalModels == true);

      // TEMP DEBUG [TF_FT]
      console.log("[TF_FT] baseLlm effect:", {
        llmProvider: values?.llmProvider,
        finetunedIn: finetunedModels?.length || 0,
        allBaseLlm: _allBaseLlm?.length || 0,
        ftIdsInAll:
          finetunedModels
            ?.filter((f) => _allBaseLlm?.some((u) => u?.id === f?.id))
            ?.map((f) => f?.id) || [],
        isRegionalFilter: _isRegionalFilter,
        useInterleavedReasoning: values?.useInterleavedReasoning,
        agentMode: values?.mode,
      });

      const _filteredBaseLlm = _allBaseLlm
        ?.filter((u) => {
          if (_isRegionalFilter) {
            // Fine-tuned models are served on lanes in the workspace's own
            // region — they must survive the regional-ids filter (their ids
            // are never in the serverless regional list).
            if (finetunedModels?.some((f) => f?.id === u?.id)) return true;
            const included = _regionalModelIds?.includes(u.id);

            return included;
          }
          return true;
        })
        ?.filter(
          (u) =>
            u?.experimental != true ||
            u?.experimental == getActiveWorkspace()?.enableExperimentalFeatures,
        );
      console.log("[TF_FT] baseLlm after filters:", {
        count: _filteredBaseLlm?.length || 0,
        ftSurvived:
          finetunedModels
            ?.filter((f) => _filteredBaseLlm?.some((u) => u?.id === f?.id))
            ?.map((f) => ({ id: f?.id, name: f?.name })) || [],
      });
      setBaseLlmModels(_filteredBaseLlm);
      setFunctionCallingModels(
        matchModelsWithProviders(
          values?.functionCallingProvider,
          getActiveWorkspace()?.subscriptionType,
          true,
          customHostings,
        )
          ?.filter((u) => {
            if (
              values?.functionCallingProvider === "tf" &&
              (values?.regionalEndpoint === true ||
                values?.regionalEndpoint === "true" ||
                getActiveWorkspace()?.enforceRegionalModels == true)
            ) {
              return _regionalModelIds?.includes(u.id);
            }
            return true;
          })
          ?.filter(
            (u) =>
              u?.experimental != true ||
              u?.experimental ==
                getActiveWorkspace()?.enableExperimentalFeatures,
          ),
      );
      setAdverserialModels(
        matchModelsWithProviders(
          values?.adverserialProvider,
          getActiveWorkspace()?.subscriptionType,
          true,
          customHostings,
        )
          ?.filter((u) => {
            if (
              values?.adverserialProvider === "tf" &&
              (values?.regionalEndpoint === true ||
                values?.regionalEndpoint === "true" ||
                getActiveWorkspace()?.enforceRegionalModels == true)
            ) {
              return _regionalModelIds?.includes(u.id);
            }
            return true;
          })
          ?.filter(
            (u) =>
              u?.experimental != true ||
              u?.experimental ==
                getActiveWorkspace()?.enableExperimentalFeatures,
          ),
      );
      setFilteredVisionModels(
        visionModels?.filter((u) => {
          if (
            values?.regionalEndpoint === true ||
            values?.regionalEndpoint === "true" ||
            getActiveWorkspace()?.enforceRegionalModels == true
          ) {
            return _regionalModelIds?.includes(u.id);
          }
          return true;
        }),
      );
    }
  }, [
    values?.adverserialProvider,
    values?.functionCallingProvider,
    values?.llmProvider,
    values?.regionalEndpoint,
    JSON.stringify(customHostings),
    JSON.stringify(finetunedModels),
    getActiveWorkspace()?.subscriptionType,
    getActiveWorkspace()?.enforceRegionalModels,
    canAgentRunLocally,
    JSON.stringify(_regionalModelIds),
    _baseModels?.length,
    _functionCallingModels?.length,
  ]);
  useEffect(() => {
    if (
      values?.functionCallingModel != null &&
      initialLoadCompleted &&
      functionCallingModels.find(
        (u) => u?.id == values?.functionCallingModel,
      ) == null
    ) {
      setFieldValue("functionCallingModel", functionCallingModels[0]?.id);
    }
  }, [
    values?.functionCallingProvider,
    values?.functionCallingModel,
    JSON.stringify(functionCallingModels?.map((u) => u?.id)),
  ]);
  useEffect(() => {
    if (values?.functionCallingModel) {
      const selectedModel = functionCallingModels?.find(
        (u) => u?.id == values?.functionCallingModel,
      );
      if (
        selectedModel?.reasoner ||
        shouldShowReasonerDropdown(values?.functionCallingModel)
      ) {
        if (
          values?.functionCallingModel == "sorcerer_15" ||
          values?.functionCallingModel == "mystica_15"
        )
          setFieldValue("fnReasoningEffort", "medium");
        else if (!values?.fnReasoningEffort)
          setFieldValue("fnReasoningEffort", "medium");
      }
    }
  }, [
    values?.functionCallingModel,
    JSON.stringify(functionCallingModels?.map((u) => u?.id)),
  ]);
  useEffect(() => {
    if (
      values?.adverserialModel != null &&
      initialLoadCompleted &&
      adverserialModels.find((u) => u?.id == values?.adverserialModel) == null
    ) {
      setFieldValue("adverserialModel", adverserialModels[0]?.id);
    }
  }, [
    values?.adverserialProvider,
    values?.adverserialModel,
    JSON.stringify(adverserialModels?.map((u) => u?.id)),
  ]);
  useEffect(() => {
    if (
      values?.visionModel != null &&
      initialLoadCompleted &&
      filteredVisionModels.find((u) => u?.id == values?.visionModel) == null
    ) {
      setFieldValue("visionModel", filteredVisionModels[0]?.id);
    }
  }, [
    values?.visionModel,
    values?.regionalEndpoint,
    initialLoadCompleted,
    JSON.stringify(filteredVisionModels?.map((u) => u?.id)),
  ]);
  useEffect(() => {
    if (
      values?.agenticCodingModel != null &&
      values?.agenticCodingModel !== "inherit" &&
      initialLoadCompleted &&
      functionCallingModels.find((u) => u?.id == values?.agenticCodingModel) ==
        null
    ) {
      setFieldValue("agenticCodingModel", functionCallingModels[0]?.id);
    }
  }, [
    values?.agenticCodingModel,
    initialLoadCompleted,
    JSON.stringify(functionCallingModels?.map((u) => u?.id)),
  ]);
  useEffect(() => {
    if (getActiveWorkspace()?.enforceRegionalModels === true) {
      setFieldValue("regionalEndpoint", true);
    } else if (values?.llmProvider && values?.llmProvider !== "tf") {
      setFieldValue("regionalEndpoint", false);
    }
  }, [values?.llmProvider, getActiveWorkspace()?.enforceRegionalModels]);

  // Reset all model selections to "sorcerer" when user switches between regional/global
  const regionalEndpointUserChangeRef = React.useRef({
    value: values?.regionalEndpoint,
    initialized: false,
  });
  useEffect(() => {
    if (!initialLoadCompleted) {
      return;
    }
    if (!regionalEndpointUserChangeRef.current.initialized) {
      regionalEndpointUserChangeRef.current = {
        value: values?.regionalEndpoint,
        initialized: true,
      };
      return;
    }
    const prev = regionalEndpointUserChangeRef.current.value;
    const curr = values?.regionalEndpoint;
    // Only reset if the boolean meaning actually flipped
    const prevBool = prev === true || prev === "true";
    const currBool = curr === true || curr === "true";
    if (prevBool === currBool) {
      regionalEndpointUserChangeRef.current.value = curr;
      return;
    }
    regionalEndpointUserChangeRef.current.value = curr;

    const defaultModel = "sorcerer";
    setFieldValue("llmBaseModel", defaultModel);
    setFieldValue("functionCallingModel", defaultModel);
    setFieldValue("visionModel", defaultModel);
    setFieldValue("agenticCodingModel", defaultModel);
    setFieldValue("adverserialModel", defaultModel);
  }, [values?.regionalEndpoint, initialLoadCompleted]);

  useEffect(() => {
    const enforceRegional =
      getActiveWorkspace()?.enforceRegionalModels === true;

    if (
      values?.llmBaseModel != null &&
      initialLoadCompleted &&
      baseLlmModels.find((u) => u?.id == values?.llmBaseModel) == null
    ) {
      // TEMP DEBUG [TF_FT]
      console.log("[TF_FT] llmBaseModel AUTO-RESET:", {
        currentModel: values?.llmBaseModel,
        baseLlmCount: baseLlmModels?.length || 0,
        enforceRegional,
        regionalEndpoint: values?.regionalEndpoint,
        resettingTo: baseLlmModels[0]?.id,
      });
      if (enforceRegional && !values?.regionalEndpoint) {
        setFieldValue("regionalEndpoint", true);
      }
      setFieldValue("llmBaseModel", baseLlmModels[0]?.id);
    }
  }, [
    values?.llmBaseModel,
    values?.regionalEndpoint,
    initialLoadCompleted,
    JSON.stringify(baseLlmModels?.map((u) => u?.id)),
  ]);
  useEffect(() => {
    if (values?.icon) {
      getUrl(values?.icon);
    } else {
      setAgentImage(null);
    }
  }, [values?.icon]);
  useEffect(() => {
    if (values?.largeLogo) {
      getUrl(values?.largeLogo, true);
    } else {
      setAgentLogo(null);
    }
  }, [values?.largeLogo]);
  useEffect(() => {
    if (values?.voiceFileKey) {
      getUrl(values?.voiceFileKey, true);
    } else {
      setAgentVoice(null);
    }
  }, [values?.voiceFileKey]);

  // Filter voices based on selected TTS model and language
  useEffect(() => {
    // Map TTS model to provider
    const getProviderForModel = (model) => {
      switch (model) {
        case "orpheus-fast":
          return "orpheus-fast";
        case "orpheus":
          return "orpheus";
        case "kokoro":
          return "kokoro";
        case "cartesia":
          return "cartesia";
        default:
          return "orpheus-fast";
      }
    };

    const selectedProvider = getProviderForModel(values?.ttsModel);

    // Reset language if current language is not available for the selected TTS model
    const modelLanguages = getLanguagesByModel(values?.ttsModel);
    if (
      values?.defaultVoiceLanguage &&
      !modelLanguages.find((l) => l.id === values.defaultVoiceLanguage)
    ) {
      // Default to American English which has the most voice support across all TTS models
      setFieldValue("defaultVoiceLanguage", "en-US");
    }

    // Start with all voices or filter by provider
    let voicesToFilter = selectedProvider
      ? ALL_VOICES.filter((v) => v.provider === selectedProvider)
      : ALL_VOICES;

    // Then filter by language if selected
    if (values?.defaultVoiceLanguage) {
      const modelLanguages = getLanguagesByModel(values?.ttsModel);
      const language = modelLanguages.find(
        (l) => l.id === values.defaultVoiceLanguage,
      );
      if (language) {
        voicesToFilter = voicesToFilter.filter(
          (voice) =>
            voice.language === language.languageCode &&
            (language.accent === voice.accent ||
              (language.languageCode === "en" && voice.language === "en")),
        );
      }
    }

    // Format voice names with accent and speed info
    const formattedVoices = voicesToFilter.map((v) => ({
      ...v,
      name: formatVoiceDisplayName(v),
    }));
    setFilteredVoices(formattedVoices);

    // If current voice is not available in filtered list, reset to first available.
    // Cloned voices (Cartesia voice IDs — UUIDs) are never in the preset list;
    // keep them selected — the backend serves them via native Cartesia.
    const isClonedVoiceId = (v) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        v || "",
      );
    if (
      values?.voiceName &&
      !isClonedVoiceId(values.voiceName) &&
      !voicesToFilter.find((v) => v.id === values.voiceName)
    ) {
      if (voicesToFilter.length > 0) {
        setFieldValue("voiceName", voicesToFilter[0].id);
      }
    }
  }, [values?.ttsModel, values?.defaultVoiceLanguage]);

  // Filter voices for the audio generation picker based on the selected
  // voice provider (audiosGenerationTtsModel). Mirrors the voice-agent
  // filter above: format display names and reset the selected voice when
  // it is not available for the newly selected provider.
  // Defaults: Cartesia Sonic as the provider and a region-appropriate
  // female voice (AU -> Australian, EU -> British, US -> American) when
  // audio generation is enabled and nothing has been picked yet.
  useEffect(() => {
    if (!values?.allowAudiosGeneration) {
      setAudioGenerationVoices([]);
      return;
    }
    if (!values?.audiosGenerationTtsModel) {
      setFieldValue("audiosGenerationTtsModel", "cartesia");
      return;
    }
    const voicesToFilter = ALL_VOICES.filter(
      (v) => v.provider === values?.audiosGenerationTtsModel,
    );
    const formattedVoices = voicesToFilter.map((v) => ({
      ...v,
      name: formatVoiceDisplayName(v),
    }));
    setAudioGenerationVoices(formattedVoices);
    if (
      !values?.audiosGenerationVoiceName &&
      values?.audiosGenerationTtsModel === "cartesia"
    ) {
      const region = getEffectiveRegion();
      const defaultVoiceId =
        region === "eu"
          ? "british lady"
          : region === "us"
          ? "helpful woman"
          : "australian woman";
      if (voicesToFilter.find((v) => v.id === defaultVoiceId)) {
        setFieldValue("audiosGenerationVoiceName", defaultVoiceId);
        return;
      }
    }
    if (
      values?.audiosGenerationVoiceName &&
      !voicesToFilter.find((v) => v.id === values.audiosGenerationVoiceName)
    ) {
      if (voicesToFilter.length > 0) {
        setFieldValue("audiosGenerationVoiceName", voicesToFilter[0].id);
      } else {
        setFieldValue("audiosGenerationVoiceName", null);
      }
    }
  }, [values?.allowAudiosGeneration, values?.audiosGenerationTtsModel]);

  // Fine-tuned models come from the workspace's training logs, which are only
  // loaded by the Training screen — on a fresh session where Training was
  // never opened, state.trainingLogs is empty and the base-model dropdown
  // would silently miss every fine-tuned model. Fetch the logs here once when
  // missing (mirroring TrainingScreenContainer's load), and recompute the
  // list whenever the logs change (new completed run, updates...).
  useEffect(() => {
    // TEMP DEBUG [TF_FT]
    console.log("[TF_FT] list effect fired:", {
      subscriptionType: getActiveWorkspace()?.subscriptionType,
      workspaceid,
      logsInState: state?.trainingLogs?.length || 0,
    });
    if (!state?.trainingLogs || state.trainingLogs.length === 0) {
      if (!workspaceid) {
        console.log("[TF_FT] no workspaceid — cannot fetch logs");
        return;
      }
      console.log("[TF_FT] trainingLogs empty — fetching by workspace");
      let cancelled = false;
      trainingLogHelper
        .getTrainingLogsByWorkspaceID(workspaceid)
        .then((res) => {
          if (cancelled) return;
          const logs =
            res?.data?.trainingLogsByWorkspace?.items?.filter(
              (u) => u?._deleted != true,
            ) || [];
          console.log("[TF_FT] fetched training logs:", logs.length, {
            sample: logs.slice(0, 5).map((u) => ({
              id: u?.id,
              name: u?.name,
              status: u?.status,
              hasMeta: !!u?.trainingMeta,
              metaType: typeof u?.trainingMeta,
            })),
          });
          if (logs.length > 0) dispatch("SET_TRAINING_LOGS", logs);
        })
        .catch((e) => console.error("[TF_FT] fetch failed:", e));
      return () => {
        cancelled = true;
      };
    }
    const _ftModels = getFinetunedModels();
    console.log("[TF_FT] setting finetunedModels:", _ftModels);
    setFinetunedModels(_ftModels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getActiveWorkspace()?.subscriptionType,
    JSON.stringify(state?.trainingLogs),
    workspaceid,
  ]);
  useEffect(() => {
    if (!isEdit) {
      if (isChatBot) {
        setFieldValue("temperature", values?.temperature || 0.1);
        setFieldValue("showReferencesToInternalDocs", true);

        setFieldValue("hasTopicsContext", isKnowledgeEnabled);
        setFieldValue("hasFunctions", isKnowledgeEnabled);
      } else {
        setFieldValue("hasTopicsContext", true);
      }
    }
  }, [isChatBot, isKnowledgeEnabled, values?.mode, isEdit]);
  useEffect(() => {
    if (values?.hasTopicsContext != true) setFieldValue("blender", false);
  }, [values?.hasTopicsContext]);
  useEffect(() => {
    if (
      values?.mode == "coder" &&
      values?.allowDocsUpload == true &&
      values?.hasCode == true
    ) {
      setCodeUploadDisabled(false);
      setFieldValue("allowDocsUpload", !values?.hasCode);
      setTimeout(() => {
        setCodeUploadDisabled(true);
      }, 300);
    } else if (values?.mode == "coder" && values?.hasCode != true)
      setCodeUploadDisabled(false);
  }, [values?.allowDocsUpload, values?.hasCode, values?.mode]);
  useEffect(() => {
    if (values?.isMultiLanguageEnabled) {
      setFieldValue("charting", false);
      setTimeout(() => {
        setDisableDueToTranslation(true);
      }, 500);
    } else {
      setDisableDueToTranslation(false);
    }
  }, [values?.isMultiLanguageEnabled]);
  const handleAudioSelection = async (file) => {
    if (!file) {
      setFieldValue("voiceFileKey", null);
      return;
    }
    // Extract the file name and extension
    // generate a random file name
    const filename = file.name;
    const _fileN = String(Date.now()) + filename;
    const contentType = filename.slice(
      (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
    );
    const result = await S3uploadUrlRequest({
      filename: `${workspaceid}/${_fileN}`,
      importType: "imported_audio_voices",
      contentType,
    });
    const upload = await axios.request({
      method: "put",
      url: JSON.parse(result?.body)?.uploadURL,
      data: file,
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (p) => {
        setFileUploadInProgress(true);
        setFileUploadProgress(Math.round((p.loaded * 100) / p.total));
        if (p.loaded === p.total) {
          setFileUploadInProgress(false);
          setFileUploadProgress(100);
        }
      },
    });
    if (JSON.parse(result?.body).uploadURL) {
      setFieldValue(
        "voiceFileKey",
        `imported_audio_files/${workspaceid}/${_fileN}`,
      );
      // Clone the uploaded clip so livecalls use it (voiceName + ttsModel),
      // mirroring the mic-recording path above.
      await cloneVoiceFromClip(file);
    }
  };

  const handleIconSelection = async (file, isLargeLogo) => {
    if (!file && isLargeLogo) {
      setFieldValue("largeLogo", null);
      setAgentLogo(null);
      return;
    } else if (!file) {
      setFieldValue("icon", null);
      setAgentImage(null);
      return;
    }

    // Convert to PNG if it's an image but not already PNG
    let processedFile = file;
    if (file.type.startsWith("image/") && file.type !== "image/png") {
      try {
        processedFile = await convertImageToPng(file);
      } catch (error) {
        console.error("Failed to convert image:", error);
        AppToasty.show(t("toasts.logo.convertFailed"), {
          type: "danger",
        });
        return;
      }
    }

    // Reflect the selection in the UI/preview immediately; the effect on
    // values.icon/values.largeLogo replaces it with the S3 URL once uploaded.
    const localPreviewUrl = URL.createObjectURL(processedFile);
    if (isLargeLogo) setAgentLogo(localPreviewUrl);
    else setAgentImage(localPreviewUrl);

    // Extract the file name and extension
    // generate a random file name
    const filename = processedFile.name;
    const _fileN = String(Date.now()) + filename;
    const contentType = filename.slice(
      (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
    );
    const result = await S3uploadUrlRequest({
      filename: `${workspaceid}/${_fileN}`,
      importType: "imported-image",
      contentType,
    });
    const upload = await axios.request({
      method: "put",
      url: JSON.parse(result?.body)?.uploadURL,
      data: processedFile,
      headers: {
        "Content-Type": processedFile.type,
      },
      onUploadProgress: (p) => {
        setFileUploadInProgress(true);
        setFileUploadProgress(Math.round((p.loaded * 100) / p.total));
        if (p.loaded === p.total) {
          setFileUploadInProgress(false);
          setFileUploadProgress(100);
        }
      },
    });
    if (JSON.parse(result?.body).uploadURL) {
      setFieldValue(isLargeLogo ? "largeLogo" : "icon", _fileN);
    }
  };
  useEffect(() => {
    if (
      values?.plainTextOutput ||
      (!["sorcerer", "mystica", "sorcerer_15", "mystica_15"]?.find(
        (u) => u == values?.llmBaseModel,
      ) &&
        values?.llmBaseModel != null)
    ) {
      setFieldValue("charting", false);
      setFieldValue("hasImages", false);
      // setFieldValue("allowDocsUpload", false);
      setTimeout(() => {
        setDisableMultiModal(true);
      }, 500);
    } else {
      setDisableMultiModal(false);
    }
  }, [
    values?.plainTextOutput,
    values?.llmProvider,
    values?.llmBaseModel,
    values?.isMultiLanguageEnabled,
  ]);
  useEffect(() => {
    if (values?.restrictedAccess) {
      setFieldValue("preventWidgetUsage", true);
    }
  }, [values?.restrictedAccess]);

  // Prepopulate VM dropdown based on agentDesktopVM IP
  useEffect(() => {
    if (values?.agentDesktopVM && state?.vms?.length > 0) {
      const matchingVm = state?.vms?.find(
        (vm) => vm?.ip === values?.agentDesktopVM,
      );
      if (matchingVm && (!values?.vms || values?.vms !== matchingVm.id)) {
        setFieldValue("vms", matchingVm.id);
      }
    } else if (!values?.agentDesktopVM && values?.vms) {
      // If no IP is set but dropdown has value, set to "none"
      setFieldValue("vms", "none");
    }
  }, [values?.agentDesktopVM, state?.vms]);
  const getMaxTokensBasedOnModels = (values) => {
    return (
      _baseModels?.find((u) => u?.id == values?.llmBaseModel)?.maxTokens || 8192
    );
  };
  function getMaxTokens(
    isContentCreator,
    isChatBot,
    getActiveWorkspace,
    values,
  ) {
    if (isContentCreator) {
      return getActiveWorkspace()?.isExtendedContentCreatorEnabled
        ? getMaxTokensBasedOnModels(values)
        : getMaxTokensBasedOnModels(values);
    } else if (isChatBot) {
      if (
        values?.mode === "coder" ||
        values?.mode === "chatter" ||
        values?.mode === "retriever"
      )
        return getMaxTokensBasedOnModels(values);
      else {
        return getMaxTokensBasedOnModels(values);
      }
    } else {
      return getMaxTokensBasedOnModels(values);
    }
  }

  const _topics = getWorkspaceEntities("topic")?.map((topic) => {
    topic.name = topic["label"];
    topic.category = topic["type"];
    return { ...topic };
  });

  const _agentGroups = useMemo(
    () =>
      (getWorkspaceEntities("topic") || []).map((g) => ({
        title: g?.emoji ? `${g.emoji} ${g.label}` : g.label,
        id: g.id,
        color: g?.backgroundColor,
        emoji: g?.emoji,
      })),
    [state?.entities, state?.activeWorkspaceID],
  );

  useEffect(() => {
    // values.allowedTopics can arrive as a non-array (e.g. a single topic id
    // string or an object), which would throw on .map. Coerce to a plain
    // array and derive the id list once before filtering.
    const allowedTopicsArray = Array.isArray(values?.allowedTopics)
      ? values.allowedTopics
      : values?.allowedTopics != null
      ? [values.allowedTopics]
      : [];
    // Always surface the latest documents available per topic in the static
    // docs dropdown: prefer ALL workspace documents (mrcDocuments) over the
    // embedded-only subset, and never filter by any status (embedding /
    // published). Fall back to the embedded subset if the full set hasn't
    // loaded yet.
    const availableDocs =
      state?.mrcDocuments?.length > 0
        ? state?.mrcDocuments
        : state?.embeddedMrcDocuments;
    if (allowedTopicsArray.length > 0) {
      const allowedTopicIds = allowedTopicsArray
        .map((v) => (v && typeof v === "object" ? v?.id : v))
        .filter((id) => id != null)
        .map((id) => String(id));
      let filteredObjects = (availableDocs || [])
        ?.filter((obj) => {
          const objTopics = (obj?.topics || []).map((t) => String(t));
          return objTopics.some((id) => allowedTopicIds.includes(id));
        })
        ?.map((u) => {
          // Published docs whose embedding is not ready yet are still being
          // learned: surface that in the dropdown, localised the same way the
          // knowledge hub does (training.status.inProgress).
          const learningInProgress =
            u?.status === "published" && u?.embedding_status !== "ready";
          const displayTitle = learningInProgress
            ? `${u?.title} (${t("training.status.inProgress")})`
            : u?.title;
          return {
            ...u,
            backgroundColor: currentTheme?.theme?.primary,
            name: displayTitle,
            category: "docs",
            label: displayTitle,
            value: u?.id,
          };
        });
      setFilteredDocs(filteredObjects);
    } else {
      setFilteredDocs([]);
      setFieldValue("staticDocs", null);
    }
  }, [values?.allowedTopics, state?.mrcDocuments, state?.embeddedMrcDocuments]);

  // Guardrail: Validate agentsPool against loaded agents to remove stale/deleted agents
  // Similar to how allowedTopics manages filteredDocs
  useEffect(() => {
    if (
      values?.agentsPool &&
      values?.agentsPool?.length > 0 &&
      _agents?.length > 0
    ) {
      const validAgentIds = _agents?.map((a) => a?.id);
      const filteredAgentsPool = values?.agentsPool?.filter((agent) => {
        // Handle both object format (from enhanceAgents) and ID format
        const agentId = typeof agent === "object" ? agent?.id : agent;
        const agentData = _agents?.find((a) => a?.id === agentId);
        return (
          validAgentIds?.includes(agentId) &&
          agentData?.restrictedAccess !== true &&
          agentData?.mode !== "voice" &&
          agentData?.mode !== "planner"
        );
      });

      // Only update if we found stale agents to remove
      if (filteredAgentsPool?.length !== values?.agentsPool?.length) {
        console.log(
          `[agentsPool] Removed ${
            values?.agentsPool?.length - filteredAgentsPool?.length
          } stale agent(s) from pool`,
        );
        setFieldValue("agentsPool", filteredAgentsPool);
      }
    }
  }, [values?.agentsPool, _agents, setFieldValue]);

  const _functionContext = [
    {
      id: "customer",
      label: "Customer",
      type: "ner",
      backgroundColor: currentTheme?.theme?.primary,
    },
    {
      id: "case",
      label: "Case",
      type: "ner",
      backgroundColor: currentTheme?.theme?.green,
    },
    {
      id: "chat",
      label: "Chat",
      type: "ner",
      backgroundColor: currentTheme?.theme?.orange,
    },
  ]?.map((topic) => {
    topic.name = topic["label"];
    topic.category = topic["type"];
    return { ...topic };
  });
  // REMOVE FILTER WHEN AGENT DESKTOP CAN BE USED BY PLANNER
  const fetchAgents = async () => {
    const __agents = await loadAgents(getActiveWorkspace()?.id);
    if (__agents?.length > 0) {
      _setAgents(
        __agents
          ?.filter((u) => u?.agentType == "Chatbot" && u?.mode != "planner")
          ?.map((agent) => {
            return {
              id: agent["id"],
              label: agent["label"],
              name: agent["label"],
              value: agent["id"],
              category: agent["agentType"],
              mode: agent["mode"],
              restrictedAccess: agent?.restrictedAccess,
              backgroundColor: colorsMappings.mapBackgroundColorWithAgentTypeAndMode(
                agent,
                currentTheme,
              ),
            };
          }),
      );
    }
  };
  const fetchAuthorisations = async () => {
    const __authorisations = await authorisationsHelper.getAuthorisations(
      getActiveWorkspace()?.id,
    );
    if (__authorisations?.data?.authorisationsByWorkspace?.items?.length > 0) {
      _setAuthorisations(
        __authorisations?.data?.authorisationsByWorkspace?.items
          ?.filter((u) => u?.type == "username_and_password")
          ?.map((auth) => {
            return {
              id: auth["id"],
              label: auth["name"],
              name: auth["name"],
              value: auth["id"],
              category: auth["type"],
              backgroundColor: colorsMappings.mapBackgroundColorWithAgentTypeAndMode(
                auth,
                currentTheme,
              ),
            };
          }),
      );
    }
  };
  useEffect(() => {
    fetchAgents();
    fetchAuthorisations();
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!values?.isMultiLanguageEnabled)
      setFieldValue("advancedLanguageDetection", false);
  }, [values?.isMultiLanguageEnabled]);

  const appendCustomProviders = (baseModels, customHostings, type) => {
    const togetherHostings = customHostings?.filter((u) => u?.type == type);
    for (const hosting of togetherHostings) {
      baseModels.push({
        name: hosting?.name,
        id: hosting?.name,
      });
    }
    return baseModels;
  };

  const matchProvidersWithSubscription = (
    subscriptionPlan,
    functionCalling = false,
    customHostings = [],
  ) => {
    if (functionCalling) {
      let baseModels = [{ name: "ToothFairyAI", id: "tf" }];
      if (customHostings?.find((u) => u?.type == "together"))
        appendCustomProviders(baseModels, customHostings, "together");

      if (customHostings?.find((u) => u?.type == "azure_openai"))
        appendCustomProviders(baseModels, customHostings, "azure_openai");
      if (customHostings?.find((u) => u?.type == "aws"))
        appendCustomProviders(baseModels, customHostings, "aws");
      if (customHostings?.find((u) => u?.type == "groq"))
        appendCustomProviders(baseModels, customHostings, "groq");
      return baseModels;
    }
    let hostingModels = [{ name: "ToothFairyAI", id: "tf" }] || [];
    if (customHostings?.find((u) => u?.type == "together"))
      appendCustomProviders(hostingModels, customHostings, "together");
    if (customHostings?.find((u) => u?.type == "replicate"))
      appendCustomProviders(hostingModels, customHostings, "replicate");
    if (customHostings?.find((u) => u?.type == "groq"))
      appendCustomProviders(hostingModels, customHostings, "groq");
    if (customHostings?.find((u) => u?.type == "ollama") && canAgentRunLocally)
      appendCustomProviders(hostingModels, customHostings, "ollama");
    if (customHostings?.find((u) => u?.type == "openai"))
      appendCustomProviders(hostingModels, customHostings, "openai");
    if (customHostings?.find((u) => u?.type == "azure_openai"))
      appendCustomProviders(hostingModels, customHostings, "azure_openai");
    if (customHostings?.find((u) => u?.type == "anthropic"))
      appendCustomProviders(hostingModels, customHostings, "anthropic");
    if (customHostings?.find((u) => u?.type == "google"))
      appendCustomProviders(hostingModels, customHostings, "google");

    if (customHostings?.find((u) => u?.type == "local"))
      appendCustomProviders(hostingModels, customHostings, "local");
    return hostingModels;
  };

  useEffect(() => {
    if (
      customHostings?.find(
        (u) => values?.llmProvider == u?.name && u?.type == "ollama",
      ) &&
      values?.llmBaseModel != null
    ) {
      // loadModel(values?.llmBaseModel);
    }
  }, [values?.llmProvider, values?.llmBaseModel, customHostings]);
  useEffect(() => {
    if (
      values?.maxTokens &&
      values?.reasoningBudget &&
      values?.reasoningBudget >= values?.maxTokens
    ) {
      setFieldValue("reasoningBudget", values?.maxTokens - 50);
    }
  }, [values?.maxTokens, values?.reasoningBudget]);
  useEffect(() => {
    if (
      state?.benchmarks?.length > 0 &&
      state?.batchLogs?.length &&
      initialMapValues?.id
    ) {
      setBenchmarks(getBenchmarksByAgent(initialMapValues?.id));
    } else {
      setBenchmarks([]);
    }
  }, [
    state?.benchmarks?.length,
    state?.batchLogs?.length,
    initialMapValues?.id,
  ]);
  const matchModelsWithProviders = (
    llmProvider,
    subscriptionPlan,
    functionCalling = false,
    customHostings = [],
  ) => {
    const llmProviderType = customHostings?.find((u) => u?.name == llmProvider)
      ?.type;
    const customModels =
      customHostings
        ?.filter((u) => u?.name == llmProvider)
        ?.map((u) => {
          if (u?.modelName)
            return {
              name: u?.modelName,
              id: u?.modelName,
            };
        })
        ?.filter((u) => u) || [];

    const getExternalModelsForProvider = (
      provider,
      includeToolCalling = false,
    ) => {
      const models =
        _externalModels?.filter((m) => m?.provider === provider) || [];
      const deprecatedModels =
        _externalDeprecatedModels?.filter((m) => m?.provider === provider) ||
        [];
      const allModels = [...models, ...deprecatedModels];

      if (includeToolCalling) {
        return allModels.filter((m) => m?.toolCalling === true);
      }
      return allModels;
    };

    if (functionCalling) {
      switch (llmProviderType) {
        case "tf":
          return [..._functionCallingModels, ...finetunedModels];
        case "aws":
          return [
            ...getExternalModelsForProvider("aws", true),
            ...customModels,
          ];
        case "openai":
          return [
            ...getExternalModelsForProvider("openai", true),
            ...customModels,
          ];
        case "anthropic":
          return [
            ...getExternalModelsForProvider("anthropic", true),
            ...customModels,
          ];
        case "google":
          return [
            ...getExternalModelsForProvider("google", true),
            ...customModels,
          ];
        case "azure_openai":
          return customModels;
        case "together":
          return [
            {
              name: "Mistral 7b",
              id: "mistralai/Mistral-7B-Instruct-v0.1",
            },
            {
              name: "Mixtral 8x7b",
              id: "mistralai/Mixtral-8x7B-Instruct-v0.1",
            },
            ...customModels,
          ];
        case "groq":
          return [
            {
              name: "Llama 3.1 8b",
              id: "llama-3.1-8b-instant",
            },
            {
              name: "Llama 3.1 70b",
              id: "llama-3.1-70b-versatile",
            },
            {
              name: "Llama 3.3 70b",
              id: "llama-3.3-70b-specdec",
            },
            {
              name: "QwQ-32B-Groq",
              id: "qwen-qwq-32b",
            },
            {
              name: "DeepSeek R1-Groq - Llama 3.3 70b Distill",
              id: "deepseek-r1-distill-llama-70b-specdec",
            },
            ...customModels,
          ];
        default:
          return [..._functionCallingModels, ...finetunedModels];
      }
    } else
      switch (llmProviderType) {
        case "tf":
          const baseModel = [..._baseModels, ..._proModels, ...finetunedModels];
          return baseModel;
        case "ollama":
          return modelsAvailable;

        case "openai":
          return [...getExternalModelsForProvider("openai"), ...customModels];
        case "anthropic":
          return [
            ...getExternalModelsForProvider("anthropic"),
            ...customModels,
          ];
        case "google":
          return [...getExternalModelsForProvider("google"), ...customModels];
        case "azure_openai":
          return customModels;
        case "together":
          return [
            ..._baseModels?.filter((u) => u?.tfOnly != true),
            ..._proModels,
            ...customModels,
          ];
        case "replicate":
          return [
            {
              name: "Mistral 7b v0.1",
              id: "mistralai/Mistral-7B-Instruct-v0.1",
            },
            {
              name: "Mistral 7b v0.2",
              id: "mistralai/Mistral-7B-Instruct-v0.2",
            },
            {
              name: "Mixtral 8x7b",
              id: "mistralai/Mixtral-8x7B-Instruct-v0.1",
            },
            {
              name: "Llama 3 8b",
              id: "meta/meta-llama-3-8b-instruct",
            },
            {
              name: "Llama 3 70b",
              id: "meta/meta-llama-3-70b-instruct",
            },
            {
              name: "DeepSeek R1 - 0528",
              id: "deepseek-ai/DeepSeek-R1_TF",
            },
            ...customModels,
          ];
        case "groq":
          return [
            {
              name: "Llama 3.1 8b",
              id: "llama-3.1-8b-instant",
            },
            {
              name: "Llama 3.1 70b",
              id: "llama-3.1-70b-versatile",
            },
            {
              name: "Llama 3.3 70b",
              id: "llama-3.3-70b-specdec",
            },
            {
              name: "QwQ-32B-Groq",
              id: "qwen-qwq-32b",
            },
            {
              name: "DeepSeek R1-Groq - Llama 3.3 70b Distill",
              id: "deepseek-r1-distill-llama-70b-specdec",
            },
            ...customModels,
          ];
        case "local":
          return [...customModels];

        default:
          const baseModels = [
            ..._baseModels,
            ..._proModels,
            ...finetunedModels,
          ];
          return baseModels;
      }
  };
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? color
          : isFocused
          ? color.alpha(0.1).css()
          : undefined,
        color: isDisabled
          ? "#ccc"
          : isSelected
          ? currentTheme?.theme?.white
            ? "white"
            : "black"
          : data?.backgroundColor,
        cursor: isDisabled ? "not-allowed" : "default",

        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? data?.backgroundColor
              : color.alpha(0.3).css()
            : undefined,
        },
      };
    },
    multiValue: (styles, { data }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: color.alpha(0.1).css(),
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data?.backgroundColor,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data?.backgroundColor,
      ":hover": {
        backgroundColor: data?.backgroundColor,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };

  return (
    <AppToast.ToastProvider
      {...AppConfig.notificationConfig}
      // offset for both top and bottom toasts..
    >
      <View
        style={{
          zIndex: -1,
        }}
      >
        {_showBuilderSection("profile") && (
          <View
            style={
              isBuilderLayout
                ? { width: "100%", maxWidth: 600, alignSelf: "center" }
                : null
            }
          >
            {isEdit && (
              <IdCopyView
                wrapperStyle={{
                  marginTop: 20,
                  marginBottom: 10,
                }}
                id={initialMapValues?.id}
              />
            )}
            <View>
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                label={t("settings.list.name")}
                placeholder={t("settings.qa.agentNamePlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="label"
                maxCharacters={50}
              />
            </View>
            <View>
              <AppForms.AppFormField
                showTextInputLabel
                multiline={false}
                label={t("settings.list.domain")}
                placeholder={t("settings.qa.subjectNamePlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="subject"
                maxCharacters={50}
              />
            </View>
            {values?.mode != null && (
              <View>
                <AppForms.AppFormField
                  showTextInputLabel
                  textContainer={{
                    minHeight: 120,
                  }}
                  multiline={true}
                  label={t("settings.list.description")}
                  placeholder={t("settings.qa.agentDescriptionPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="description"
                  maxCharacters={255}
                />

                <View>
                  <AppForms.AppFormDropDown
                    items={[
                      {
                        title: t("settings.qa.noAgentGroup", {
                          defaultValue: "No group",
                        }),
                        id: "none",
                      },
                      ..._agentGroups,
                    ]}
                    showTextInputLabel
                    isElevated={false}
                    label={t("settings.qa.agentGroup", {
                      defaultValue: "Group",
                    })}
                    name="agentCategoryType"
                    propertyValue={"id"}
                    propertyLabel="title"
                  />
                  {_agentGroups.length === 0 && (
                    <AppText
                      style={{
                        fontSize: 12,
                        marginTop: -5,
                        marginBottom: 10,
                        marginLeft: 5,
                      }}
                      color={currentTheme?.theme?.secondary_font_color}
                    >
                      {t("settings.qa.noAgentGroupsAvailable", {
                        defaultValue:
                          "No topics available. Create a topic to organize agents into groups.",
                      })}
                    </AppText>
                  )}
                </View>
              </View>
            )}
            <View>
              {isChatBot && (
                <View>
                  <AppForms.AppFormDropDown
                    isElevated={false}
                    dropDownStyle={{
                      borderColor: currentTheme?.theme?.border_color,
                      maxWidth: !isLargeScreen() && 250,
                    }}
                    showTextInputLabel
                    label={t("settings.qa.mode")}
                    isMandatory
                    name="mode"
                    items={_modes}
                    propertyLabel="name"
                    propertyValue="id"
                    disabled={isEdit}
                    _onValueChange={(item) => {
                      // Voice is not selectable in EU/US regions yet — the
                      // dropdown only shows it so users can see it's disabled.
                      if (
                        item === "voice" &&
                        !isVoiceAgentsAvailableInRegion()
                      ) {
                        setFieldValue("mode", values?.mode || _modes?.[0]?.id);
                      }
                    }}
                    description={
                      generateModes()?.find((u) => u?.id == values?.mode)
                        ?.description
                    }
                  />
                </View>
              )}

              {values?.mode === "custom" && (
                <AppForms.AppFormDropDown
                  isElevated={false}
                  dropDownStyle={{
                    borderColor: currentTheme?.theme?.border_color,
                    maxWidth: !isLargeScreen() && 250,
                  }}
                  showTextInputLabel
                  label={t("settings.qa.overview")}
                  isMandatory
                  name="qaUrl"
                  items={
                    [
                      {
                        name: t("settings.qa.default"),
                        propertyValue: "default_qa_model",
                      },
                      ...getTrainingLogsWithModels("readComprehensionTraining"),
                    ] || []
                  }
                  propertyLabel="name"
                  propertyValue="id"
                />
              )}
            </View>
            {isKnowledgeEnabled && values?.mode == "retriever" && (
              <>
                {values?.useInterleavedReasoning && (
                  <AppForms.AppFormDropDown
                    isElevated={false}
                    dropDownStyle={{
                      borderColor: currentTheme?.theme?.border_color,
                      maxWidth: !isLargeScreen() && 250,
                    }}
                    showTextInputLabel
                    label={t("settings.qa.executionPreset")}
                    name="interleavedPreset"
                    items={[
                      { name: t("settings.qa.presetShort"), id: "short" },
                      {
                        name: t("settings.qa.presetMedium"),
                        id: "medium",
                      },
                      { name: t("settings.qa.presetLong"), id: "long" },
                      {
                        name: t("settings.qa.presetMarathon"),
                        id: "marathon",
                      },
                    ]}
                    propertyLabel="name"
                    propertyValue="id"
                  />
                )}
                {isChatBot &&
                  values?.mode != "coder" &&
                  values?.agenticRAG != true &&
                  !(
                    _baseModels?.find((u) => u?.id == values?.llmBaseModel)
                      ?.reasoner ||
                    shouldShowReasonerDropdown(values?.llmBaseModel)
                  ) && (
                    <View>
                      <AppForms.AppFormSwitch
                        wrapperStyle={[
                          {
                            maxHeight: 30,
                            paddingHorizontal: 10,

                            paddingVertical: 18,
                            margin: 5,
                            marginTop: 10,
                          },
                        ]}
                        showTextInputLabel={true}
                        label={t("settings.qa.stepByStepReasoning")}
                        displayIsTicked={true}
                        name="blender"
                        value={values?.blender}
                        disabled={
                          values?.hasTopicsContext != true &&
                          values?.hasFunctions != true
                        }
                        description={t("settings.qa.stepByStepReasoningDesc")}
                        descriptionIcon={false}
                        descriptionColor={currentTheme?.theme?.mediumGrey}
                      />
                    </View>
                  )}
              </>
            )}
            {isChatBot && values?.mode === "coder" && !isAgentOllama && (
              <View>
                <AppForms.AppFormSwitch
                  wrapperStyle={[
                    {
                      maxHeight: 30,
                      paddingHorizontal: 10,
                      paddingVertical: 18,
                      margin: 5,
                      marginTop: 10,
                    },
                  ]}
                  showTextInputLabel={true}
                  label={t("settings.qa.codeExecution")}
                  displayIsTicked={true}
                  name="hasCode"
                  value={values?.hasCode}
                  description={t("settings.qa.codeExecutionDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                <AppForms.AppFormSwitch
                  wrapperStyle={[
                    {
                      maxHeight: 30,
                      paddingHorizontal: 10,
                      paddingVertical: 18,
                      margin: 5,
                      marginTop: 20,
                    },
                  ]}
                  showTextInputLabel={true}
                  label={t("settings.qa.codeUpload")}
                  displayIsTicked={true}
                  name="allowDocsUpload"
                  value={values?.allowDocsUpload}
                  disabled={codeUploadDisabled}
                  description={t("settings.qa.codeUploadDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />

                {/* <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 10,

                        paddingVertical: 18,
                        margin: 5,
                        marginTop: 20,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.allowExternalAPI")}
                    displayIsTicked={true}
                    name="allowExternalAPI"
                    value={values?.allowExternalAPI}
                    disabled={!values?.hasCode}
                  /> */}
              </View>
            )}
          </View>
        )}

        <View>
          {(values?.hasTopicsContext || values?.mode == "voice") &&
            isKnowledgeEnabled &&
            _showBuilderSection("knowledge") &&
            !isAgentOllama && (
              <SectionRenderer
                style={{ minWidth: 250 }}
                isExpandedOnLoad={true}
                title={t("settings.qa.knowledgeSettings")}
                icon={AppAssets.icons.notes}
              >
                <View
                  style={{
                    paddingLeft: 0,
                    marginHorizontal: 10,
                  }}
                >
                  <AppForms.AppFormMultiSelect
                    showTextInputLabel
                    label={t("settings.qa.assignTopics")}
                    name="allowedTopics"
                    key={`topics-${lastOpened}`}
                    topErrorOffset={-21}
                    touchedByDefault
                    placeholder={t("settings.qa.assignTopicsPlaceholder")}
                    hidePlaceholder={true}
                    items={_topics}
                    uniqueKey="id"
                    showDropDowns={true}
                    readOnlyHeadings={true}
                    labelOption="label"
                    groupByProperty="type"
                    style={{ maxWidth: 800 }}
                    maxSelectedValues={10}
                    description={
                      values?.allowedTopics?.length >= 10
                        ? t("settings.qa.maxTopicsReached")
                        : t("settings.qa.selectUpToTopics", { count: 10 })
                    }
                    descriptionIcon={AppAssets?.icons?.question_icon}
                    onError={(error) => {
                      AppToasty.show(t("toasts.topics.maxReached"), {
                        type: "warning",
                      });
                    }}
                    wrapperContainerStyle={{
                      zIndex: 20,
                      marginBottom: 10,
                    }}
                    styles={colourStyles}
                    options={_topics.map((u) => {
                      return {
                        ...u,
                        value: u?.id,
                      };
                    })}
                  />
                  {values?.allowedTopics && values?.allowedTopics?.length > 0 && (
                    <View
                      style={{
                        zIndex: 10,
                      }}
                    >
                      <AppForms.AppFormMultiSelect
                        showTextInputLabel
                        key={`staticdocs-${lastOpened}`}
                        label={t("settings.qa.assignStaticDocs")}
                        name="staticDocs"
                        chipHref={chipHrefKnowledgeHubDoc}
                        topErrorOffset={-21}
                        touchedByDefault
                        placeholder={t(
                          "settings.qa.assignStaticDocsPlaceholder",
                        )}
                        hidePlaceholder={true}
                        items={filteredDocs}
                        options={filteredDocs.map((u) => {
                          return {
                            ...u,
                            value: u?.id,
                          };
                        })}
                        uniqueKey="id"
                        showDropDowns={true}
                        readOnlyHeadings={true}
                        labelOption="title"
                        groupByProperty="embedding_status"
                        style={{ maxWidth: 800 }}
                        wrapperContainerStyle={{
                          zIndex: 10,
                          marginBottom: 10,
                        }}
                        styles={colourStyles}
                        maxSelectedValues={10}
                        onError={(error) => {
                          AppToasty.show(t("toasts.documents.maxReached"), {
                            type: "warning",
                          });
                        }}
                        descriptionIcon={null}
                        description={
                          values?.staticDocs?.length >= 10
                            ? t("settings.qa.maxStaticDocsReached")
                            : t("settings.qa.selectUpToStaticDocs", {
                                count: 10,
                              })
                        }
                      />
                    </View>
                  )}

                  {values?.agenticRAG && (
                    <>
                      <AppForms.AppFormDropDown
                        isElevated={false}
                        formFieldContainerStyle={{
                          marginBottom: 15,
                          marginTop: -10,
                        }}
                        dropDownStyle={{
                          borderColor: currentTheme?.theme?.border_color,
                          maxWidth: !isLargeScreen() && 250,
                        }}
                        showTextInputLabel
                        label={t("settings.qa.agenticSearchStrategy")}
                        name="ragSearchStrategy"
                        items={[
                          { name: t("settings.qa.parallel"), id: "parallel" },
                          {
                            name: t("settings.qa.targetedFirst"),
                            id: "targeted_first",
                          },
                          {
                            name: t("settings.qa.vectorFirst"),
                            id: "vector_first",
                          },
                        ]}
                        propertyLabel="name"
                        propertyValue="id"
                      />
                    </>
                  )}
                  {values?.mode != "voice" && (
                    <View>
                      <AppForms.AppFormSwitch
                        wrapperStyle={fieldSwitchWrapper}
                        showTextInputLabel={true}
                        label={t("settings.qa.keywordsForKnowledgeBase")}
                        displayIsTicked={true}
                        name="keyWordsForKnowledgeBase"
                        value={values?.keyWordsForKnowledgeBase}
                        description={t(
                          "settings.qa.keywordsForKnowledgeBaseDesc",
                        )}
                        descriptionIcon={false}
                        descriptionColor={currentTheme?.theme?.mediumGrey}
                      />
                      <AppForms.AppFormSwitch
                        wrapperStyle={fieldSwitchWrapper}
                        showTextInputLabel={true}
                        label={t("settings.qa.rerankResultsOnRetrieval")}
                        displayIsTicked={true}
                        name="reRank"
                        value={values?.reRank}
                        description={t(
                          "settings.qa.rerankResultsOnRetrievalDesc",
                        )}
                        descriptionIcon={false}
                        descriptionColor={currentTheme?.theme?.mediumGrey}
                      />
                      {values?.mode == "retriever" && (
                        <AppForms.AppFormSwitch
                          wrapperStyle={fieldSwitchWrapper}
                          showTextInputLabel={true}
                          label={t("settings.qa.topicsEnhancement")}
                          displayIsTicked={true}
                          name="topicEnhancer"
                          value={values?.topicEnhancer}
                          description={t("settings.qa.topicsEnhancementDesc")}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                      )}
                      {values?.mode == "retriever" && (
                        <AppForms.AppFormSwitch
                          wrapperStyle={fieldSwitchWrapper}
                          showTextInputLabel={true}
                          label={t("settings.qa.crossDocumentAnalysis")}
                          displayIsTicked={true}
                          name="chainOfThoughts"
                          value={values?.chainOfThoughts}
                          disabled={
                            values?.hasTopicsContext != true &&
                            values?.hasFunctions != true
                          }
                          description={t(
                            "settings.qa.crossDocumentAnalysisDesc",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                      )}
                      {isKnowledgeEnabled && values?.mode != "coder" && (
                        <View>
                          <AppForms.AppFormSwitch
                            wrapperStyle={fieldSwitchWrapper}
                            showTextInputLabel={true}
                            label={t("settings.qa.enableRetrievalWithNER")}
                            displayIsTicked={true}
                            name="hasNER"
                            value={values?.hasNER}
                            description={t(
                              "settings.qa.enableRetrievalWithNERDesc",
                            )}
                            descriptionIcon={false}
                            descriptionColor={currentTheme?.theme?.mediumGrey}
                          />
                          <AppForms.AppFormSwitch
                            wrapperStyle={fieldSwitchWrapper}
                            showTextInputLabel={true}
                            label={t("settings.qa.enableImagesRetrieval")}
                            displayIsTicked={true}
                            name="hasImages"
                            value={values?.hasImages}
                            disabled={disableMultiModal}
                            description={
                              disableMultiModal
                                ? t("settings.qa.imagesRetrievalDisabled")
                                : t("settings.qa.enableImagesRetrievalDesc")
                            }
                            descriptionIcon={!!disableMultiModal}
                            descriptionColor={
                              disableMultiModal
                                ? currentTheme?.theme?.orange
                                : currentTheme?.theme?.mediumGrey
                            }
                          />
                        </View>
                      )}
                    </View>
                  )}

                  <View>
                    <AppForms.AppFormTrackSlider
                      step={1}
                      maximumValue={
                        getActiveWorkspace()
                          ?.isExtendedBusinessAnalystReportEnabled
                          ? 50
                          : 25
                      }
                      minimumValue={1}
                      label={t("settings.qa.topK")}
                      name="topK"
                      description={t("settings.qa.topKDesc")}
                    />
                    <AppForms.AppFormTrackSlider
                      step={1}
                      maximumValue={
                        getActiveWorkspace()
                          ?.isExtendedBusinessAnalystReportEnabled
                          ? 50
                          : 25
                      }
                      minimumValue={1}
                      label={t("settings.qa.docTopK")}
                      name="docTopK"
                      description={t("settings.qa.docTopKDesc")}
                    />
                    <AppForms.AppFormTrackSlider
                      step={0.01}
                      maximumValue={0.99}
                      minimumValue={0.01}
                      visualMultiplier={100}
                      unitLabel="%"
                      label={t("settings.qa.minRetrievalAccuracy")}
                      name="minRetrievalScore"
                      description={t("settings.qa.minRetrievalAccuracyDesc")}
                    />
                    <AppForms.AppFormTrackSlider
                      step={0.01}
                      maximumValue={1}
                      minimumValue={0.01}
                      visualMultiplier={100}
                      unitLabel="%"
                      label={t("settings.qa.inContextRetrievalImportance")}
                      name="contextRelevancyRatio"
                      description={t(
                        "settings.qa.inContextRetrievalImportanceDesc",
                      )}
                    />
                    <AppForms.AppFormTrackSlider
                      step={1}
                      maximumValue={5}
                      minimumValue={0}
                      label={t("settings.qa.recencyImportance")}
                      name="recencyImportance"
                      description={t("settings.qa.recencyImportanceDesc")}
                    />
                    <AppForms.AppFormTrackSlider
                      step={1}
                      maximumValue={5}
                      minimumValue={0}
                      label={t("settings.qa.keywordsImportance")}
                      name="keywordsWeightInRetrieval"
                      description={t("settings.qa.keywordsImportanceDesc")}
                    />
                    {values?.agenticRAG == true && (
                      <>
                        <AppForms.AppFormTrackSlider
                          step={0.01}
                          maximumValue={1}
                          minimumValue={0.25}
                          visualMultiplier={100}
                          unitLabel="%"
                          label={t("settings.qa.minChunksBeforeCompleteness")}
                          name="minChunksBeforeCompleteness"
                          description={t(
                            "settings.qa.minChunksBeforeCompletenessDesc",
                          )}
                        />
                        <AppForms.AppFormTrackSlider
                          step={0.01}
                          maximumValue={1}
                          minimumValue={0.25}
                          visualMultiplier={100}
                          unitLabel="%"
                          label={t("settings.qa.agenticCompletenessThreshold")}
                          name="ragCompletenessThreshold"
                          description={t(
                            "settings.qa.agenticCompletenessThresholdDesc",
                          )}
                        />
                      </>
                    )}
                  </View>

                  {isBusinessAnalyst &&
                    getActiveWorkspace()
                      ?.isExtendedBusinessAnalystReportEnabled && (
                      <View>
                        <AppForms.AppFormSwitch
                          wrapperStyle={fieldSwitchWrapper}
                          showTextInputLabel={true}
                          label={t("settings.qa.extendedReport")}
                          displayIsTicked={true}
                          name="generateExtendedReport"
                          value={values?.generateExtendedReport}
                          description={t("settings.qa.extendedReportDesc")}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                      </View>
                    )}
                </View>
              </SectionRenderer>
            )}
        </View>
        {isChatBot &&
          _showBuilderSection("tools") &&
          (isKnowledgeEnabled && values?.mode != "coder" && !isAgentOllama ? (
            <View
              style={{
                zIndex: values?.mode == "voice" ? 0 : -1,
              }}
            >
              <SectionRenderer
                expanderStyle={{
                  zIndex: 500,
                }}
                style={{ minWidth: 250, zIndex: 20 }}
                isExpandedOnLoad={builderActiveSection != null}
                isFullWidth
                title={t("settings.qa.toolsAndHooksSettings", {
                  defaultValue: "Tools & hooks settings",
                })}
                icon={AppAssets.icons.advanced}
              >
                <View
                  style={{
                    flexDirection: isLargeScreen() ? "row" : "column",
                    gap: 24,
                    alignItems: "stretch",
                  }}
                >
                  <View style={{ flex: 1, maxWidth: 600 }}>
                    <View
                      style={{
                        paddingLeft: 10,
                        zIndex: 30,
                      }}
                    >
                      <AppForms.AppFormMultiSelect
                        showTextInputLabel
                        key={`functions-${lastOpened}`}
                        label={t("tools.title")}
                        name="agentFunctions"
                        chipHref={chipHrefFunctions}
                        placeholder={t("settings.qa.assignToolsPlaceholder")}
                        hidePlaceholder={true}
                        items={availableFunctions}
                        uniqueKey="id"
                        showDropDowns={true}
                        readOnlyHeadings={true}
                        labelOption="name"
                        groupByProperty="type"
                        style={{ maxWidth: 800 }}
                        wrapperContainerStyle={{
                          flex: 1,
                          zIndex: 30,
                        }}
                        styles={colourStyles}
                        formatOptionLabel={makeOptionLabelWithDescription(
                          currentTheme,
                          getToolOptionDescription,
                        )}
                        description={t("settings.qa.assignToolsDesc", {
                          defaultValue:
                            "Tools let the agent call APIs, databases, MCP servers and custom code. Assign the tools this agent can use — each option shows what it does.",
                        })}
                        descriptionIcon={false}
                        descriptionColor={currentTheme?.theme?.mediumGrey}
                        options={availableFunctions?.map((u) => {
                          return {
                            ...u,
                            label:
                              u?.name +
                              " - " +
                              Utility?.mapFunctionTypeToLabel(u),
                            value: u?.id,
                            backgroundColor: Utility?.mapFunctionTypeToColour(
                              u,
                              currentTheme,
                            ),
                          };
                        })}
                      />

                      <AppForms.AppFormMultiSelect
                        showTextInputLabel
                        key={`exec_env-${lastOpened}`}
                        label={t("settings.qa.executionHooks", {
                          defaultValue: "Execution hooks",
                        })}
                        name="customCodeExecutionEnvironments"
                        chipHref={chipHrefExecEnvs}
                        placeholder={t("settings.qa.assignExecutionHooks")}
                        hidePlaceholder={true}
                        items={customCodeExecutionEnvironments}
                        uniqueKey="id"
                        showDropDowns={true}
                        readOnlyHeadings={true}
                        labelOption="name"
                        groupByProperty="type"
                        style={{ maxWidth: 800 }}
                        wrapperContainerStyle={{
                          // flex: 1,
                          zIndex: 20,
                          // marginBottom: 20,
                        }}
                        styles={colourStyles}
                        formatOptionLabel={makeOptionLabelWithDescription(
                          currentTheme,
                          getHookOptionDescription,
                        )}
                        description={`${t("settings.qa.oneHookPerTurn")}`}
                        descriptionIcon={false}
                        descriptionColor={currentTheme?.theme?.mediumGrey}
                        options={customCodeExecutionEnvironments?.map((u) => {
                          return {
                            ...u,
                            label: u?.name + " - " + t("settings.qa.hook"),
                            value: u?.id,
                            backgroundColor: Utility?.mapFunctionTypeToColour(
                              u,
                              currentTheme,
                            ),
                          };
                        })}
                      />

                      <AppForms.AppFormFieldJSONV2
                        showTextInputLabel
                        errorMessageTopOffset={-18}
                        wrapperContainer={{
                          height: 290,
                          paddingTop: 10,
                          marginBottom: 20,
                        }}
                        // change
                        mode="code"
                        // schema={validations.parametersVal}
                        label={t("settings.qa.agentParameters")}
                        name="agentFnParams"
                        darkMode={mode === "dark"}
                        description={t("settings.qa.agentParametersDesc", {
                          defaultValue:
                            "A JSON object dynamically injected into the request body of every tool this agent calls. It overrides the tool's own parameters.",
                        })}
                        descriptionIcon={false}
                        descriptionColor={currentTheme?.theme?.mediumGrey}
                      />
                    </View>
                    <View
                      style={{
                        paddingLeft: 10,
                        zIndex: 10,
                        paddingBottom: 40,
                        marginTop: -20,
                      }}
                    >
                      <AppForms.AppFormMultiSelect
                        showTextInputLabel
                        key={`functioncontext-${lastOpened}`}
                        label={t("settings.qa.context")}
                        name="functionContext"
                        chipHref={chipHrefFunctions}
                        topErrorOffset={-21}
                        touchedByDefault
                        placeholder={t("settings.qa.assignContextPlaceholder")}
                        hidePlaceholder={true}
                        items={_functionContext}
                        uniqueKey="id"
                        showDropDowns={true}
                        readOnlyHeadings={true}
                        labelOption="label"
                        groupByProperty="type"
                        style={{ maxWidth: 800 }}
                        wrapperContainerStyle={{
                          flex: !isLargeScreen() ? 1 : 0.5,
                          zIndex: 20,
                        }}
                        styles={colourStyles}
                        formatOptionLabel={makeOptionLabelWithDescription(
                          currentTheme,
                          getToolOptionDescription,
                        )}
                        description={t("settings.qa.contextDesc", {
                          defaultValue:
                            "Inject customer, case and conversation data into tool calls so the agent can complete them with info that isn't in the chat.",
                        })}
                        descriptionIcon={false}
                        descriptionColor={currentTheme?.theme?.mediumGrey}
                        options={_functionContext.map((u) => {
                          return {
                            ...u,
                            value: u?.id,
                          };
                        })}
                      />
                    </View>
                  </View>
                  {isLargeScreen() ? (
                    <ToolsInsightPanel
                      tools={values?.agentFunctions}
                      hooks={values?.customCodeExecutionEnvironments}
                      currentTheme={currentTheme}
                      t={t}
                      getTypeLabel={(item) =>
                        Utility?.mapFunctionTypeToLabel(item)
                      }
                      history={history}
                      workspaceId={workspaceid}
                    />
                  ) : null}
                </View>
              </SectionRenderer>
            </View>
          ) : (
            <View style={{ width: "100%" }}>
              <SectionUnavailable
                icon={AppAssets.icons.tools}
                title={t("settings.qa.toolsUnavailableTitle", {
                  defaultValue: "Tools aren't available for this agent",
                })}
                message={t("settings.qa.toolsUnavailableMessage", {
                  defaultValue:
                    "This agent mode doesn't support tools and hooks. Switch the agent to a mode that supports them to assign tools, execution hooks and parameters.",
                })}
                suggestedModes={[
                  t("settings.qa.modeRetriever", { defaultValue: "Retriever" }),
                  t("settings.qa.modeCoder", { defaultValue: "Coder" }),
                ]}
              />
            </View>
          ))}

        {isChatBot &&
          _showBuilderSection("internet") &&
          (isKnowledgeEnabled &&
          values?.mode != "coder" &&
          values?.mode != "voice" ? (
            <View
              style={{
                zIndex: values?.mode == "retriever" ? -30 : -1,
              }}
            >
              <SectionRenderer
                style={{
                  minWidth: 250,
                  zIndex: -1,
                }}
                isExpandedOnLoad={builderActiveSection != null}
                title={t("settings.qa.internetSearch")}
                icon={AppAssets.icons.network}
              >
                <View style={{ marginHorizontal: 10, paddingLeft: 0 }}>
                  <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 0,

                        paddingVertical: 18,
                        marginHorizontal: 5,
                        marginVertical: 10,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.allowDeepSearch")}
                    displayIsTicked={true}
                    name="allowDeepInternetSearch"
                    value={values?.allowDeepInternetSearch}
                    description={t("settings.qa.allowDeepSearchDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  <View
                    style={{
                      paddingLeft: 0,
                      zIndex: 20,
                      marginTop: 30,
                    }}
                  >
                    <AppForms.AppFormMultiSelect
                      name="internetSearchModes"
                      placeholder={t("settings.qa.selectSearchModes")}
                      hidePlaceholder={true}
                      key={`internetmodes-${lastOpened}`}
                      items={[
                        {
                          label: "search",
                          value: "search",
                          backgroundColor: currentTheme?.theme?.primary,
                        },
                        {
                          label: "news",
                          value: "news",
                          backgroundColor: currentTheme?.theme?.green,
                        },
                        {
                          label: "images",
                          value: "images",
                          backgroundColor: currentTheme?.theme?.orange,
                        },
                        {
                          label: "shopping",
                          value: "shopping",
                          backgroundColor: currentTheme?.theme?.purple,
                        },
                        {
                          label: "videos",
                          value: "videos",
                          backgroundColor: currentTheme?.theme?.red,
                        },
                      ]}
                      uniqueKey="value"
                      showDropDowns={true}
                      readOnlyHeadings={true}
                      labelOption="name"
                      groupByProperty="type"
                      style={{ maxWidth: 800 }}
                      wrapperContainerStyle={{
                        flex: !isLargeScreen() ? 1 : 0.5,
                        zIndex: 20,
                      }}
                      styles={colourStyles}
                      options={[
                        {
                          label: "search",
                          value: "search",
                          backgroundColor: currentTheme?.theme?.primary,
                        },
                        {
                          label: "news",
                          value: "news",
                          backgroundColor: currentTheme?.theme?.green,
                        },
                        {
                          label: "images",
                          value: "images",
                          backgroundColor: currentTheme?.theme?.orange,
                        },
                        {
                          label: "shopping",
                          value: "shopping",
                          backgroundColor: currentTheme?.theme?.purple,
                        },
                        {
                          label: "videos",
                          value: "videos",
                          backgroundColor: currentTheme?.theme?.red,
                        },
                      ]}
                    />
                    <AppForms.AppFormDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                        maxWidth: !isLargeScreen() && 250,
                        height: 35,
                      }}
                      showTextInputLabel
                      label={t("settings.qa.internetSearchLocation")}
                      isMandatory
                      name="internetSearchLocation"
                      items={internetSearchLocations?.map((u) => {
                        {
                          return {
                            ...u,
                            id: u?.name,
                          };
                        }
                      })}
                      propertyLabel="name"
                      propertyValue="id"
                    />
                  </View>
                  <View
                    style={{
                      marginLeft: 0,
                      paddingLeft: 0,
                    }}
                  >
                    <AppForms.AppFormTrackSlider
                      step={1}
                      maximumValue={
                        getActiveWorkspace()
                          ?.isExtendedBusinessAnalystReportEnabled
                          ? 1000
                          : 100
                      }
                      minimumValue={1}
                      label={t("settings.qa.maxSearchResults")}
                      name="maxUrlsForInternetSearch"
                    />
                    <AppForms.AppFormTrackSlider
                      step={0.01}
                      maximumValue={1}
                      minimumValue={0.01}
                      label={t("settings.qa.minPagesSearchThreshold")}
                      name="minDeepSearchPagesPercentage"
                      visualMultiplier={100}
                      unitLabel="%"
                    />
                  </View>

                  <AppForms.AppFormField
                    showTextInputLabel
                    multiline={true}
                    textContainer={{
                      minHeight: 120,
                    }}
                    errorOffset={0}
                    label={t("settings.qa.excludedDomainsFromSearch")}
                    placeholder={t("settings.qa.excludedDomainsPlaceholder")}
                    keyboardtype="default"
                    textContextType="label"
                    autoCorrect={false}
                    name="bannedDomains"
                    textInputLabelStyle={{
                      marginLeft: 15,
                    }}
                  />
                  <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 0,

                        paddingVertical: 18,
                        marginHorizontal: 5,
                        marginBottom: 10,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.allowProxyScraping")}
                    displayIsTicked={true}
                    name="allowProxyScraping"
                    value={values?.allowProxyScraping}
                    description={t("settings.qa.allowProxyScrapingDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                </View>
              </SectionRenderer>
            </View>
          ) : (
            <View style={{ width: "100%" }}>
              <SectionUnavailable
                icon={AppAssets.icons.network}
                title={t("settings.qa.internetUnavailableTitle", {
                  defaultValue:
                    "Internet search isn't available for this agent",
                })}
                message={t("settings.qa.internetUnavailableMessage", {
                  defaultValue:
                    "This agent mode doesn't support internet search. Switch the agent to a mode that supports it to configure search modes and location.",
                })}
                suggestedModes={[
                  t("settings.qa.modeRetriever", {
                    defaultValue: "Retriever",
                  }),
                  t("settings.qa.modeCoder", { defaultValue: "Coder" }),
                ]}
              />
            </View>
          ))}
        {_showBuilderSection("memory") && (
          <SectionRenderer
            style={{
              minWidth: 250,
            }}
            isExpandedOnLoad={builderActiveSection != null}
            title={t("settings.qa.agentMemory", {
              defaultValue: "Agent Memory",
            })}
            icon={AppAssets.icons.memory}
          >
            <View
              style={{
                paddingLeft: 10,
              }}
            >
              <AppForms.AppFormSwitch
                // disabled={isEdit}
                // disabled={values?.summarisation == false}
                wrapperStyle={[
                  {
                    maxHeight: 30,
                    paddingHorizontal: 10,

                    paddingVertical: 18,
                    margin: 5,
                    marginTop: 20,
                  },
                ]}
                showTextInputLabel={true}
                label={t("settings.qa.longTermMemory")}
                displayIsTicked={true}
                name="isLongTermMemoryEnabled"
                value={values?.isLongTermMemoryEnabled}
                description={t("settings.qa.longTermMemoryDesc")}
                descriptionIcon={false}
                descriptionColor={currentTheme?.theme?.mediumGrey}
              />

              <View
                style={{
                  marginTop: 35,
                  paddingLeft: 7,
                }}
              >
                <AppInputLabel
                  label={t("settings.qa.memoryContent")}
                  wrapperStyle={{
                    marginBottom: 5,
                  }}
                />
                <AppForms.AppFormField
                  showTextInputLabel={false}
                  textContainer={{
                    minHeight: 450,
                  }}
                  multiline={true}
                  placeholder={t("settings.qa.noMemoryContentPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="agentLongTermMemory"
                  maxCharacters={64000}
                />
              </View>
            </View>
          </SectionRenderer>
        )}
        <View
          style={{
            zIndex: values?.mode == "retriever" ? -40 : -1,
          }}
        >
          {isChatBot &&
            isCharacterNeeded &&
            values?.mode != "planner" &&
            _showBuilderSection("instructions") && (
              <View
                style={{
                  zIndex: values?.mode == "retriever" ? -40 : -1,
                }}
              >
                <SectionRenderer
                  expanderStyle={{
                    zIndex: values?.mode == "retriever" ? -30 : -1,
                  }}
                  style={{
                    minWidth: 250,
                    zIndex: values?.mode == "retriever" ? -30 : -1,
                  }}
                  isExpandedOnLoad={builderActiveSection != null}
                  isFullWidth
                  title={t("settings.qa.agentInstructions")}
                  icon={AppAssets.icons.persona}
                >
                  <View
                    style={{
                      paddingLeft: 10,
                    }}
                  >
                    <View>
                      <AppForms.AppFormField
                        showTextInputLabel
                        isMandatory
                        textContainer={{
                          minHeight: 480,
                        }}
                        maxCharacters={64000}
                        multiline={true}
                        label={t("settings.qa.agentRoleAndInstructions")}
                        placeholder={t(
                          "settings.qa.interpolationRolePlaceholder",
                        )}
                        keyboardtype="default"
                        textContextType="label"
                        autoCorrect={false}
                        name="interpolationString"
                        showMarkdownToggle
                      />
                      {showVoicePipelineConfigs && (
                        <AppForms.AppFormField
                          showTextInputLabel
                          textContainer={{
                            minHeight: 480,
                          }}
                          formFieldContainerStyle={{
                            zIndex: 11,
                          }}
                          multiline={true}
                          label={t("settings.qa.voiceInstructions")}
                          placeholder={t(
                            "settings.qa.voiceInstructionsPlaceholder",
                          )}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="voiceInstructions"
                          maxCharacters={4096}
                          mentioning
                          mentions={getSpeechTagsMentions(
                            values?.ttsModel || "orpheus-fast",
                          )}
                          onMentionSelect={(mention) =>
                            console.log("Selected speech tag:", mention)
                          }
                        />
                      )}
                      {/* Voice Instructions */}

                      {values?.mode != "planner" && !isAgentOllama && (
                        <>
                          <AppForms.AppFormField
                            showTextInputLabel
                            formFieldContainerStyle={{
                              zIndex: 10,
                            }}
                            textContainer={{
                              minHeight: 480,
                            }}
                            multiline={true}
                            label={t("settings.qa.agentToolingGuidelines")}
                            placeholder={t("settings.qa.guidelinesPlaceholder")}
                            keyboardtype="default"
                            textContextType="label"
                            autoCorrect={false}
                            name="customToolingInstructions"
                            maxMentionsMenuHeight={390}
                            maxCharacters={16000}
                            mentioning
                            mentions={[
                              ...getFilteredTools(_tools, values, t).map(
                                (tool) => ({
                                  ...tool,
                                  id: t(tool.name),
                                  name: t(tool.name),
                                  label: t(tool.name),
                                }),
                              ),
                              ...(values?.agentFunctions || []).map((fn) => ({
                                id: fn?.name,
                                name: fn?.name,
                                label: fn?.name,
                                description: fn?.description || fn?.name,
                              })),
                            ]}
                            onMentionSelect={(mention) => console.log(mention)}
                          />
                          <AppForms.AppFormField
                            showTextInputLabel
                            textContainer={{
                              minHeight: 480,
                            }}
                            multiline={true}
                            label={t("settings.qa.agenticSearchInstructions")}
                            placeholder={t(
                              "settings.qa.agenticSearchPlaceholder",
                            )}
                            keyboardtype="default"
                            textContextType="label"
                            autoCorrect={false}
                            name="ragInstructions"
                            maxCharacters={16000}
                          />
                        </>
                      )}
                      <AppForms.AppFormField
                        showTextInputLabel
                        multiline={true}
                        textContainer={{
                          minHeight: 480,
                        }}
                        label={t("settings.qa.goals")}
                        placeholder={t("settings.qa.goalsPassagePlaceholder")}
                        keyboardtype="default"
                        textContextType="label"
                        autoCorrect={false}
                        name="goals"
                        maxCharacters={16000}
                      />
                      {(values?.mode == "retriever" && values?.agenticRAG) ||
                      values?.mode == "planner" ? (
                        <>
                          <AppForms.AppFormField
                            showTextInputLabel
                            multiline={true}
                            textContainer={{
                              minHeight: 480,
                            }}
                            label={t("settings.qa.planningInstructions")}
                            placeholder={t(
                              "settings.qa.planningInstructionsPlaceholder",
                            )}
                            keyboardtype="default"
                            textContextType="label"
                            autoCorrect={false}
                            name="planningInstructions"
                            maxCharacters={16000}
                          />
                          <AppForms.AppFormField
                            showTextInputLabel
                            multiline={true}
                            textContainer={{
                              minHeight: 480,
                            }}
                            label={t("settings.qa.reviewInstructions")}
                            placeholder={t(
                              "settings.qa.reviewInstructionsPlaceholder",
                            )}
                            keyboardtype="default"
                            textContextType="label"
                            autoCorrect={false}
                            name="reviewInstructions"
                            maxCharacters={16000}
                          />
                        </>
                      ) : null}
                    </View>
                    <View
                      style={{
                        zIndex: -1,
                      }}
                    >
                      <View>
                        <AppForms.AppFormField
                          showTextInputLabel
                          multiline={true}
                          textContainer={{
                            minHeight: 120,
                          }}
                          label={t("settings.qa.inhibitionPassage")}
                          placeholder={t(
                            "settings.qa.inhibitionPassagePlaceholder",
                          )}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="inhibitionPassage"
                          maxCharacters={512}
                        />
                      </View>
                      <View>
                        <AppForms.AppFormField
                          showTextInputLabel
                          multiline={true}
                          textContainer={{
                            minHeight: 120,
                          }}
                          label={t("settings.qa.pertinancePassage")}
                          placeholder={t(
                            "settings.qa.pertinancePassagePlaceholder",
                          )}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="pertinencePassage"
                          maxCharacters={512}
                        />
                      </View>
                      <AppForms.AppFormField
                        showTextInputLabel
                        textContainer={{
                          minHeight: 120,
                        }}
                        multiline={true}
                        label={t("settings.qa.defaultAnswer")}
                        placeholder={t("settings.qa.defaultAnswerPlaceholder")}
                        keyboardtype="default"
                        textContextType="label"
                        autoCorrect={false}
                        name="defaultAnswer"
                        maxCharacters={512}
                      />
                      {values?.mode == "retriever" && (
                        <AppForms.AppFormField
                          showTextInputLabel
                          textContainer={{
                            minHeight: 120,
                          }}
                          multiline={true}
                          label={t("settings.qa.noDataSourcesAnswer")}
                          placeholder={t("settings.qa.noKnowledgePlaceholder")}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="noKnowledgeDefaultAnswer"
                          maxCharacters={512}
                        />
                      )}
                    </View>
                  </View>
                </SectionRenderer>
              </View>
            )}
          {isChatBot &&
            (values?.mode == "computer" ||
              (values?.agenticRAG == true && values?.mode == "retriever")) &&
            _showBuilderSection("desktop") && (
              <SectionRenderer
                expanderStyle={{
                  zIndex: -1,
                }}
                style={{ minWidth: 250, zIndex: -1 }}
                isExpandedOnLoad={builderActiveSection != null}
                title={t("settings.qa.desktopSetupAndCredentials")}
                icon={AppAssets.icons.credentials}
              >
                <View
                  style={{
                    paddingLeft: 0,
                  }}
                >
                  <AppForms.AppFormSwitch
                    // disabled={isEdit}
                    // disabled={true}
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 6,

                        paddingVertical: 14,
                        marginTop: -7,
                        marginHorizontal: 1,
                        marginBottom: 45,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.browserOnly")}
                    displayIsTicked={true}
                    name="agentDesktopBrowser"
                    value={values?.agentDesktopBrowser}
                    description={t("settings.qa.browserOnlyDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  {values?.agentDesktopBrowser && (
                    <AppForms.AppFormTrackSlider
                      minimumValue={1}
                      maximumValue={300}
                      step={1}
                      label={t("settings.qa.maxBrowserSteps")}
                      name="maxBrowserSteps"
                      containerStyle={{
                        marginBottom: 10,
                      }}
                    />
                  )}
                  {values?.agentDesktopBrowser && (
                    <AppSwitch
                      wrapperStyle={[
                        {
                          maxHeight: 30,
                          paddingHorizontal: 6,

                          paddingVertical: 14,
                          marginTop: -7,
                          marginHorizontal: 1,
                          marginBottom: 25,
                        },
                      ]}
                      showTextInputLabel={true}
                      label={t("settings.qa.allowIpRotationViaProxy")}
                      displayIsTicked={true}
                      isTicked={values?.allowIpRotationViaProxy === true}
                      onChange={(v) =>
                        setFieldValue("allowIpRotationViaProxy", v)
                      }
                      description={t("settings.qa.allowIpRotationViaProxyDesc")}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                      name="allowIpRotationViaProxy"
                    />
                  )}
                  <View
                    style={{
                      height: 1,
                      backgroundColor: currentTheme?.theme?.lightGrey,
                      marginVertical: 18,
                      width: "100%",
                    }}
                  />
                  <AppForms.AppFormMultiSelect
                    key={`creden-${lastOpened}`}
                    name="agentDesktopCredentials"
                    chipHref={chipHrefAuthorisations}
                    topErrorOffset={-21}
                    touchedByDefault
                    placeholder={t("settings.qa.credentialsPlaceholder")}
                    hidePlaceholder={true}
                    items={_authorisations}
                    uniqueKey="id"
                    showDropDowns={true}
                    readOnlyHeadings={true}
                    labelOption="label"
                    groupByProperty="type"
                    style={{ maxWidth: 800 }}
                    wrapperContainerStyle={{
                      flex: !isLargeScreen() ? 1 : 0.5,
                      zIndex: 20,
                    }}
                    styles={colourStyles}
                    options={_authorisations.map((u) => {
                      return {
                        ...u,
                        value: u?.id,
                      };
                    })}
                  />
                  <AppText
                    style={{
                      paddingLeft: 5,

                      fontSize: currentTheme?.theme?.extra_small_font_size,
                    }}
                    wrapperStyle={{
                      top: -10,
                      justifyContent: "center",
                      marginLeft: 10,
                    }}
                    iconSize={12}
                    leftIcon={AppAssets?.icons?.question_icon}
                  >
                    {t("settings.qa.credentialsInfo")}
                  </AppText>
                  <View
                    style={{
                      height: 1,
                      backgroundColor: currentTheme?.theme?.lightGrey,
                      marginVertical: 18,
                      width: "100%",
                    }}
                  />
                  <View>
                    <AppForms.AppFormDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                        maxWidth: !isLargeScreen() && 250,
                      }}
                      showTextInputLabel
                      label={t("settings.qa.browserModel")}
                      name="browserModel"
                      selectedValue={values?.browserModel ?? "default"}
                      items={[
                        { id: "default", name: t("settings.qa.default") },
                        ...(baseLlmModels?.filter(
                          (m) => m?.supportsBrowser === true,
                        ) || []),
                      ]}
                      propertyLabel="name"
                      propertyValue="id"
                      _onValueChange={(v) => {
                        if (v === "default")
                          setFieldValue("browserModel", null);
                      }}
                    />
                    {values?.browserModel && (
                      <ModelPricingInfo
                        modelId={values?.browserModel}
                        style={{ marginBottom: 12 }}
                        showReasonerBadge={true}
                      />
                    )}
                    <AppForms.AppFormDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                        maxWidth: !isLargeScreen() && 250,
                      }}
                      showTextInputLabel
                      label={t("settings.qa.availableVirtualDesktops")}
                      isMandatory
                      name="vms"
                      items={
                        state?.vms?.length > 0
                          ? [
                              { id: "none", name: t("settings.list.none") },
                              ...state?.vms,
                            ]
                          : [{ id: "none", name: t("settings.list.none") }]
                      }
                      propertyLabel="name"
                      propertyValue="id"
                      _onValueChange={(v) => {
                        if (v != "none") {
                          setFieldValue(
                            "agentDesktopVM",
                            state?.vms?.find((u) => u?.id == v)?.ip,
                          );
                          setFieldValue(
                            "agentDesktopPortRange",
                            state?.vms?.find((u) => u?.id == v)?.port,
                          );
                        } else {
                          setFieldValue("agentDesktopVM", "");
                          setFieldValue("agentDesktopPortRange", "");
                        }
                      }}
                    />
                    <AppForms.AppFormField
                      showTextInputLabel
                      isMandatory
                      multiline={false}
                      label={t("settings.qa.virtualDesktopIpAddress")}
                      placeholder={t("settings.qa.virtualDesktopIpPlaceholder")}
                      keyboardtype="default"
                      textContextType="label"
                      autoCorrect={false}
                      name="agentDesktopVM"
                      editable={false}
                      description={t("settings.qa.virtualDesktopIpInfo")}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                    />
                  </View>
                  <View>
                    <AppForms.AppFormField
                      showTextInputLabel
                      isMandatory
                      multiline={false}
                      label={t("settings.qa.virtualDesktopPort")}
                      placeholder={t(
                        "settings.qa.virtualDesktopPortPlaceholder",
                      )}
                      keyboardtype="default"
                      textContextType="label"
                      autoCorrect={false}
                      name="agentDesktopPortRange"
                      editable={false}
                      description={t("settings.qa.virtualDesktopPortInfo")}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                    />
                  </View>
                </View>
              </SectionRenderer>
            )}
          {isChatBot &&
            isCharacterNeeded &&
            !isAgentOllama &&
            !["planner", "voice"].includes(values?.mode) &&
            _showBuilderSection("feedback") && (
              <SectionRenderer
                expanderStyle={{
                  zIndex: -1,
                }}
                style={{ minWidth: 250, zIndex: -1 }}
                isExpandedOnLoad={builderActiveSection != null}
                isFullWidth={isBuilderLayout}
                title={t("settings.qa.agentFeedback")}
                icon={AppAssets.icons.teach}
              >
                <View
                  style={{
                    paddingLeft: 10,
                    gap: 5,
                    width: "100%",
                  }}
                >
                  {agentFeedbackList?.length > 0 ? (
                    <AppTable
                      showPagination
                      hideSearch={false}
                      searchPlaceholder={t(
                        "common.filterByAnyField",
                        "Filter by any field...",
                      )}
                      actionButtons
                      showDropDownMenuForActions
                      mainActionLabel={t("settings.qa.goToChat")}
                      onClickMainAction={(item) => {
                        if (item?.chatID) {
                          history.push(
                            `/workspaces/${getActiveWorkspace()?.id}/chats/${
                              item.chatID
                            }`,
                          );
                        }
                      }}
                      onActionItemPress={(id, item) => {
                        if (id === "GO_TO_CHAT" && item?.chatID) {
                          history.push(
                            `/workspaces/${getActiveWorkspace()?.id}/chats/${
                              item.chatID
                            }`,
                          );
                        }
                      }}
                      actions={[
                        {
                          label: t("settings.qa.goToChat"),
                          id: "GO_TO_CHAT",
                          icon: AppAssets.icons.enter,
                        },
                      ]}
                      style={{
                        zIndex: -1,
                        maxHeight: height - 120,
                        minHeight: 300,
                        paddingRight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                      headers={feedbackColumns.map((col) => ({
                        ...col,
                        title: t(col.title),
                      }))}
                      items={agentFeedbackList?.map((u) => {
                        const cleanMessage = (u?.text || "")
                          .trim()
                          .replace(/\n+/g, " ")
                          .replace(/\s+/g, " ");
                        return {
                          message:
                            cleanMessage?.length > 90
                              ? `${cleanMessage.slice(0, 90)}...`
                              : cleanMessage,
                          feedback: u?.humanFeedbackComment || "",
                          datetime: u?.creationTime
                            ? dayjs(u.creationTime * 1000)?.format(
                                "DD-MM-YYYY HH:mm",
                              )
                            : " ",
                          chatID: u?.chatID,
                        };
                      })}
                      itemsPerPage={50}
                    />
                  ) : (
                    <View
                      style={{
                        paddingLeft: 0,
                      }}
                    >
                      <AppPlaceholderView
                        wrapperStyle={{
                          width: "80%",
                          height: "100%",
                          marginVertical: "auto",
                          marginHorizontal: "auto",
                          alignItems: "center",
                        }}
                        size={30}
                        icon={AppAssets.icons.orbit}
                        mainCaption={t("settings.list.nothingToShow")}
                        subCaption={t("settings.qa.feedbackEmptyCaption")}
                      />
                    </View>
                  )}
                </View>
              </SectionRenderer>
            )}

          <View
            style={{
              zIndex: 10,
            }}
          >
            {isChatBot &&
              ["planner", "retriever"].includes(values?.mode) &&
              _showBuilderSection("planning") && (
                <SectionRenderer
                  expanderStyle={{
                    zIndex: 10000,
                  }}
                  style={{ minWidth: 250, zIndex: 20 }}
                  title={t("settings.qa.planningTools")}
                  icon={AppAssets.icons.agentsPlan}
                  isExpandedOnLoad={values?.mode == "planner"}
                >
                  <View
                    style={{
                      paddingLeft: 10,
                    }}
                  >
                    {(values?.mode == "planner" ||
                      (values?.mode == "retriever" &&
                        values?.useInterleavedReasoning)) && (
                      <>
                        <AppForms.AppFormMultiSelect
                          showTextInputLabel
                          label={t("settings.qa.availableAgentsForPlanning")}
                          key={`agentpools-${lastOpened}`}
                          name="agentsPool"
                          chipHref={chipHrefRoles}
                          maxSelectedValues={10}
                          topErrorOffset={-21}
                          touchedByDefault
                          placeholder={t(
                            "settings.qa.availableAgentsForPlanningPlaceholder",
                          )}
                          hidePlaceholder={true}
                          items={_agents?.filter(
                            (a) =>
                              a?.id !== values?.id &&
                              a?.restrictedAccess !== true &&
                              a?.mode !== "voice" &&
                              a?.mode !== "planner",
                          )}
                          uniqueKey="id"
                          showDropDowns={true}
                          readOnlyHeadings={true}
                          labelOption="label"
                          groupByProperty="type"
                          style={{ maxWidth: 800 }}
                          wrapperContainerStyle={{
                            flex: 1,
                            zIndex: 20,
                          }}
                          styles={colourStyles}
                          options={_agents
                            ?.filter(
                              (a) =>
                                a?.id !== values?.id &&
                                a?.restrictedAccess !== true &&
                                a?.mode !== "voice" &&
                                a?.mode !== "planner",
                            )
                            .map((u) => {
                              return {
                                ...u,
                                value: u?.id,
                              };
                            })}
                          onError={(error) => {
                            AppToasty.show(t("toasts.agents.maxReached"), {
                              type: "warning",
                            });
                          }}
                        />
                        <AppText
                          style={{
                            paddingLeft: 5,
                            fontSize:
                              currentTheme?.theme?.extra_small_font_size,
                          }}
                          wrapperStyle={{
                            marginTop: 5,
                            marginBottom: 15,
                            marginLeft: 15,
                            justifyContent: "flex-start",
                          }}
                          iconSize={12}
                          leftIcon={AppAssets?.icons?.question_icon}
                        >
                          {values?.agentsPool?.length >= 20
                            ? t("settings.qa.maxAgentsReached")
                            : t("settings.qa.selectUpToAgents")}
                        </AppText>
                      </>
                    )}
                    {(values?.mode == "retriever" && values?.agenticRAG) ||
                    values?.mode == "planner" ? (
                      <>
                        <AppForms.AppFormTrackSlider
                          minimumValue={1}
                          maximumValue={50000}
                          step={50}
                          label={t("settings.qa.maxTotalToolCalls")}
                          name="maxTotalToolCalls"
                        />
                        <AppForms.AppFormTrackSlider
                          minimumValue={1}
                          maximumValue={300}
                          step={1}
                          label={t("settings.qa.maxConsecutiveSameTool")}
                          name="maxConsecutiveSameTool"
                        />
                        <AppForms.AppFormTrackSlider
                          minimumValue={1}
                          maximumValue={300}
                          step={1}
                          label={t("settings.qa.maxConsecutiveFailures")}
                          name="maxConsecutiveFailures"
                        />
                        {values?.useInterleavedReasoning != true && (
                          <>
                            <AppForms.AppFormTrackSlider
                              minimumValue={1}
                              maximumValue={10}
                              step={1}
                              label={t("settings.qa.maxExecutionSteps")}
                              name="maxPlanningSteps"
                            />
                            {values?.agenticRAG && (
                              <>
                                <AppForms.AppFormTrackSlider
                                  minimumValue={1}
                                  maximumValue={5}
                                  step={1}
                                  label={t("settings.qa.maxReAttempts")}
                                  name="plannerExecutionAttempts"
                                />
                                <AppForms.AppFormSwitch
                                  // disabled={isEdit}
                                  // disabled={true}
                                  wrapperStyle={[
                                    {
                                      maxHeight: 30,
                                      paddingHorizontal: 10,

                                      paddingVertical: 18,
                                      margin: 5,
                                      marginBottom: 10,
                                    },
                                  ]}
                                  showTextInputLabel={true}
                                  label={t("settings.qa.allowReplanning")}
                                  displayIsTicked={true}
                                  name="allowReplanning"
                                  value={values?.allowReplanning}
                                  description={t(
                                    "settings.qa.allowReplanningDesc",
                                  )}
                                  descriptionIcon={false}
                                  descriptionColor={
                                    currentTheme?.theme?.mediumGrey
                                  }
                                />
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <AppPlaceholderView
                        wrapperStyle={{
                          width: "80%",
                          height: "100%",
                          marginVertical: "auto",
                          marginHorizontal: "auto",
                          alignItems: "center",
                        }}
                        size={30}
                        icon={AppAssets.icons.orbit}
                        mainCaption={t("settings.list.nothingToShow")}
                        subCaption={t("settings.qa.planningEmptyCaption")}
                      />
                    )}

                    {values?.mode == "planner" && (
                      <>
                        <AppForms.AppFormTrackSlider
                          minimumValue={1}
                          maximumValue={5}
                          step={1}
                          label={t("settings.qa.maxReAttempts")}
                          name="plannerExecutionAttempts"
                        />
                        <AppForms.AppFormSwitch
                          // disabled={isEdit}
                          // disabled={true}
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              // paddingHorizontal: 10,
                              width: "auto",
                              paddingVertical: 18,
                              margin: 7,
                              marginBottom: 10,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t(
                            "settings.qa.requiresApprovalForPlanExecution",
                          )}
                          displayIsTicked={true}
                          name="plannerRequiresApprovalForPlanExecution"
                          value={
                            values?.plannerRequiresApprovalForPlanExecution
                          }
                          description={t(
                            "settings.qa.requiresApprovalForPlanExecutionDesc",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                        <AppForms.AppFormSwitch
                          // disabled={isEdit}
                          // disabled={true}
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              // paddingHorizontal: 10,
                              width: "auto",
                              paddingVertical: 18,
                              margin: 7,
                              marginBottom: 10,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.allowReplanning")}
                          displayIsTicked={true}
                          name="allowReplanning"
                          value={values?.allowReplanning}
                          description={t("settings.qa.allowReplanningDesc")}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />

                        <>
                          <AppForms.AppFormSwitch
                            // disabled={isEdit}
                            // disabled={true}
                            wrapperStyle={[
                              {
                                maxHeight: 30,
                                // paddingHorizontal: 10,
                                width: "auto",
                                paddingVertical: 18,
                                margin: 7,
                                marginBottom: 10,
                              },
                            ]}
                            showTextInputLabel={true}
                            label={t("settings.qa.dynamicAgentsGeneration")}
                            displayIsTicked={true}
                            name="plannerAutoAgentSpawn"
                            value={values?.plannerAutoAgentSpawn}
                            description={t(
                              "settings.qa.dynamicAgentsGenerationDesc",
                            )}
                            descriptionIcon={false}
                            descriptionColor={currentTheme?.theme?.mediumGrey}
                          />
                          {values?.plannerAutoAgentSpawn && (
                            <AppForms.AppFormMultiSelect
                              key={`plan_topics-${lastOpened}`}
                              name="allowedTopics"
                              topErrorOffset={-21}
                              label={t(
                                "settings.qa.availableTopicsPlaceholder",
                              )}
                              touchedByDefault
                              placeholder={t(
                                "settings.qa.availableTopicsPlaceholder",
                              )}
                              hidePlaceholder={true}
                              items={_topics}
                              uniqueKey="id"
                              showDropDowns={true}
                              readOnlyHeadings={true}
                              labelOption="label"
                              groupByProperty="type"
                              style={{ maxWidth: 800 }}
                              wrapperContainerStyle={{
                                flex: !isLargeScreen() ? 1 : 0.5,
                                zIndex: 20,
                                marginTop: 25,
                              }}
                              styles={colourStyles}
                              options={_topics.map((u) => {
                                return {
                                  ...u,
                                  value: u?.id,
                                };
                              })}
                            />
                          )}

                          {/* <AppForms.AppFormSwitch
                      // disabled={isEdit}
                      // disabled={true}
                      wrapperStyle={[
                        {
                          maxHeight: 30,
                          paddingHorizontal: 10,

                          paddingVertical: 18,
                          margin: 5,
                          marginBottom: 10,
                        },
                      ]}
                      showTextInputLabel={true}
                      label={"Retain spawned agents"}
                      displayIsTicked={true}
                      name="plannerRetainsSpawnedAgents"
                      value={values?.plannerRetainsSpawnedAgents}
                    /> */}
                        </>
                      </>
                    )}
                  </View>
                </SectionRenderer>
              )}
          </View>
          {values?.mode == "planner" && _showBuilderSection("notifications") && (
            <SectionRenderer
              style={{ minWidth: 250, zIndex: -1 }}
              isExpandedOnLoad={builderActiveSection != null}
              title={t("settings.qa.notifications")}
              icon={AppAssets.icons.notification}
            >
              <View
                style={{
                  paddingLeft: 10,
                }}
              >
                <AppForms.AppFormSwitch
                  // disabled={isEdit}
                  // disabled={true}
                  wrapperStyle={[
                    {
                      maxHeight: 30,
                      paddingHorizontal: 10,

                      paddingVertical: 18,
                      margin: 5,
                      marginBottom: 10,
                    },
                  ]}
                  showTextInputLabel={true}
                  label={t("settings.qa.emailNotifyPlanApproval")}
                  displayIsTicked={true}
                  name="plannerEmailOnApprovalRequest"
                  value={values?.plannerEmailOnApprovalRequest}
                  description={t("settings.qa.emailNotifyPlanApprovalDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                <AppForms.AppFormSwitch
                  // disabled={isEdit}
                  // disabled={true}
                  wrapperStyle={[
                    {
                      maxHeight: 30,
                      paddingHorizontal: 10,

                      paddingVertical: 18,
                      margin: 5,
                      marginBottom: 10,
                    },
                  ]}
                  showTextInputLabel={true}
                  label={t("settings.qa.emailNotifyPlanFailure")}
                  displayIsTicked={true}
                  name="plannerEmailOnFailedPlan"
                  value={values?.plannerEmailOnFailedPlan}
                  description={t("settings.qa.emailNotifyPlanFailureDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                <AppForms.AppFormSwitch
                  // disabled={isEdit}
                  // disabled={true}
                  wrapperStyle={[
                    {
                      maxHeight: 30,
                      paddingHorizontal: 10,

                      paddingVertical: 18,
                      margin: 5,
                      marginBottom: 10,
                    },
                  ]}
                  showTextInputLabel={true}
                  label={t("settings.qa.emailNotifyPlanCompletion")}
                  displayIsTicked={true}
                  name="plannerEmailOnCompletedPlan"
                  value={values?.plannerEmailOnCompletedPlan}
                  description={t("settings.qa.emailNotifyPlanCompletionDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />

                <AppForms.AppFormMultiSelect
                  key={`approver-${lastOpened}`}
                  name="plannerApproverEmails"
                  topErrorOffset={-21}
                  touchedByDefault
                  placeholder={t(
                    "settings.qa.notificationRecipientsPlaceholder",
                  )}
                  hidePlaceholder={true}
                  items={getWorkspaceUsers()}
                  uniqueKey="id"
                  showDropDowns={true}
                  readOnlyHeadings={true}
                  labelOption="label"
                  groupByProperty="type"
                  style={{ maxWidth: 800 }}
                  wrapperContainerStyle={{
                    flex: !isLargeScreen() ? 1 : 0.5,
                    zIndex: 20,
                    marginTop: 25,
                    marginLeft: 15,
                  }}
                  styles={colourStyles}
                  options={getWorkspaceUsers()?.map((u) => {
                    return {
                      ...u,
                      value: u?.id,
                      label: u?.username || u?.user_email,
                      category: "user",
                      backgroundColor: AppUtilities.stringToHslColor(
                        u?.username || u?.user_email,
                        71,
                      ),
                    };
                  })}
                />
              </View>
            </SectionRenderer>
          )}
          <View
            style={{
              zIndex: -1,
            }}
          >
            {isChatBot &&
              !["coder", "computer"].includes(values?.mode) &&
              _showBuilderSection("capabilities") &&
              !isAgentOllama &&
              (values?.mode != "voice" ? (
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: -100 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  title={t("settings.qa.agentCapabilities")}
                  icon={AppAssets.icons.capabililities}
                >
                  {values?.mode != "planner" && values?.mode != "voice" && (
                    <View
                      style={{
                        paddingLeft: 10,
                      }}
                    >
                      <View>
                        <AppForms.AppFormSwitch
                          // disabled={isEdit}
                          // disabled={true}
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingHorizontal: 10,

                              paddingVertical: 18,
                              margin: 5,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.summarisedMemoryInChats")}
                          displayIsTicked={true}
                          name="summarisation"
                          value={values?.summarisation}
                          description={t(
                            "settings.qa.summarisedMemoryInChatsDesc",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                      </View>
                    </View>
                  )}

                  {isChatBot && values?.mode != "voice" && (
                    <>
                      <View
                        style={{
                          paddingLeft: 10,
                        }}
                      >
                        <AppForms.AppFormSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingHorizontal: 10,

                              paddingVertical: 18,
                              margin: 5,
                              marginTop: 20,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.chartsGeneration")}
                          displayIsTicked={true}
                          name="charting"
                          value={values?.charting}
                          disabled={
                            disableMultiModal || disableDueToTranslation
                          }
                          description={
                            disableMultiModal || disableDueToTranslation
                              ? t("settings.qa.chartsGenerationDisabledInfo")
                              : t("settings.qa.chartsGenerationDesc")
                          }
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />

                        {["chatter", "planner", "retriever"]?.includes(
                          values?.mode,
                        ) && (
                          <AppForms.AppFormSwitch
                            wrapperStyle={[
                              {
                                maxHeight: 30,
                                paddingHorizontal: 10,

                                paddingVertical: 18,
                                margin: 5,
                                marginTop: 20,
                              },
                            ]}
                            showTextInputLabel={true}
                            label={t("settings.qa.enableDocsUpload")}
                            displayIsTicked={true}
                            name="allowDocsUpload"
                            value={values?.allowDocsUpload}
                            description={t("settings.qa.enableDocsUploadDesc")}
                            descriptionIcon={false}
                            descriptionColor={currentTheme?.theme?.mediumGrey}
                          />
                        )}

                        <AppForms.AppFormSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingHorizontal: 10,

                              paddingVertical: 18,
                              margin: 5,
                              marginTop: 20,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.enableImagesUpload")}
                          displayIsTicked={true}
                          name="allowImagesUpload"
                          value={values?.allowImagesUpload}
                          description={t("settings.qa.enableImagesUploadDesc")}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                        <AppForms.AppFormSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingHorizontal: 10,

                              paddingVertical: 18,
                              margin: 5,
                              marginTop: 20,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.enableAudioUpload")}
                          displayIsTicked={true}
                          name="allowAudiosUpload"
                          value={values?.allowAudiosUpload}
                          description={t("settings.qa.enableAudioUploadDesc")}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                        <AppForms.AppFormSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingHorizontal: 10,

                              paddingVertical: 18,
                              margin: 5,
                              marginTop: 20,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.enableVideoUpload")}
                          displayIsTicked={true}
                          name="allowVideosUpload"
                          value={values?.allowVideosUpload}
                          description={t("settings.qa.enableVideoUploadDesc")}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                        <AppForms.AppFormSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingHorizontal: 10,

                              paddingVertical: 18,
                              margin: 5,
                              marginTop: 20,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.enableAgentIntrospection")}
                          displayIsTicked={true}
                          name="allowIntrospection"
                          value={values?.allowIntrospection}
                          description={t(
                            "settings.qa.enableAgentIntrospectionDesc",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                        <>
                          <AppForms.AppFormSwitch
                            wrapperStyle={[
                              {
                                maxHeight: 30,
                                paddingHorizontal: 10,

                                paddingVertical: 18,
                                margin: 5,
                                marginTop: 20,
                              },
                            ]}
                            showTextInputLabel={true}
                            label={t("settings.qa.enableAudioGeneration")}
                            displayIsTicked={true}
                            name="allowAudiosGeneration"
                            value={values?.allowAudiosGeneration}
                            disabled={false}
                            description={t(
                              "settings.qa.enableAudioGenerationDesc",
                            )}
                            descriptionIcon={false}
                            descriptionColor={currentTheme?.theme?.mediumGrey}
                          />
                          <View
                            style={{
                              marginLeft: 10,
                              marginTop: 10,
                            }}
                          >
                            <View
                              style={{
                                marginTop: 20,
                                marginLeft: -5,
                              }}
                            >
                              <ClonedVoiceControl
                                isActive={isClonedVoiceId(values?.voiceName)}
                                isCloneInProgress={voiceCloneInProgress}
                                audioPath={values?.voiceFileKey}
                                onFileSelection={handleAudioSelection}
                                onRecordPress={() =>
                                  setShowVoiceCloningModal(true)
                                }
                                onRemove={handleRemoveClonedVoice}
                                fileUploadInProgress={fileUploadInProgress}
                                fileUploadProgress={fileUploadProgress}
                                isRecording={isAudioRecording}
                              />
                            </View>
                          </View>
                          {values?.allowAudiosGeneration && (
                            <View
                              style={{
                                marginLeft: 10,
                              }}
                            >
                              <AppText
                                wrapperStyle={{
                                  paddingHorizontal: 10,
                                  marginTop: 15,
                                  marginBottom: 5,
                                }}
                                style={{
                                  fontWeight: "bold",
                                  fontSize:
                                    currentTheme?.theme?.small_font_size,
                                }}
                                color={currentTheme?.theme?.font_color}
                              >
                                {t("settings.qa.audioGenerationProviderVoice")}
                              </AppText>
                              <AppText
                                wrapperStyle={{
                                  paddingHorizontal: 10,
                                  marginBottom: 5,
                                }}
                                style={{
                                  fontSize:
                                    currentTheme?.theme?.extra_small_font_size,
                                }}
                                color={currentTheme?.theme?.mediumGrey}
                              >
                                {t(
                                  "settings.qa.audioGenerationProviderVoiceDesc",
                                )}
                              </AppText>
                              {isClonedVoiceId(values?.voiceName) && (
                                <View
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 6,
                                    marginHorizontal: 10,
                                    marginBottom: 10,
                                    paddingHorizontal: 10,
                                    paddingVertical: 8,
                                    borderRadius: 8,
                                    backgroundColor:
                                      currentTheme?.theme
                                        ?.buttonBackgroundColor ||
                                      currentTheme?.theme?.lightGrey,
                                    borderLeftWidth: 3,
                                    borderLeftColor:
                                      currentTheme?.theme?.primary,
                                    opacity: values?.voiceFileKey ? 1 : 0.6,
                                  }}
                                >
                                  <AppIcon
                                    icon={AppAssets?.icons?.microphone}
                                    size={12}
                                    color={currentTheme?.theme?.primary}
                                  />
                                  <AppText
                                    style={{
                                      flex: 1,
                                      fontSize:
                                        currentTheme?.theme
                                          ?.extra_small_font_size,
                                    }}
                                    color={currentTheme?.theme?.font_color}
                                  >
                                    {t("settings.qa.clonedVoiceActiveCaption")}
                                  </AppText>
                                </View>
                              )}
                              {/* Voice Provider - First since voices depend on it */}
                              <AppForms.AppFormDropDown
                                isElevated={false}
                                enabled={!isClonedVoiceId(values?.voiceName)}
                                dropDownStyle={{
                                  borderColor:
                                    currentTheme?.theme?.border_color,
                                  maxWidth: !isLargeScreen() && 250,
                                }}
                                showTextInputLabel
                                label={t(
                                  "settings.qa.audioGenerationVoiceProvider",
                                )}
                                name="audiosGenerationTtsModel"
                                items={AUDIO_GENERATION_TTS_MODELS}
                                propertyLabel="name"
                                propertyValue="id"
                                description={t(
                                  "settings.qa.audioGenerationVoiceProviderDesc",
                                )}
                              />

                              {/* Voice - Filtered by provider */}
                              {values?.audiosGenerationTtsModel && (
                                <View
                                  style={{
                                    marginTop: -5,
                                  }}
                                >
                                  <AppForms.AppFormDropDown
                                    isElevated={false}
                                    enabled={
                                      !isClonedVoiceId(values?.voiceName)
                                    }
                                    dropDownStyle={{
                                      borderColor:
                                        currentTheme?.theme?.border_color,
                                      maxWidth: !isLargeScreen() && 250,
                                    }}
                                    showTextInputLabel
                                    label={t(
                                      "settings.qa.audioGenerationVoice",
                                    )}
                                    name="audiosGenerationVoiceName"
                                    items={audioGenerationVoices}
                                    propertyLabel="name"
                                    propertyValue="id"
                                  />
                                  {audioGenerationVoices?.length > 0 && (
                                    <AppText
                                      preventTranslation
                                      wrapperStyle={{
                                        paddingHorizontal: 10,
                                        marginBottom: 10,
                                      }}
                                      color={currentTheme?.theme?.darkGrey}
                                      style={{
                                        marginLeft: 0,
                                        fontSize:
                                          currentTheme?.theme
                                            ?.extra_small_font_size,
                                      }}
                                    >
                                      {t("settings.qa.voicesAvailableCount", {
                                        count: audioGenerationVoices.length,
                                      })}
                                    </AppText>
                                  )}
                                </View>
                              )}

                              {/* Speech Mode - refined script vs exact displayed text */}
                              <AppForms.AppFormDropDown
                                isElevated={false}
                                dropDownStyle={{
                                  borderColor:
                                    currentTheme?.theme?.border_color,
                                  maxWidth: !isLargeScreen() && 250,
                                }}
                                showTextInputLabel
                                label={t(
                                  "settings.qa.audioGenerationSpeechMode",
                                )}
                                name="audiosGenerationSpeechMode"
                                items={[
                                  {
                                    name: t("settings.qa.speechModeAudible"),
                                    id: "audible",
                                  },
                                  {
                                    name: t("settings.qa.speechModeDirect"),
                                    id: "direct",
                                  },
                                ]}
                                propertyLabel="name"
                                propertyValue="id"
                                description={t(
                                  "settings.qa.audioGenerationSpeechModeDesc",
                                )}
                              />
                            </View>
                          )}
                        </>
                      </View>
                    </>
                  )}
                  {values?.mode == "planner" && (
                    <AppText
                      style={{
                        paddingLeft: 5,
                        fontWeight: "bold",
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                      }}
                      wrapperStyle={{
                        paddingVertical: 5,
                        top: 10,
                        left: 30,
                      }}
                      iconSize={12}
                      iconColor={currentTheme?.theme?.orange}
                      leftIcon={AppAssets?.icons?.warning}
                      color={currentTheme?.theme?.orange}
                    >
                      {t("settings.qa.plannerRequiresTooling")}
                    </AppText>
                  )}
                </SectionRenderer>
              ) : (
                <View style={{ width: "100%" }}>
                  <SectionUnavailable
                    icon={AppAssets.icons.capabililities}
                    title={t("settings.qa.capabilitiesUnavailableTitle", {
                      defaultValue:
                        "Agent capabilities aren't available for this agent",
                    })}
                    message={t("settings.qa.capabilitiesUnavailableMessage", {
                      defaultValue:
                        "Voice agents don't support capabilities like summarised memory, file uploads and content generation. Switch the agent to a mode that supports them to configure capabilities.",
                    })}
                    suggestedModes={[
                      t("settings.qa.modeRetriever", {
                        defaultValue: "Retriever",
                      }),
                      t("settings.qa.modeCoder", { defaultValue: "Coder" }),
                    ]}
                  />
                </View>
              ))}
            {isChatBot &&
              (values?.mode == "chatter" ||
                (values?.mode == "retriever" &&
                  values?.useInterleavedReasoning)) &&
              !isAgentOllama &&
              _showBuilderSection("images") && (
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: -1 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  title={t("settings.qa.imagesGeneration")}
                  icon={AppAssets.icons.images}
                >
                  <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 0,

                        paddingVertical: 18,
                        marginHorizontal: 5,
                        marginTop: 20,
                        marginBottom: 35,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.allowImagesGeneration")}
                    displayIsTicked={true}
                    name="allowImagesGeneration"
                    value={values?.allowImagesGeneration}
                    description={t("settings.qa.allowImagesGenerationDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  <View
                    style={{
                      marginLeft: 0,
                      paddingLeft: 0,
                    }}
                  >
                    <AppForms.AppFormDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                        maxWidth: !isLargeScreen() && 250,
                      }}
                      showTextInputLabel
                      label={t("settings.qa.imageGenerationModel")}
                      isMandatory
                      name="baseImageGenerationModel"
                      items={baseImageModels}
                      propertyLabel="name"
                      propertyValue="id"
                    />
                    <ModelPricingInfo
                      modelId={
                        values?.baseImageGenerationModel ||
                        baseImageModels?.[0]?.id
                      }
                      type="image"
                      style={{ marginBottom: 12 }}
                      showDynamicRouting={false}
                    />
                    <View
                      style={{
                        marginLeft: 0,
                        marginTop: 10,
                      }}
                    >
                      <AppForms.AppFormTrackSlider
                        step={1}
                        maximumValue={
                          values?.baseImageGenerationModel ==
                            "black-forest-labs/flux-pro" ||
                          values?.baseImageGenerationModel ==
                            "black-forest-labs/flux-1.1-pro" ||
                          values?.baseImageGenerationModel ==
                            "black-forest-labs/flux-1.1-pro-ultra"
                            ? 1
                            : 4
                        }
                        minimumValue={1}
                        label={t("settings.qa.maxImagesGenerated")}
                        name="maxImagesGenerated"
                      />
                      <AppText
                        style={{
                          paddingLeft: 5,
                          fontSize: currentTheme?.theme?.extra_small_font_size,
                        }}
                        wrapperStyle={{
                          marginTop: 5,
                          justifyContent: "flex-start",
                          marginLeft: 10,
                        }}
                        iconSize={12}
                        leftIcon={AppAssets?.icons?.question_icon}
                      >
                        {values?.baseImageGenerationModel ==
                          "black-forest-labs/flux-pro" ||
                        values?.baseImageGenerationModel ==
                          "black-forest-labs/flux-1.1-pro" ||
                        values?.baseImageGenerationModel ==
                          "black-forest-labs/flux-1.1-pro-ultra"
                          ? t("settings.qa.imageLimitOnePerGen")
                          : t("settings.qa.imageLimitSomeModels")}
                      </AppText>
                    </View>
                  </View>
                </SectionRenderer>
              )}
            {isChatBot &&
              (values?.mode == "chatter" ||
                (values?.mode == "retriever" &&
                  values?.useInterleavedReasoning)) &&
              !isAgentOllama &&
              _showBuilderSection("videos") && (
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: -1 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  title={t("settings.qa.videos3dGeneration")}
                  icon={AppAssets.icons.video}
                >
                  <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 0,
                        paddingVertical: 18,
                        marginHorizontal: 5,
                        marginVertical: 20,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.enableAudioGeneration")}
                    displayIsTicked={true}
                    name="allowAudiosGeneration"
                    value={values?.allowAudiosGeneration}
                    disabled={false}
                    description={t("settings.qa.enableAudioGenerationDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 0,

                        paddingVertical: 18,
                        marginHorizontal: 5,
                        marginBottom: 35,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.allow3dModelGeneration")}
                    displayIsTicked={true}
                    name="allow3dModelGeneration"
                    value={values?.allow3dModelGeneration}
                    description={t("settings.qa.allow3dModelGenerationDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  <View
                    style={{
                      marginLeft: 0,
                      paddingLeft: 0,
                    }}
                  >
                    <AppForms.AppFormDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                        maxWidth: !isLargeScreen() && 250,
                      }}
                      showTextInputLabel
                      label={t("settings.qa.videoGenerationModel")}
                      isMandatory
                      name="videoGenerationModel"
                      items={[
                        // TF Branded Models (Top)
                        {
                          name: "Sorcerer Motion",
                          id: "sorcerer_motion",
                          tier: "large",
                          description:
                            "Cinematic video generation with native audio",
                        },
                        {
                          name: "Mystica Motion",
                          id: "mystica_motion",
                          tier: "extraLarge",
                          description:
                            "Premium video generation with audio & reference support",
                        },
                        {
                          name: "Mystica Motion Pro",
                          id: "mystica_motion_pro",
                          tier: "extraLarge",
                          description: "Premium flagship video generation",
                        },
                        {
                          name: "Mystica Video SD",
                          id: "mystica_video_sd",
                          tier: "medium",
                          description: "Standard balanced video generation",
                        },
                        // Small tier
                        {
                          name: "Wan 2.2 I2V Fast",
                          id: "wan-video/wan-2.2-i2v-fast",
                          tier: "small",
                          description: "Fast image-to-video generation",
                        },
                        {
                          name: "Wan 2.2 T2V Fast",
                          id: "wan-video/wan-2.2-t2v-fast",
                          tier: "small",
                          description: "Fast text-to-video generation",
                        },
                        {
                          name: "Wan 2.5 T2V Fast",
                          id: "wan-video/wan-2.5-t2v-fast",
                          tier: "small",
                          description:
                            "Fast text-to-video with prompt expansion",
                        },
                        {
                          name: "Wan 2.5 I2V Fast",
                          id: "wan-video/wan-2.5-i2v-fast",
                          tier: "small",
                          description: "Fast image-to-video generation",
                        },
                        {
                          name: "Seedance 1 Lite",
                          id: "bytedance/seedance-1-lite",
                          tier: "small",
                          description: "Lightweight video generation",
                        },
                        {
                          name: "Seedance 2.0 Fast",
                          id: "bytedance/seedance-2.0-fast",
                          tier: "small",
                          description: "Fast version of Seedance 2.0",
                        },
                        {
                          name: "P-Video",
                          id: "prunaai/p-video",
                          tier: "small",
                          description: "Budget video generation with audio",
                        },
                        {
                          name: "Hailuo 2.3 Fast",
                          id: "minimax/hailuo-2.3-fast",
                          tier: "small",
                          description: "Fast MiniMax Hailuo generation",
                        },
                        // Medium tier
                        {
                          name: "MiniMax Video 01",
                          id: "minimax/video-01",
                          tier: "medium",
                          description: "Standard MiniMax video generation",
                        },
                        {
                          name: "Seedance 1 Pro",
                          id: "bytedance/seedance-1-pro",
                          tier: "medium",
                          description: "Professional ByteDance video",
                        },
                        {
                          name: "Seedance 1.5 Pro",
                          id: "bytedance/seedance-1.5-pro",
                          tier: "medium",
                          description:
                            "ByteDance Seedance 1.5 with native audio",
                        },
                        {
                          name: "Wan 2.5 T2V",
                          id: "wan-video/wan-2.5-t2v",
                          tier: "medium",
                          description: "Alibaba Wan 2.5 text-to-video",
                        },
                        {
                          name: "Wan 2.5 I2V",
                          id: "wan-video/wan-2.5-i2v",
                          tier: "medium",
                          description:
                            "Wan 2.5 image-to-video with background audio",
                        },
                        {
                          name: "Wan 2.7 T2V",
                          id: "wan-video/wan-2.7-t2v",
                          tier: "medium",
                          description: "Alibaba Wan 2.7 text-to-video, 1080p",
                        },
                        {
                          name: "PixVerse V5",
                          id: "pixverse/pixverse-v5",
                          tier: "medium",
                          description:
                            "5-8s videos, great for anime & complex actions",
                        },
                        {
                          name: "PixVerse V5.6",
                          id: "pixverse/pixverse-v5.6",
                          tier: "medium",
                          description: "PixVerse V5.6 with AI audio support",
                        },
                        {
                          name: "Vidu Q3 Turbo",
                          id: "vidu/q3-turbo",
                          tier: "medium",
                          description: "Fast Vidu Q3 with native audio",
                        },
                        {
                          name: "Happyhorse 1.1",
                          id: "alibaba/happyhorse-1.1",
                          tier: "medium",
                          description: "Alibaba Happyhorse multi-image video",
                        },
                        {
                          name: "Veo 3.1 Fast",
                          id: "google/veo-3.1-fast",
                          tier: "medium",
                          description: "Google Veo 3.1 Fast",
                        },
                        // Large tier
                        {
                          name: "MiniMax Hailuo 02",
                          id: "minimax/hailuo-02",
                          tier: "large",
                          description: "Advanced MiniMax video",
                        },
                        {
                          name: "MiniMax Hailuo 2.3",
                          id: "minimax/hailuo-2.3",
                          tier: "large",
                          description: "Latest MiniMax Hailuo with 1080p",
                        },
                        {
                          name: "Kling V2.1",
                          id: "kwaivgi/kling-v2.1",
                          tier: "large",
                          description: "Kuaishou's Kling video model",
                        },
                        {
                          name: "Kling V2.5 Turbo Pro",
                          id: "kwaivgi/kling-v2.5-turbo-pro",
                          tier: "large",
                          description: "Kling V2.5 turbo pro mode",
                        },
                        {
                          name: "Kling V3 Video",
                          id: "kwaivgi/kling-v3-video",
                          tier: "large",
                          description: "Kling V3 with native audio, up to 15s",
                        },
                        {
                          name: "Seedance 2.0",
                          id: "bytedance/seedance-2.0",
                          tier: "large",
                          description: "ByteDance Seedance 2.0 with 4K & audio",
                        },
                        {
                          name: "PixVerse V6",
                          id: "pixverse/pixverse-v6",
                          tier: "large",
                          description: "PixVerse V6 with AI audio & multi-clip",
                        },
                        {
                          name: "Luma Ray 3.2",
                          id: "luma/ray-3.2",
                          tier: "large",
                          description: "Luma Ray 3.2 with HDR support",
                        },
                        {
                          name: "Vidu Q3 Pro",
                          id: "vidu/q3-pro",
                          tier: "large",
                          description: "Vidu Q3 Pro with native audio",
                        },
                        // Extra Large tier
                        {
                          name: "Kling V3 Omni Video",
                          id: "kwaivgi/kling-v3-omni-video",
                          tier: "extraLarge",
                          description:
                            "Kling V3 Omni with reference video support",
                        },
                        {
                          name: "Runway Gen 4.5",
                          id: "runwayml/gen-4.5",
                          tier: "extraLarge",
                          description: "Runway Gen 4.5 premium video",
                        },
                        {
                          name: "Veo 3.1",
                          id: "google/veo-3.1",
                          tier: "extraLarge",
                          description: "Google Veo 3.1 premium",
                        },
                        {
                          name: "Veo 3",
                          id: "google/veo-3",
                          tier: "extraLarge",
                          description: "Google Veo 3 premium",
                        },
                      ]}
                      propertyLabel="name"
                      propertyValue="id"
                    />
                    <ModelPricingInfo
                      modelId={
                        values?.videoGenerationModel || "sorcerer_motion"
                      }
                      type="video"
                      style={{ marginBottom: 12 }}
                      showDynamicRouting={false}
                    />
                  </View>
                </SectionRenderer>
              )}

            {isChatBot &&
              !["planner", "computer", "voice"].includes(values?.mode) &&
              _showBuilderSection("multilingual") && (
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: 1 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  title={t("settings.qa.multilingual")}
                  icon={AppAssets.icons.language}
                >
                  <View
                    style={{
                      paddingLeft: 10,
                    }}
                  >
                    <AppForms.AppFormSwitch
                      // disabled={isEdit}
                      wrapperStyle={[
                        {
                          maxHeight: 30,
                          paddingHorizontal: 10,

                          paddingVertical: 18,
                          margin: 5,
                          marginTop: 20,
                        },
                      ]}
                      showTextInputLabel={true}
                      label={t("settings.qa.multilingual")}
                      displayIsTicked={true}
                      name="isMultiLanguageEnabled"
                      value={values?.isMultiLanguageEnabled !== false}
                      description={
                        values?.isMultiLanguageEnabled
                          ? t("settings.qa.multilingualDisablesCharts")
                          : t("settings.qa.multilingualDesc")
                      }
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                    />

                    <AppForms.AppFormSwitch
                      wrapperStyle={[
                        {
                          maxHeight: 30,
                          paddingHorizontal: 10,

                          paddingVertical: 18,
                          margin: 5,
                          marginTop: 20,
                        },
                      ]}
                      showTextInputLabel={true}
                      label={t("settings.qa.dynamicLanguageDetection")}
                      displayIsTicked={true}
                      name="advancedLanguageDetection"
                      value={values?.advancedLanguageDetection}
                      disabled={!values?.isMultiLanguageEnabled}
                      description={t(
                        "settings.qa.dynamicLanguageDetectionDesc",
                      )}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                    />
                    <AppForms.AppFormSwitch
                      wrapperStyle={[
                        {
                          maxHeight: 30,
                          paddingHorizontal: 10,

                          paddingVertical: 18,
                          margin: 5,
                          marginTop: 20,
                        },
                      ]}
                      showTextInputLabel={true}
                      label={t("settings.qa.showDetectedLanguage")}
                      displayIsTicked={true}
                      name="showDetectedLanguage"
                      value={values?.showDetectedLanguage}
                      disabled={!values?.isMultiLanguageEnabled}
                      description={t("settings.qa.showDetectedLanguageDesc")}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                    />
                    <View
                      style={{
                        marginLeft: 5,
                        marginTop: 40,
                      }}
                    >
                      <AppForms.AppFormTrackSlider
                        step={0.01}
                        maximumValue={1}
                        minimumValue={0.5}
                        label={t("settings.qa.minConfidence")}
                        name="languageDetectionConfidence"
                        visualMultiplier={100}
                        unitLabel="%"
                      />
                    </View>
                  </View>
                </SectionRenderer>
              )}
            {isChatBot &&
              values?.restrictedAccess != true &&
              (!["planner", "computer", "voice"].includes(values?.mode) ||
                isBuilderLayout) &&
              _showBuilderSection("widget") && (
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: 1 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  isFullWidth
                  title={
                    ["planner", "computer", "voice"].includes(values?.mode)
                      ? t("settings.qa.shareApi", "Share & API")
                      : t("settings.qa.appearanceWebWidget")
                  }
                  icon={
                    ["planner", "computer", "voice"].includes(values?.mode)
                      ? AppAssets.icons.code
                      : AppAssets.icons.widget
                  }
                >
                  <View
                    style={
                      isBuilderLayout
                        ? {
                            flexDirection: "row",
                            alignItems: "flex-start",
                            width: "100%",
                          }
                        : null
                    }
                  >
                    {!["planner", "computer", "voice"].includes(
                      values?.mode,
                    ) && (
                      <View
                        style={
                          isBuilderLayout ? { flex: 1, minWidth: 300 } : null
                        }
                      >
                        <AppExpander
                          title={t(
                            "settings.qa.widgetGroupBranding",
                            "Branding",
                          )}
                          icon={AppAssets.icons.paintRoller}
                          isExpandedOnLoad={true}
                          style={{ width: "100%", zIndex: 20 }}
                        >
                          <View
                            style={{
                              flexDirection: "row",
                              gap: 24,
                              paddingLeft: 10,
                            }}
                          >
                            <View>
                              <AppText
                                style={{
                                  paddingLeft: 10,
                                  paddingBottom: 2,
                                }}
                                fontWeight="bold"
                              >
                                {t("settings.qa.agentIcon", "Agent icon")}
                              </AppText>
                              <AppText
                                style={{
                                  paddingLeft: 10,
                                  paddingBottom: 5,
                                  fontSize:
                                    currentTheme?.theme?.extra_small_font_size,
                                  maxWidth: 160,
                                }}
                                color={currentTheme?.theme?.mediumGrey}
                              >
                                {t(
                                  "settings.qa.agentIconDesc",
                                  "Shown next to every agent message and on the splash screen",
                                )}
                              </AppText>
                              <ImagePlaceholder
                                onFileSelection={handleIconSelection}
                                placeholderWrapperIconStyle={{
                                  marginLeft: 4,
                                }}
                                imagePath={agentImage}
                                isFileUploadInProgress={fileUploadInProgress}
                                fileUploadProgress={fileUploadProgress}
                                style={{
                                  alignSelf: "flex-start",
                                  marginLeft: 10,
                                  paddingLeft: 0,
                                  borderRadius: 10,
                                }}
                                wrapperStyleCloseIcon={{
                                  top: -2,
                                  right: -2,
                                }}
                                hintStyle={{
                                  marginLeft: 50,
                                }}
                              />
                            </View>
                            <View>
                              <AppText
                                style={{
                                  paddingLeft: 10,
                                  paddingBottom: 2,
                                }}
                                fontWeight="bold"
                              >
                                {t("settings.qa.splashLogo")}
                              </AppText>
                              <AppText
                                style={{
                                  paddingLeft: 10,
                                  paddingBottom: 5,
                                  fontSize:
                                    currentTheme?.theme?.extra_small_font_size,
                                  maxWidth: 160,
                                }}
                                color={currentTheme?.theme?.mediumGrey}
                              >
                                {t(
                                  "settings.qa.splashLogoDesc",
                                  "Displayed in the header bar at the top of the chat window",
                                )}
                              </AppText>
                              <ImagePlaceholder
                                onFileSelection={(file) =>
                                  handleIconSelection(file, true)
                                }
                                imagePath={agentLogo}
                                isFileUploadInProgress={fileUploadInProgress}
                                fileUploadProgress={fileUploadProgress}
                                isLargeLogo
                                hintStyle={{
                                  marginLeft: 5,
                                }}
                                style={{
                                  alignSelf: "flex-start",
                                  marginLeft: 10,
                                  paddingLeft: 0,
                                  borderRadius: 10,
                                }}
                                wrapperStyleCloseIcon={{
                                  top: -2,
                                  right: -2,
                                }}
                              />
                            </View>
                          </View>

                          <View
                            style={{
                              paddingLeft: 10,
                            }}
                          >
                            <ColorPickerField
                              showTextInputLabel
                              errorOffset={-13}
                              previewTop={40}
                              label={t("settings.qa.iconsColorLightMode")}
                              placeholder={t("settings.qa.hexColorPlaceholder")}
                              name="color"
                              previewColor={currentTheme?.theme?.primary}
                            />
                            <ColorPickerField
                              showTextInputLabel
                              errorOffset={-13}
                              previewTop={40}
                              label={t("settings.qa.iconsColorDarkMode")}
                              placeholder={t("settings.qa.hexColorPlaceholder")}
                              name="darkColor"
                              previewColor={currentTheme?.theme?.primary}
                            />
                            <ColorPickerField
                              showTextInputLabel
                              errorOffset={-13}
                              previewTop={40}
                              label={t("settings.qa.splashBackgroundLightMode")}
                              placeholder={t("settings.qa.hexColorPlaceholder")}
                              name="logoBackground"
                            />
                            <ColorPickerField
                              showTextInputLabel
                              errorOffset={-13}
                              previewTop={40}
                              label={t("settings.qa.splashBackgroundDarkMode")}
                              placeholder={t("settings.qa.hexColorPlaceholder")}
                              name="darkLogoBackground"
                            />
                            <AppForms.AppFormDropDown
                              isElevated={false}
                              dropDownStyle={{
                                borderColor: currentTheme?.theme?.border_color,
                                maxWidth: !isLargeScreen() && 250,
                              }}
                              showTextInputLabel
                              label={t("settings.qa.themeControl")}
                              isMandatory
                              name="forcedTheme"
                              items={[
                                {
                                  id: "auto",
                                  name: t("settings.qa.themeAuto"),
                                },
                                {
                                  id: "light",
                                  name: t("settings.qa.themeLight"),
                                },
                                {
                                  id: "dark",
                                  name: t("settings.qa.themeDark"),
                                },
                              ]}
                              propertyLabel="name"
                              propertyValue="id"
                            />
                            <AppSwitch
                              wrapperStyle={[
                                {
                                  maxHeight: 30,
                                  paddingLeft: 0,
                                  paddingRight: 10,
                                  marginLeft: 5,
                                  paddingVertical: 18,
                                  marginTop: 0,
                                  marginBottom: 50,
                                },
                              ]}
                              showTextInputLabel={true}
                              label={t(
                                "settings.qa.showWidgetControls",
                                "Show minimize & close controls",
                              )}
                              displayIsTicked={true}
                              isTicked={values?.showWidgetControls !== false}
                              onChange={(v) =>
                                setFieldValue("showWidgetControls", v)
                              }
                              description={t(
                                "settings.qa.showWidgetControlsDesc",
                                "Show minimize and close icons in the widget header when embedded. Icon colours follow the light and dark theme colours.",
                              )}
                              descriptionIcon={false}
                              descriptionColor={currentTheme?.theme?.mediumGrey}
                            />
                            <AppSwitch
                              wrapperStyle={[
                                {
                                  maxHeight: 30,
                                  paddingLeft: 0,
                                  paddingRight: 10,
                                  marginLeft: 5,
                                  paddingVertical: 18,
                                  marginTop: -10,
                                  marginBottom: 50,
                                },
                              ]}
                              showTextInputLabel={true}
                              label={t(
                                "settings.qa.showToolCallsDetailsInWidget",
                                "Show tool call details in widget",
                              )}
                              displayIsTicked={true}
                              isTicked={
                                values?.showToolCallsDetailsInWidget === true
                              }
                              onChange={(v) =>
                                setFieldValue("showToolCallsDetailsInWidget", v)
                              }
                              description={t(
                                "settings.qa.showToolCallsDetailsInWidgetDesc",
                                "Let public widget users expand tool calls to see input parameters and output. When off, tool calls show as a single line that blends into the answer.",
                              )}
                              descriptionIcon={false}
                              descriptionColor={currentTheme?.theme?.mediumGrey}
                            />
                            {(() => {
                              const accent =
                                values?.color ||
                                currentTheme?.theme?.primary ||
                                "#1DB8C6";
                              const showDetails =
                                values?.showToolCallsDetailsInWidget === true;
                              return (
                                <View
                                  style={{
                                    marginLeft: 5,
                                    marginBottom: 20,
                                    marginTop: -8,
                                  }}
                                >
                                  <AppText
                                    style={{
                                      fontSize: 11,
                                      color: currentTheme?.theme?.mediumGrey,
                                      marginBottom: 6,
                                    }}
                                  >
                                    {t(
                                      "settings.qa.showToolCallsDetailsInWidgetPreviewLabel",
                                      "Preview",
                                    )}
                                  </AppText>
                                  <View
                                    style={{
                                      borderRadius: 8,
                                      borderWidth: 1,
                                      borderColor:
                                        currentTheme?.theme?.border_color,
                                      padding: 10,
                                    }}
                                  >
                                    <AppText
                                      style={{
                                        fontSize: 12,
                                        color: currentTheme?.theme?.font_color,
                                        marginBottom: 6,
                                      }}
                                    >
                                      {t(
                                        "settings.qa.showToolCallsDetailsInWidgetPreviewAnswer",
                                        "Here is what I found:",
                                      )}
                                    </AppText>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        paddingVertical: 2,
                                      }}
                                    >
                                      <AppIcon
                                        icon={AppAssets.icons.web}
                                        size={13}
                                        color={accent}
                                      />
                                      <AppText
                                        style={{
                                          fontSize: 13,
                                          color: accent,
                                          fontWeight: "600",
                                          marginLeft: 6,
                                          marginRight: 6,
                                        }}
                                      >
                                        {t(
                                          "settings.qa.showToolCallsDetailsInWidgetPreviewTool",
                                          "Searched the web · 1.20s",
                                        )}
                                      </AppText>
                                      {showDetails && (
                                        <AppIcon
                                          icon={
                                            AppAssets.icons.collapseReasoning
                                          }
                                          size={12}
                                          color={accent}
                                          style={{ marginLeft: "auto" }}
                                        />
                                      )}
                                    </View>
                                    {showDetails && (
                                      <View
                                        style={{
                                          marginTop: 6,
                                          paddingLeft: 8,
                                          borderLeftWidth: 2,
                                          borderLeftColor: accent,
                                        }}
                                      >
                                        <AppText
                                          style={{
                                            fontSize: 11,
                                            color:
                                              currentTheme?.theme?.darkGrey,
                                          }}
                                        >
                                          query: "toothfairyai pricing"
                                        </AppText>
                                        <AppText
                                          style={{
                                            fontSize: 11,
                                            color:
                                              currentTheme?.theme?.darkGrey,
                                            marginTop: 2,
                                          }}
                                        >
                                          {t(
                                            "settings.qa.showToolCallsDetailsInWidgetPreviewDetail",
                                            "3 sources · 1.20s",
                                          )}
                                        </AppText>
                                      </View>
                                    )}
                                  </View>
                                </View>
                              );
                            })()}
                          </View>
                        </AppExpander>

                        <AppExpander
                          title={t("settings.qa.widgetGroupContent", "Content")}
                          icon={AppAssets.icons.chatIcon}
                          isExpandedOnLoad={true}
                          style={{ width: "100%", zIndex: 20 }}
                        >
                          <View
                            style={{
                              paddingLeft: 10,
                              zIndex: 20,
                            }}
                          >
                            <AppForms.AppFormField
                              showTextInputLabel
                              multiline={true}
                              textContainer={{
                                minHeight: 120,
                              }}
                              label={t("settings.qa.firstMessage")}
                              placeholder={t(
                                "settings.qa.messageLaunchTextPlaceholder",
                              )}
                              keyboardtype="default"
                              textContextType="label"
                              autoCorrect={false}
                              name="messageOnLaunch"
                              maxCharacters={255}
                            />
                            <AppForms.AppFormSwitch
                              wrapperStyle={[
                                {
                                  maxHeight: 30,
                                  paddingLeft: 0,
                                  paddingRight: 10,
                                  marginLeft: 5,
                                  paddingVertical: 18,
                                  marginBottom: 20,
                                },
                              ]}
                              showTextInputLabel={true}
                              label={t("settings.qa.showSplashMessage")}
                              displayIsTicked={true}
                              name="showSplashWhenEmpty"
                              value={values?.showSplashWhenEmpty}
                              description={t(
                                "settings.qa.showSplashMessageDesc",
                              )}
                              descriptionIcon={false}
                              descriptionColor={currentTheme?.theme?.mediumGrey}
                            />
                            <AppForms.AppFormSwitch
                              wrapperStyle={[
                                {
                                  maxHeight: 30,
                                  paddingLeft: 0,
                                  paddingRight: 10,
                                  marginLeft: 5,
                                  paddingVertical: 18,

                                  marginBottom: 50,
                                },
                              ]}
                              showTextInputLabel={true}
                              label={t("settings.qa.showAgentName")}
                              displayIsTicked={true}
                              name="showAgentName"
                              value={values?.showAgentName}
                              description={t("settings.qa.showAgentNameDesc")}
                              descriptionIcon={false}
                              descriptionColor={currentTheme?.theme?.mediumGrey}
                            />

                            <AppForms.AppFormField
                              showTextInputLabel
                              multiline={false}
                              label={t("settings.qa.inputPlaceholder")}
                              placeholder={t(
                                "settings.qa.inputTextPlaceholder",
                              )}
                              keyboardtype="default"
                              textContextType="label"
                              autoCorrect={false}
                              name="placeholderInputMessage"
                              maxCharacters={200}
                            />
                            <AppForms.AppFormField
                              showTextInputLabel
                              multiline={false}
                              label={t("settings.qa.loadingPlaceholder")}
                              placeholder={t(
                                "settings.qa.loadingTextPlaceholder",
                              )}
                              keyboardtype="default"
                              textContextType="label"
                              autoCorrect={false}
                              name="placeholderMessageOnLoading"
                              maxCharacters={50}
                            />

                            <AppForms.AppFormField
                              showTextInputLabel
                              multiline={true}
                              textContainer={{
                                minHeight: 120,
                              }}
                              label={t("settings.qa.disclaimer")}
                              placeholder={t(
                                "settings.qa.disclaimerPlaceholder",
                              )}
                              keyboardtype="default"
                              textContextType="label"
                              autoCorrect={false}
                              name="disclaimer"
                              maxCharacters={255}
                            />
                            <AppForms.AppFormField
                              showTextInputLabel
                              textInputLabelStyle={{
                                marginLeft: 10,
                              }}
                              multiline={true}
                              textContainer={{
                                minHeight: 120,
                              }}
                              errorOffset={0}
                              label={t("settings.qa.initialQuestions")}
                              placeholder={t(
                                "settings.qa.initialQuestionsPlaceholder",
                              )}
                              keyboardtype="default"
                              textContextType="label"
                              autoCorrect={false}
                              name="quickQuestions"
                              maxCharacters={255}
                            />
                          </View>
                        </AppExpander>

                        <AppExpander
                          title={t("settings.qa.widgetGroupPublish", "Publish")}
                          icon={AppAssets.icons.share}
                          isExpandedOnLoad={true}
                          style={{ width: "100%", zIndex: 20 }}
                        >
                          <View
                            style={{
                              paddingLeft: 10,
                              zIndex: 20,
                            }}
                          >
                            {!isAgentOllama && (
                              <View
                                style={{
                                  marginLeft: 10,
                                }}
                              >
                                <AppForms.AppFormSwitch
                                  wrapperStyle={[
                                    {
                                      maxHeight: 30,
                                      paddingRight: 10,
                                    },
                                  ]}
                                  showTextInputLabel={true}
                                  label={t(
                                    "settings.qa.makeItLive",
                                    "Make it live",
                                  )}
                                  displayIsTicked={true}
                                  name="preventWidgetUsage"
                                  isTicked={values?.preventWidgetUsage != true}
                                  onChange={(v) =>
                                    setFieldValue("preventWidgetUsage", !v)
                                  }
                                  disabled={false}
                                  description={t(
                                    "settings.qa.makeItLiveDesc",
                                    "When live, this agent can be shared via its public link or embedded on any website",
                                  )}
                                  descriptionIcon={false}
                                  descriptionColor={
                                    currentTheme?.theme?.mediumGrey
                                  }
                                />
                              </View>
                            )}
                          </View>
                        </AppExpander>
                      </View>
                    )}
                    {isBuilderLayout && (
                      <AgentWidgetPreview
                        isEdit={isEdit}
                        mode={values?.mode}
                        agentId={initialMapValues?.id}
                        workspaceId={workspaceid}
                        apiKey={getActiveWorkspace()?.workspaceToken}
                        agentLink={agentLink}
                        widgetCode={widgetString}
                        agentImage={agentImage}
                        agentLogo={agentLogo}
                        agentTitle={values?.label}
                        disclaimer={values?.disclaimer}
                        placeholderInputMessage={
                          values?.placeholderInputMessage
                        }
                        messageOnLaunch={values?.messageOnLaunch}
                        showSplashWhenEmpty={values?.showSplashWhenEmpty}
                        showAgentName={values?.showAgentName}
                        color={values?.color}
                        darkColor={values?.darkColor}
                        logoBackground={values?.logoBackground}
                        darkLogoBackground={values?.darkLogoBackground}
                        quickQuestions={values?.quickQuestions}
                        forcedTheme={values?.forcedTheme}
                        showWidgetControls={values?.showWidgetControls}
                      />
                    )}
                  </View>
                </SectionRenderer>
              )}

            {isChatBot && !isAgentOllama && _showBuilderSection("moderation") && (
              <SectionRenderer
                style={{ minWidth: 250, zIndex: -1 }}
                isExpandedOnLoad={builderActiveSection != null}
                title={t("settings.qa.moderationAndFeedback")}
                icon={AppAssets.icons.scan}
              >
                <View
                  style={{
                    paddingLeft: 10,
                  }}
                >
                  <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 0,

                        paddingVertical: 18,
                        margin: 5,
                        marginTop: 20,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.allowFeedback")}
                    displayIsTicked={true}
                    name="allowFeedback"
                    value={values?.allowFeedback}
                    description={t("settings.qa.allowFeedbackDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  <AppForms.AppFormSwitch
                    // disabled={isEdit}
                    // disabled={true}
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 0,

                        paddingVertical: 18,
                        margin: 5,
                        marginTop: 20,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.contentModeration")}
                    displayIsTicked={true}
                    name="hasModeration"
                    value={values?.hasModeration}
                    description={t("settings.qa.contentModerationDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  <AppForms.AppFormFieldJSONV2
                    showTextInputLabel
                    isMandatory
                    wrapperInputStyle={{
                      paddingTop: 10,
                    }}
                    textInputLabelStyle={{
                      marginLeft: 7,
                      marginTop: 30,
                    }}
                    wrapperContainer={{
                      height: 450,
                      marginBottom: 10,
                    }}
                    mode="code"
                    // schema={validations.parametersVal}
                    label={t("settings.qa.moderationRules")}
                    name="customModerations"
                    darkMode={mode === "dark"}
                  />
                  <AppForms.AppFormField
                    showTextInputLabel
                    textContainer={{
                      minHeight: 120,
                    }}
                    multiline={true}
                    label={t("settings.qa.messageOnModeration")}
                    placeholder={t(
                      "settings.qa.messageOnModerationPlaceholder",
                    )}
                    keyboardtype="default"
                    textContextType="label"
                    autoCorrect={false}
                    name="moderationMessage"
                    maxCharacters={512}
                  />
                  {values?.useInterleavedReasoning === true && (
                    <>
                      <AppForms.AppFormTrackSlider
                        minimumValue={1}
                        maximumValue={10000}
                        step={1}
                        label={t("settings.qa.maxSessionUoI")}
                        name="maxSessionUoI"
                        nullable
                        nullLabel={t("settings.qa.null", {
                          defaultValue: "Null",
                        })}
                      />

                      <AppForms.AppFormTrackSlider
                        minimumValue={1}
                        maximumValue={100}
                        step={1}
                        label={t("settings.qa.maxSessionTurns")}
                        name="maxSessionTurns"
                        nullable
                        nullLabel={t("settings.qa.null", {
                          defaultValue: "Null",
                        })}
                      />
                      <AppForms.AppFormField
                        showTextInputLabel
                        textContainer={{
                          minHeight: 120,
                        }}
                        multiline={true}
                        label={t("settings.qa.messageOnMaxSession")}
                        placeholder={t(
                          "settings.qa.messageOnMaxSessionPlaceholder",
                        )}
                        formFieldContainerStyle={{
                          marginTop: 10,
                        }}
                        keyboardtype="default"
                        textContextType="label"
                        autoCorrect={false}
                        name="maxSessionMessage"
                        maxCharacters={512}
                      />
                    </>
                  )}
                </View>
              </SectionRenderer>
            )}

            {isChatBot &&
              !isAgentOllama &&
              !["planner", "voice"].includes(values?.mode) &&
              _showBuilderSection("analytics") && (
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: -1 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  title={t("settings.qa.analytics")}
                  icon={AppAssets.icons.charting}
                >
                  <View
                    style={{
                      paddingLeft: 10,
                    }}
                  >
                    <AppForms.AppFormMultiSelect
                      key={`alerts-${lastOpened}`}
                      name="alertIDs"
                      topErrorOffset={-21}
                      showTextInputLabel
                      label={t("settings.qa.alerts")}
                      touchedByDefault
                      placeholder={t(
                        "settings.qa.alertsPlaceholder",
                        "Select alerts to trigger for this agent",
                      )}
                      hidePlaceholder={true}
                      items={availableAlerts}
                      uniqueKey="id"
                      showDropDowns={true}
                      readOnlyHeadings={true}
                      style={{ maxWidth: 800 }}
                      wrapperContainerStyle={{
                        flex: !isLargeScreen() ? 1 : 0.5,
                        zIndex: 20,
                        marginLeft: 5,
                        marginTop: 5,
                        marginBottom: 50,
                      }}
                      styles={colourStyles}
                      options={availableAlerts.map((u) => {
                        return {
                          ...u,
                          value: u?.id,
                          label: u?.name,
                          category: "alert",
                          backgroundColor: AppUtilities.stringToHslColor(
                            u?.name,
                            71,
                          ),
                        };
                      })}
                    />
                    <AppForms.AppFormField
                      showTextInputLabel
                      textContainer={{
                        minHeight: 350,
                      }}
                      multiline={true}
                      label={t("settings.qa.analyticsInstructions")}
                      placeholder={t(
                        "settings.qa.analyticsInstructionsPlaceholder",
                      )}
                      keyboardtype="default"
                      textContextType="label"
                      autoCorrect={false}
                      name="analyticsInstructions"
                      maxCharacters={4096}
                    />
                    <AppForms.AppFormFieldJSONV2
                      showTextInputLabel
                      wrapperInputStyle={{
                        paddingTop: 10,
                      }}
                      textInputLabelStyle={{
                        marginLeft: 10,
                        marginBottom: 20,
                      }}
                      wrapperContainer={{
                        height: 450,
                      }}
                      mode="code"
                      label={t("settings.qa.analyticsCustomFields")}
                      description={t("settings.qa.analyticsCustomFieldsDesc")}
                      descriptionIcon={false}
                      name="analyticsCustomFields"
                      darkMode={mode === "dark"}
                    />
                  </View>
                </SectionRenderer>
              )}

            {_showBuilderSection("hosting") && (
              <SectionRenderer
                style={{ minWidth: 250, zIndex: -20 }}
                isExpandedOnLoad={builderActiveSection != null}
                title={t("settings.qa.hostingAndModels")}
                icon={AppAssets.icons.hosting}
              >
                <View
                  style={{
                    paddingLeft: 0,
                    marginHorizontal: 10,
                  }}
                >
                  {values?.llmProvider === "tf" && (
                    <AppForms.AppFormDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                        maxWidth: !isLargeScreen() && 250,
                      }}
                      showTextInputLabel
                      label={t("settings.qa.endpointType")}
                      isMandatory
                      name="regionalEndpoint"
                      items={
                        getActiveWorkspace()?.enforceRegionalModels === true
                          ? [{ name: t("settings.qa.regional"), id: true }]
                          : [
                              { name: t("settings.qa.global"), id: false },
                              { name: t("settings.qa.regional"), id: true },
                            ]
                      }
                      propertyLabel="name"
                      propertyValue="id"
                      disabled={
                        getActiveWorkspace()?.enforceRegionalModels === true
                      }
                    />
                  )}
                  {!["planner", "computer", "voice"].includes(values?.mode) && (
                    <>
                      <AppForms.AppFormDropDown
                        isElevated={false}
                        dropDownStyle={{
                          borderColor: currentTheme?.theme?.border_color,
                          maxWidth: !isLargeScreen() && 250,
                        }}
                        showTextInputLabel
                        label={t("settings.qa.llmProvider")}
                        isMandatory
                        name="llmProvider"
                        items={baseLlmProviders}
                        propertyLabel="name"
                        propertyValue="id"
                      />

                      <AppForms.AppFormDropDown
                        isElevated={false}
                        dropDownStyle={{
                          borderColor: currentTheme?.theme?.border_color,
                          maxWidth: !isLargeScreen() && 250,
                        }}
                        showTextInputLabel
                        label={t("settings.qa.baseModel")}
                        isMandatory
                        name="llmBaseModel"
                        items={
                          isAgentOllama
                            ? oLlamaModels
                            : values?.useInterleavedReasoning
                            ? baseLlmModels?.filter(
                                (m) => m?.interleaved === true,
                              ) || []
                            : baseLlmModels
                        }
                        propertyLabel="name"
                        propertyValue="id"
                      />
                      {values?.llmBaseModel && !isAgentOllama && (
                        <>
                          <FineTunedModelStatusChip
                            modelId={values?.llmBaseModel}
                            token={getActiveWorkspace()?.workspaceToken}
                          />
                          <FineTunedModelPricingInfo
                            modelId={values?.llmBaseModel}
                            token={getActiveWorkspace()?.workspaceToken}
                            style={{ marginBottom: 12 }}
                            showReasonerBadge={true}
                          />
                        </>
                      )}

                      {(_baseModels?.find((u) => u?.id == values?.llmBaseModel)
                        ?.reasoner ||
                        shouldShowReasonerDropdown(values?.llmBaseModel)) && (
                        <>
                          <AppForms.AppFormDropDown
                            isElevated={false}
                            name="reasoningEffort"
                            dropDownStyle={{
                              borderColor: currentTheme?.theme?.border_color,
                              maxWidth: !isLargeScreen() && 250,
                            }}
                            showTextInputLabel
                            label={t("settings.qa.reasoningEffort")}
                            isMandatory
                            items={[
                              {
                                name: t("settings.qa.reasoningEffortNone"),
                                id: "none",
                              },
                              {
                                name: t("settings.qa.reasoningEffortLow"),
                                id: "low",
                              },
                              {
                                name: t("settings.qa.reasoningEffortMedium"),
                                id: "medium",
                              },
                              {
                                name: t("settings.qa.reasoningEffortHigh"),
                                id: "high",
                              },
                            ]}
                            propertyLabel="name"
                            propertyValue="id"
                          />
                        </>
                      )}
                      {values?.llmBaseModel == "dynamic" && (
                        <AppForms.AppFormField
                          showTextInputLabel
                          formFieldContainerStyle={{
                            zIndex: 10,
                          }}
                          textContainer={{
                            minHeight: 120,
                          }}
                          multiline={true}
                          label={t("settings.qa.modelRoutingInstructions")}
                          placeholder={t(
                            "settings.qa.modelRoutingInstructionsPlaceholder",
                          )}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="dynamicModelSelectionInstructions"
                          mentioning
                          mentions={matchModelsWithProviders(
                            "tf",
                            getActiveWorkspace()?.subscriptionType,
                            false,
                            customHostings,
                          )
                            ?.filter(
                              (u) => u?.id !== "dynamic" && u?.dynamicRouting,
                            )
                            ?.map((i) => {
                              return {
                                id: i.id,
                                label: t(i.name),
                              };
                            })}
                          onMentionSelect={(mention) => console.log(mention)}
                        />
                      )}
                    </>
                  )}

                  {pullProgress != 0 &&
                    pullStatus != "" &&
                    customHostings?.find(
                      (u) =>
                        values?.llmProvider == u?.name && u?.type == "ollama",
                    ) &&
                    (loadedModel == values?.llmBaseModel ||
                      downloadingModel == values?.llmBaseModel) && (
                      <AppText
                        wrapperStyle={{
                          marginLeft: 20,
                        }}
                        color={currentTheme?.theme?.orange}
                        style={{
                          fontWeight: "bold",
                        }}
                        preventTranslation
                      >
                        {pullStatus.charAt(0).toUpperCase() +
                          pullStatus.slice(1)}{" "}
                        - {Math.round(pullProgress * 100) / 100}%
                      </AppText>
                    )}
                  {isChatBot && !isAgentOllama && (
                    <>
                      {showVoicePipelineConfigs && (
                        <>
                          {/* Clarifier: these settings power the realtime
                              voice experience (/voice + /live), distinct
                              from the capabilities-side voice-over (TTS
                              files on text replies). */}
                          <AppText
                            wrapperStyle={{
                              paddingHorizontal: 10,
                              marginBottom: 10,
                            }}
                            style={{
                              fontSize:
                                currentTheme?.theme?.extra_small_font_size,
                            }}
                            color={currentTheme?.theme?.mediumGrey}
                          >
                            {t("settings.qa.voicePipelineDesc")}
                          </AppText>
                          {/* Cloned voice — same control as the Agent
                              capabilities section; a clone in voiceName
                              takes priority over the language / provider /
                              voice selections below. */}
                          <ClonedVoiceControl
                            isActive={isClonedVoiceId(values?.voiceName)}
                            isCloneInProgress={voiceCloneInProgress}
                            audioPath={values?.voiceFileKey}
                            onFileSelection={handleAudioSelection}
                            onRecordPress={() => setShowVoiceCloningModal(true)}
                            onRemove={handleRemoveClonedVoice}
                            fileUploadInProgress={fileUploadInProgress}
                            fileUploadProgress={fileUploadProgress}
                            isRecording={isAudioRecording}
                          />
                          {/* TTS Model - First since voices depend on it */}
                          <AppForms.AppFormDropDown
                            isElevated={false}
                            enabled={!isClonedVoiceId(values?.voiceName)}
                            dropDownStyle={{
                              borderColor: currentTheme?.theme?.border_color,
                              maxWidth: !isLargeScreen() && 250,
                            }}
                            showTextInputLabel
                            label={t("settings.qa.ttsModel")}
                            isMandatory
                            name="ttsModel"
                            items={TTS_MODELS}
                            propertyLabel="name"
                            propertyValue="id"
                          />
                          {values?.ttsModel && (
                            <ModelPricingInfo
                              modelId={values?.ttsModel}
                              type="tts"
                              showDynamicRouting={false}
                            />
                          )}
                          {/* Default Voice Language - To allow filtering */}
                          <AppForms.AppFormDropDown
                            isElevated={false}
                            enabled={!isClonedVoiceId(values?.voiceName)}
                            dropDownStyle={{
                              borderColor: currentTheme?.theme?.border_color,
                              maxWidth: !isLargeScreen() && 250,
                            }}
                            showTextInputLabel
                            label={t("settings.qa.defaultVoiceLanguage")}
                            isMandatory
                            name="defaultVoiceLanguage"
                            items={getLanguagesByModel(values?.ttsModel)}
                            propertyLabel="name"
                            propertyValue="id"
                            description={t(
                              "settings.qa.selectLanguageToFilterVoices",
                            )}
                          />
                          {/* Voice Selection - Filtered by language and model */}
                          <AppForms.AppFormDropDown
                            isElevated={false}
                            enabled={!isClonedVoiceId(values?.voiceName)}
                            dropDownStyle={{
                              borderColor: currentTheme?.theme?.border_color,
                              maxWidth: !isLargeScreen() && 250,
                            }}
                            showTextInputLabel
                            label={t("settings.qa.voice")}
                            isMandatory
                            name="voiceName"
                            items={filteredVoices}
                            propertyLabel="name"
                            propertyValue="id"
                          />
                          {filteredVoices?.length > 0 && (
                            <AppText
                              preventTranslation
                              wrapperStyle={{
                                paddingHorizontal: 10,
                                marginBottom: 10,
                              }}
                              color={currentTheme?.theme?.darkGrey}
                              style={{
                                marginLeft: 10,
                                fontSize:
                                  currentTheme?.theme?.extra_small_font_size,
                              }}
                            >
                              {t("settings.qa.voicesAvailableCount", {
                                count: filteredVoices.length,
                              })}
                            </AppText>
                          )}

                          {/* STT Model */}
                          <AppForms.AppFormDropDown
                            isElevated={false}
                            dropDownStyle={{
                              borderColor: currentTheme?.theme?.border_color,
                              maxWidth: !isLargeScreen() && 250,
                            }}
                            showTextInputLabel
                            label={t("settings.qa.sttModel")}
                            isMandatory
                            name="sttModel"
                            items={STT_MODELS}
                            propertyLabel="name"
                            propertyValue="id"
                          />
                          {values?.sttModel && (
                            <ModelPricingInfo
                              modelId={values?.sttModel}
                              type="stt"
                              showDynamicRouting={false}
                            />
                          )}

                          {/* LLM Voice Model */}
                          <AppForms.AppFormDropDown
                            isElevated={false}
                            dropDownStyle={{
                              borderColor: currentTheme?.theme?.border_color,
                              maxWidth: !isLargeScreen() && 250,
                            }}
                            showTextInputLabel
                            label={t("settings.qa.llmModelForVoice")}
                            isMandatory
                            name="llmVoiceModel"
                            items={LLM_VOICE_MODELS}
                            propertyLabel="name"
                            propertyValue="id"
                          />
                          {values?.llmVoiceModel && (
                            <ModelPricingInfo
                              modelId={values?.llmVoiceModel}
                              type="llm"
                              showDynamicRouting={false}
                            />
                          )}

                          {/* Background Noise */}
                          <AppForms.AppFormDropDown
                            isElevated={false}
                            dropDownStyle={{
                              borderColor: currentTheme?.theme?.border_color,
                              maxWidth: !isLargeScreen() && 250,
                            }}
                            showTextInputLabel
                            label={t("settings.qa.backgroundNoise")}
                            isMandatory
                            name="voiceBackgroundNoise"
                            items={[
                              {
                                name: t("settings.qa.backgroundNoiseOffice"),
                                id: "office",
                              },
                              {
                                name: t("settings.qa.backgroundNoiseCity"),
                                id: "city",
                              },
                              {
                                name: t("settings.qa.backgroundNoiseForest"),
                                id: "forest",
                              },
                              {
                                name: t(
                                  "settings.qa.backgroundNoiseCrowdedRoom",
                                ),
                                id: "crowded_room",
                              },
                              {
                                name: t("settings.qa.backgroundNoiseHoldMusic"),
                                id: "hold_music",
                              },
                              {
                                name: t("settings.qa.reasoningEffortNone"),
                                id: "none",
                              },
                            ]}
                            propertyLabel="name"
                            propertyValue="id"
                          />

                          {/* SIP Phone Configuration - Business/Enterprise only */}
                        </>
                      )}

                      {values?.mode !== "voice" &&
                        !values?.useInterleavedReasoning && (
                          <>
                            <View
                              style={{
                                height: 1,
                                backgroundColor:
                                  currentTheme?.theme?.mediumGrey,
                                marginVertical: 10,
                              }}
                            />
                            <AppForms.AppFormDropDown
                              isElevated={false}
                              dropDownStyle={{
                                borderColor: currentTheme?.theme?.border_color,
                                maxWidth: !isLargeScreen() && 250,
                              }}
                              showTextInputLabel
                              label={`${convertContextWithFunctionCallingLabel(
                                values?.mode,
                                t,
                              )}`}
                              isMandatory
                              name="functionCallingProvider"
                              items={
                                values?.mode == "planner"
                                  ? [{ name: "ToothFairyAI", id: "tf" }]
                                  : functionCallingProviders
                              }
                              propertyLabel="name"
                              propertyValue="id"
                              disabled={values?.mode == "computer"}
                            />
                            <AppForms.AppFormDropDown
                              isElevated={false}
                              dropDownStyle={{
                                borderColor: currentTheme?.theme?.border_color,
                                maxWidth: !isLargeScreen() && 250,
                              }}
                              showTextInputLabel
                              label={`${convertContextWithFunctionCallingLabel(
                                values?.mode,
                                t,
                              )}`}
                              isMandatory
                              name="functionCallingModel"
                              items={functionCallingModels?.filter((u) => {
                                if (
                                  values?.mode == "planner" ||
                                  values?.mode == "computer"
                                )
                                  return u?.plannerAvailable == true;
                                else return u?.preventFunctionCalling != true;
                              })}
                              disabled={values?.mode == "computer"}
                              propertyLabel="name"
                              propertyValue="id"
                            />
                            {values?.functionCallingModel && !isAgentOllama && (
                              <ModelPricingInfo
                                modelId={values?.functionCallingModel}
                                style={{ marginBottom: 12 }}
                                showDynamicRouting={false}
                              />
                            )}

                            {(functionCallingModels?.find(
                              (u) => u?.id == values?.functionCallingModel,
                            )?.reasoner ||
                              shouldShowReasonerDropdown(
                                values?.functionCallingModel,
                              )) && (
                              <>
                                <AppForms.AppFormDropDown
                                  isElevated={false}
                                  name="fnReasoningEffort"
                                  dropDownStyle={{
                                    borderColor:
                                      currentTheme?.theme?.border_color,
                                    maxWidth: !isLargeScreen() && 250,
                                  }}
                                  showTextInputLabel
                                  label={t(
                                    "settings.qa.toolCallReasoningEffort",
                                  )}
                                  isMandatory
                                  items={[
                                    {
                                      name: "None",
                                      id: "none",
                                    },
                                    {
                                      name: "Low",
                                      id: "low",
                                    },
                                    {
                                      name: "Medium",
                                      id: "medium",
                                    },
                                    {
                                      name: "High",
                                      id: "high",
                                    },
                                  ]}
                                  propertyLabel="name"
                                  propertyValue="id"
                                />
                              </>
                            )}
                          </>
                        )}
                    </>
                  )}

                  {values?.mode == "planner" && (
                    <>
                      <View
                        style={{
                          height: 1,
                          backgroundColor: currentTheme?.theme?.mediumGrey,
                          marginVertical: 10,
                        }}
                      />
                      <AppForms.AppFormDropDown
                        isElevated={false}
                        dropDownStyle={{
                          borderColor: currentTheme?.theme?.border_color,
                          maxWidth: !isLargeScreen() && 250,
                        }}
                        showTextInputLabel
                        label={t("settings.qa.reviewerProvider")}
                        isMandatory
                        name="adverserialProvider"
                        items={[{ name: "ToothFairyAI", id: "tf" }]}
                        propertyLabel="name"
                        propertyValue="id"
                      />
                      <AppForms.AppFormDropDown
                        isElevated={false}
                        dropDownStyle={{
                          borderColor: currentTheme?.theme?.border_color,
                          maxWidth: !isLargeScreen() && 250,
                        }}
                        showTextInputLabel
                        label={t("settings.qa.reviewerModel")}
                        isMandatory
                        name="adverserialModel"
                        items={adverserialModels?.filter((u) => {
                          if (
                            values?.mode == "planner" ||
                            values?.mode == "computer"
                          )
                            return u?.plannerAvailable == true;
                          else return u?.preventFunctionCalling != true;
                        })}
                        propertyLabel="name"
                        propertyValue="id"
                      />
                      {values?.adverserialModel && !isAgentOllama && (
                        <ModelPricingInfo
                          modelId={values?.adverserialModel}
                          style={{ marginBottom: 12 }}
                          showDynamicRouting={false}
                        />
                      )}
                    </>
                  )}

                  {isChatBot &&
                    !isAgentOllama &&
                    (values?.mode == "chatter" ||
                      values?.mode == "retriever") && (
                      <>
                        <View
                          style={{
                            height: 1,
                            backgroundColor: currentTheme?.theme?.mediumGrey,
                            marginVertical: 10,
                          }}
                        />
                        <AppForms.AppFormDropDown
                          isElevated={false}
                          dropDownStyle={{
                            borderColor: currentTheme?.theme?.border_color,
                            maxWidth: !isLargeScreen() && 250,
                          }}
                          showTextInputLabel
                          label={t("settings.qa.visionProvider")}
                          isMandatory
                          items={functionCallingProviders}
                          propertyLabel="name"
                          propertyValue="id"
                          disabled
                        />
                        <AppForms.AppFormDropDown
                          isElevated={false}
                          dropDownStyle={{
                            borderColor: currentTheme?.theme?.border_color,
                            maxWidth: !isLargeScreen() && 250,
                          }}
                          showTextInputLabel
                          label={t("settings.qa.visionModel")}
                          isMandatory
                          name="visionModel"
                          items={filteredVisionModels}
                          propertyLabel="name"
                          propertyValue="id"
                        />
                        {values?.visionModel && (
                          <ModelPricingInfo
                            modelId={values?.visionModel}
                            style={{ marginBottom: 12 }}
                            showDynamicRouting={false}
                          />
                        )}
                        {values?.mode == "retriever" &&
                          (values?.agenticRAG ||
                            values?.useInterleavedReasoning) && (
                            <>
                              <View
                                style={{
                                  height: 1,
                                  backgroundColor:
                                    currentTheme?.theme?.mediumGrey,
                                  marginVertical: 10,
                                }}
                              />
                              <AppForms.AppFormDropDown
                                isElevated={false}
                                dropDownStyle={{
                                  borderColor:
                                    currentTheme?.theme?.border_color,
                                  maxWidth: !isLargeScreen() && 250,
                                }}
                                showTextInputLabel
                                label={t("settings.qa.agenticCodingProvider")}
                                isMandatory
                                items={functionCallingProviders}
                                propertyLabel="name"
                                propertyValue="id"
                                disabled
                              />
                              <AppForms.AppFormDropDown
                                isElevated={false}
                                dropDownStyle={{
                                  borderColor:
                                    currentTheme?.theme?.border_color,
                                  maxWidth: !isLargeScreen() && 250,
                                }}
                                showTextInputLabel
                                label={t("settings.qa.agenticCodingModel")}
                                isMandatory
                                name="agenticCodingModel"
                                items={[
                                  {
                                    name: t("settings.qa.codingModelInherit"),
                                    id: "inherit",
                                  },
                                  ...functionCallingModels,
                                ]}
                                propertyLabel="name"
                                propertyValue="id"
                              />
                              {values?.agenticCodingModel &&
                                values?.agenticCodingModel !== "inherit" && (
                                  <ModelPricingInfo
                                    modelId={values?.agenticCodingModel}
                                    style={{ marginBottom: 12 }}
                                    showDynamicRouting={false}
                                  />
                                )}
                            </>
                          )}
                      </>
                    )}
                </View>
              </SectionRenderer>
            )}
          </View>
          <View
            style={{
              zIndex: -1,
            }}
          >
            {isChatBot &&
              !isAgentOllama &&
              _showBuilderSection("channels") &&
              !["computer"]?.includes(values?.mode) && (
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: -1 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  title={t("settings.qa.agentChannels")}
                  icon={AppAssets.icons.channels}
                  iconSize={20}
                >
                  <View
                    style={{
                      paddingLeft: 0,
                    }}
                  >
                    <View
                      style={{
                        marginLeft: 10,
                      }}
                    >
                      {
                        <>
                          <AppForms.AppFormSwitch
                            // disabled={isEdit}
                            // disabled={true}
                            wrapperStyle={[
                              {
                                maxHeight: 30,
                                paddingHorizontal: 10,

                                paddingVertical: 18,
                                marginBottom: 5,
                                marginTop: 20,
                                marginHorizontal: 0,
                              },
                            ]}
                            showTextInputLabel={true}
                            label={t("settings.qa.enableAgentTFEmail")}
                            displayIsTicked={true}
                            name="allowEmailToAgent"
                            value={values?.allowEmailToAgent}
                            description={t(
                              "settings.qa.enableAgentTFEmailDesc",
                            )}
                            descriptionIcon={false}
                            descriptionColor={currentTheme?.theme?.mediumGrey}
                          />
                          {values?.mode != "voice" && (
                            <AppForms.AppFormSwitch
                              // disabled={isEdit}
                              // disabled={true}
                              wrapperStyle={[
                                {
                                  maxHeight: 30,
                                  paddingHorizontal: 10,

                                  paddingVertical: 18,
                                  marginBottom: 15,
                                  marginTop: 20,
                                  marginHorizontal: 0,
                                },
                              ]}
                              showTextInputLabel={true}
                              label={t("settings.qa.newChatOnReceivedMsg")}
                              displayIsTicked={true}
                              name="forceNewThreadOnProgrammaticRequest"
                              value={
                                values?.forceNewThreadOnProgrammaticRequest
                              }
                              description={t(
                                "settings.qa.newChatOnReceivedMsgDesc",
                              )}
                              descriptionIcon={false}
                              descriptionColor={currentTheme?.theme?.mediumGrey}
                            />
                          )}
                        </>
                      }

                      {values?.allowEmailToAgent && (
                        <View
                          style={{
                            marginTop: 10,
                          }}
                        >
                          <AppText
                            wrapperStyle={{
                              paddingHorizontal: 13,
                              marginTop: 20,
                            }}
                            iconSize={16}
                            leftIcon={AppAssets?.icons?.info}
                            color={currentTheme?.theme?.orange}
                            style={{
                              marginLeft: 20,
                            }}
                          >
                            {t("settings.qa.agentEmailReceiveSend")}
                          </AppText>
                          <AppText
                            iconSize={16}
                            wrapperStyle={{
                              alignSelf: "flex-start",
                              marginBottom: 15,
                              width: "100%",
                              marginTop: 10,
                              paddingRight: 10,
                              marginLeft: -7,
                            }}
                            leftIcon={AppAssets?.icons?.email}
                            leftIconStyle={{
                              marginLeft: 20,
                            }}
                            style={{
                              color: currentTheme?.theme?.primary,
                              left: 55,
                              fontWeight: "bold",
                              position: "absolute",
                            }}
                            rightIcon={AppAssets?.icons?.quickCopy}
                            isClickable
                            onRightIconPress={() => {
                              AppToasty.show(t("toasts.clipboard.copied"), {
                                type: "success",
                              });
                              navigator.clipboard.writeText(
                                `${values?.label} <${values?.id}@mail.${
                                  envConfig?.REACT_APP_ENV == "dev"
                                    ? "toothfairylab.link"
                                    : "toothfairyai.com"
                                }>`,
                              );
                            }}
                            textStyle={{
                              marginLeft: 0,
                            }}
                            width={420}
                          >
                            {`${values?.id}@mail.${
                              envConfig?.REACT_APP_ENV == "dev"
                                ? "toothfairylab.link"
                                : "toothfairyai.com"
                            }`}
                          </AppText>
                          <AppForms.AppFormField
                            showTextInputLabel
                            multiline={true}
                            textContainer={{
                              minHeight: 120,
                            }}
                            label={t("settings.qa.restrictAllowedEmails")}
                            placeholder={t(
                              "settings.qa.restrictAllowedEmailsPlaceholder",
                            )}
                            keyboardtype="default"
                            textContextType="label"
                            autoCorrect={false}
                            name="allowedEmails"
                          />
                        </View>
                      )}
                      <AppForms.AppFormMultiSelect
                        showTextInputLabel
                        label={t("settings.qa.assignCustomChannels")}
                        key={`comms-${lastOpened}`}
                        name="communicationServices"
                        chipHref={chipHrefChannels}
                        topErrorOffset={-21}
                        touchedByDefault
                        placeholder={t(
                          "settings.qa.assignCustomChannelsPlaceholder",
                        )}
                        hidePlaceholder={true}
                        items={agentsChannels}
                        uniqueKey="id"
                        showDropDowns={true}
                        readOnlyHeadings={true}
                        labelOption="name"
                        groupByProperty="channel"
                        style={{ maxWidth: 800 }}
                        wrapperContainerStyle={{
                          flex: !isLargeScreen() ? 1 : 0.5,
                          zIndex: 2000,
                          marginLeft: 8,
                          marginTop: 20,
                        }}
                        styles={colourStyles}
                        options={agentsChannels?.map((u) => {
                          return {
                            ...u,
                            label: u?.name + " - " + u?.provider,
                            value: u?.id,
                            backgroundColor: Utility?.mapFunctionTypeToColour(
                              u,
                              currentTheme,
                            ),
                          };
                        })}
                      />

                      {values?.communicationServices?.find(
                        (u) => u?.channel == "sms",
                      ) && (
                        <AppForms.AppFormField
                          showTextInputLabel
                          errorOffset={0}
                          formFieldContainerStyle={{
                            paddingBottom: 0,
                            marginTop: 45,
                          }}
                          label={t("settings.qa.phoneNumber")}
                          placeholder={t("settings.qa.phoneNumberPlaceholder")}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="phoneNumberAsSender"
                        />
                      )}
                      {values?.communicationServices?.find(
                        (u) => u?.channel == "whatsapp",
                      ) && (
                        <AppForms.AppFormField
                          showTextInputLabel
                          errorOffset={0}
                          formFieldContainerStyle={{
                            paddingBottom: 0,
                            marginTop: 6,
                          }}
                          label={t("settings.qa.whatsappNumber")}
                          placeholder={t(
                            "settings.qa.whatsappNumberPlaceholder",
                          )}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="whatsappNumberAsSender"
                        />
                      )}
                      {values?.communicationServices?.find(
                        (u) => u?.channel == "email",
                      ) && (
                        <AppForms.AppFormField
                          showTextInputLabel
                          errorOffset={0}
                          formFieldContainerStyle={{
                            paddingBottom: 0,
                            marginTop: 6,
                          }}
                          label={t("settings.qa.emailAddress")}
                          placeholder={t("settings.qa.emailAddressPlaceholder")}
                          keyboardtype="default"
                          textContextType="label"
                          autoCorrect={false}
                          name="emailAsSender"
                        />
                      )}
                    </View>
                    {values?.mode != "voice" && (
                      <View
                        style={{
                          marginLeft: 10,
                          marginTop: 10,
                          zIndex: -1,
                        }}
                      >
                        <AppForms.AppFormTrackSlider
                          minimumValue={0}
                          maximumValue={120}
                          step={1}
                          label={t("settings.qa.deliveryCallbackDelaySeconds")}
                          name="channelCallbackDelay"
                        />
                      </View>
                    )}
                  </View>
                </SectionRenderer>
              )}
            {/* Voice Settings Section - Only for voice mode */}
          </View>
          {values?.mode == "voice" && _showBuilderSection("sip") && (
            <View
              style={{
                zIndex: values?.mode == "voice" ? -10 : 0,
              }}
            >
              <SectionRenderer
                style={{ minWidth: 250, zIndex: -10 }}
                isExpandedOnLoad={builderActiveSection != null}
                title={t("settings.qa.sipPhoneConfiguration")}
                icon={AppAssets.icons.voiceUser}
                iconSize={20}
              >
                <View
                  style={{
                    paddingLeft: 0,
                  }}
                >
                  <View
                    style={{
                      marginLeft: 10,
                    }}
                  >
                    {!values?.communicationServices?.find(
                      (u) => u?.channel == "sip",
                    ) ? (
                      <AppText
                        leftIcon={AppAssets?.icons?.warning}
                        wrapperStyle={{
                          paddingHorizontal: 10,
                          marginBottom: 15,
                        }}
                        color={currentTheme?.theme?.orange}
                        style={{
                          marginLeft: 10,
                          fontSize: currentTheme?.theme?.extra_small_font_size,
                        }}
                        iconSize={14}
                      >
                        {t("settings.qa.noSipChannelConfigured")}{" "}
                        {t("settings.qa.noSipChannelConfiguredBinding")}
                      </AppText>
                    ) : (
                      <>
                        <AppText
                          leftIcon={AppAssets?.icons?.info}
                          wrapperStyle={{
                            paddingHorizontal: 10,
                            marginBottom: 15,
                          }}
                          color={currentTheme?.theme?.orange}
                          style={{
                            marginLeft: 10,
                            fontSize:
                              currentTheme?.theme?.extra_small_font_size,
                          }}
                          iconSize={14}
                        >
                          {t("settings.qa.phoneNumberTwilioRegistration")}{" "}
                          {t("settings.qa.phoneNumberForVoiceCalls")}
                        </AppText>
                        <AppForms.AppFormField
                          showTextInputLabel
                          label={t("settings.qa.sipPhoneNumber")}
                          placeholder={t(
                            "settings.qa.sipPhoneNumberPlaceholder",
                          )}
                          keyboardtype="phone-pad"
                          name="sipPhoneNumber"
                          maxCharacters={20}
                        />
                        <AppText
                          style={{
                            paddingLeft: 5,
                            fontSize:
                              currentTheme?.theme?.extra_small_font_size,
                            color: currentTheme?.theme?.darkGrey,
                          }}
                          wrapperStyle={{
                            marginTop: -5,
                            marginBottom: 15,
                            justifyContent: "flex-start",
                          }}
                        >
                          {t("settings.qa.phoneNumberE164Format")}
                        </AppText>
                        <AppForms.AppFormSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingRight: 10,
                              paddingVertical: 0,
                              marginLeft: 5,
                              marginRight: 5,
                              marginTop: 0,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.enableInboundSipCalls")}
                          displayIsTicked={true}
                          name="enableInboundSip"
                          value={values?.enableInboundSip}
                          description={t(
                            "settings.qa.enableInboundSipCallsDesc",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                        <AppSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingRight: 10,
                              paddingVertical: 0,
                              marginLeft: 5,
                              marginRight: 5,
                              marginTop: 30,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.enableOutboundSipCalls")}
                          displayIsTicked={true}
                          isTicked={values?.enableOutboundSip === true}
                          onChange={handleOutboundSipToggle}
                          description={t(
                            "settings.qa.enableOutboundSipCallsDesc",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                      </>
                    )}
                  </View>
                </View>
              </SectionRenderer>
            </View>
          )}

          {_showBuilderSection("advanced") && (
            <View
              style={{
                zIndex: values?.mode == "voice" ? -5 : 0,
              }}
            >
              <SectionRenderer
                style={{ minWidth: 250, zIndex: -5 }}
                isExpandedOnLoad={builderActiveSection != null}
                title={t("settings.qa.advancedSettings")}
                icon={AppAssets.icons.experiment}
              >
                <View
                  style={{
                    paddingLeft: 10,
                  }}
                >
                  <View
                    style={{
                      marginLeft: 10,
                      marginTop: 10,
                    }}
                  >
                    <AppForms.AppFormTrackSlider
                      minimumValue={0}
                      maximumValue={1}
                      step={0.01}
                      label={t("settings.qa.temperature")}
                      name="temperature"
                    />
                    <AppForms.AppFormTrackSlider
                      step={1}
                      minimumValue={0}
                      maximumValue={4096}
                      label={t("settings.preferences.seed")}
                      name="llmSeed"
                      nullable
                      nullLabel={t("settings.qa.null", {
                        defaultValue: "Null",
                      })}
                      description={t("settings.qa.seedNotSetDesc", {
                        defaultValue:
                          "Leave unset for a random seed on every run.",
                      })}
                    />
                    <AppForms.AppFormTrackSlider
                      step={1}
                      maximumValue={getMaxTokens(
                        isContentCreator,
                        isChatBot,
                        getActiveWorkspace,
                        values,
                      )}
                      minimumValue={50}
                      label={t("settings.qa.maxOutputTokens")}
                      name="maxTokens"
                    />

                    <AppForms.AppFormTrackSlider
                      step={1}
                      maximumValue={50}
                      minimumValue={values?.useInterleavedReasoning ? 50 : 20}
                      label={t("settings.qa.maxHistory")}
                      name="maxHistory"
                    />

                    {/* Voice-specific timing settings */}
                    {showVoicePipelineConfigs && (
                      <>
                        <View
                          style={{
                            height: 1,
                            backgroundColor: currentTheme?.theme?.lightGrey,
                            marginVertical: 15,
                            marginRight: 10,
                          }}
                        />
                        <AppText
                          style={{
                            fontWeight: "bold",
                            marginBottom: 10,
                            marginLeft: 10,
                          }}
                        >
                          {t("settings.qa.voiceTimingSettings")}
                        </AppText>
                        <AppForms.AppFormTrackSlider
                          step={0.1}
                          maximumValue={5}
                          minimumValue={0.1}
                          label={t("settings.qa.minSilenceDurationSeconds")}
                          name="voiceMinSilenceDuration"
                          description={t(
                            "settings.qa.secondsOfSilenceBeforeTurnComplete",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />

                        <AppForms.AppFormTrackSlider
                          step={0.1}
                          maximumValue={5}
                          minimumValue={0.1}
                          label={t("settings.qa.minResponseDelaySeconds")}
                          name="voiceMinEndpointingDelay"
                          description={t(
                            "settings.qa.extraDelayBeforeCommittingTurn",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />

                        <AppForms.AppFormTrackSlider
                          step={0.1}
                          maximumValue={5}
                          minimumValue={0.1}
                          label={t(
                            "settings.qa.minInterruptionDurationSeconds",
                          )}
                          name="voiceMinInterruptionDuration"
                          description={t(
                            "settings.qa.minimumDurationForInterruption",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />

                        <AppForms.AppFormSwitch
                          wrapperStyle={[
                            {
                              maxHeight: 30,
                              paddingRight: 10,

                              paddingVertical: 0,
                              margin: 5,
                              marginTop: 20,
                            },
                          ]}
                          showTextInputLabel={true}
                          label={t("settings.qa.enableVoiceIntermissions")}
                          displayIsTicked={true}
                          name="enableVoiceIntermissions"
                          value={values?.enableVoiceIntermissions}
                          description={t(
                            "settings.qa.enableVoiceIntermissionsDesc",
                          )}
                          descriptionIcon={false}
                          descriptionColor={currentTheme?.theme?.mediumGrey}
                        />
                        <View
                          style={{
                            height: 1,
                            backgroundColor: currentTheme?.theme?.lightGrey,
                            marginVertical: 15,
                            marginRight: 10,
                          }}
                        />
                      </>
                    )}
                  </View>
                  <View>
                    {isChatBot && (
                      <>
                        {isKnowledgeEnabled && (
                          <AppForms.AppFormSwitch
                            wrapperStyle={[
                              {
                                maxHeight: 30,
                                paddingHorizontal: 10,

                                paddingVertical: 18,
                                margin: 5,
                                marginTop: 20,
                              },
                            ]}
                            showTextInputLabel={true}
                            label={t("settings.qa.plainTextOutput")}
                            displayIsTicked={true}
                            name="plainTextOutput"
                            value={values?.plainTextOutput}
                            description={t("settings.qa.plainTextOutputDesc")}
                            descriptionIcon={false}
                            descriptionColor={currentTheme?.theme?.mediumGrey}
                          />
                        )}
                        {(isChatBot || isContentCreator) &&
                          isKnowledgeEnabled &&
                          !isAgentOllama && (
                            <View>
                              <AppForms.AppFormSwitch
                                wrapperStyle={[
                                  {
                                    maxHeight: 30,
                                    paddingHorizontal: 10,

                                    paddingVertical: 18,
                                    margin: 5,
                                    marginTop: 20,
                                  },
                                ]}
                                showTextInputLabel={true}
                                label={t("settings.qa.showCitations")}
                                displayIsTicked={true}
                                name="showReferencesToInternalDocs"
                                value={values?.showReferencesToInternalDocs}
                                description={t("settings.qa.showCitationsDesc")}
                                descriptionIcon={false}
                                descriptionColor={
                                  currentTheme?.theme?.mediumGrey
                                }
                              />
                              {/* <AppForms.AppFormSwitch
                              wrapperStyle={[
                                {
                                  maxHeight: 30,
                                  paddingHorizontal: 10,

                                  paddingVertical: 18,
                                  margin: 5,
                                  marginTop: 20,
                                },
                              ]}
                              showTextInputLabel={true}
                              label={"Show citations"}
                              displayIsTicked={true}
                              name="showCitations"
                              value={values?.showCitations}
                            /> */}
                            </View>
                          )}
                        {values?.mode != "voice" && (
                          <>
                            {values?.mode != "planner" && !isAgentOllama && (
                              <>
                                <AppForms.AppFormSwitch
                                  wrapperStyle={[
                                    {
                                      maxHeight: 30,
                                      paddingHorizontal: 10,

                                      paddingVertical: 18,
                                      margin: 5,
                                      marginTop: 10,
                                    },
                                  ]}
                                  showTextInputLabel={true}
                                  label={t("settings.qa.promptEnhancement")}
                                  displayIsTicked={true}
                                  name="promptBuffering"
                                  value={values?.promptBuffering}
                                  description={t(
                                    "settings.qa.promptEnhancementDesc",
                                  )}
                                  descriptionIcon={false}
                                  descriptionColor={
                                    currentTheme?.theme?.mediumGrey
                                  }
                                />
                              </>
                            )}

                            {values?.mode == "chatter" && !isAgentOllama && (
                              <AppForms.AppFormSwitch
                                wrapperStyle={[
                                  {
                                    maxHeight: 30,
                                    paddingHorizontal: 10,

                                    paddingVertical: 18,
                                    margin: 5,
                                    marginTop: 20,
                                  },
                                ]}
                                showTextInputLabel={true}
                                label={t("settings.qa.entitiesExtractionMode")}
                                displayIsTicked={true}
                                name="hasEntityExtraction"
                                value={values?.hasEntityExtraction}
                                description={t(
                                  "settings.qa.entitiesExtractionModeDesc",
                                )}
                                descriptionIcon={false}
                                descriptionColor={
                                  currentTheme?.theme?.mediumGrey
                                }
                              />
                            )}
                            {values?.mode == "coder" && (
                              <View>
                                <AppForms.AppFormSwitch
                                  wrapperStyle={[
                                    {
                                      maxHeight: 30,
                                      paddingHorizontal: 10,

                                      paddingVertical: 18,
                                      margin: 5,
                                      marginTop: 20,
                                    },
                                  ]}
                                  showTextInputLabel={true}
                                  label={
                                    values?.agenticRAG
                                      ? t("settings.qa.showCodeBlocksAndErrors")
                                      : t("settings.qa.showCodeBlocks")
                                  }
                                  displayIsTicked={true}
                                  name="showCodeInExecution"
                                  value={values?.showCodeInExecution}
                                  description={
                                    values?.agenticRAG
                                      ? t(
                                          "settings.qa.showCodeBlocksAndErrorsDesc",
                                        )
                                      : t("settings.qa.showCodeBlocksDesc")
                                  }
                                  descriptionIcon={false}
                                  descriptionColor={
                                    currentTheme?.theme?.mediumGrey
                                  }
                                />
                              </View>
                            )}

                            <View>
                              <AppForms.AppFormSwitch
                                wrapperStyle={[
                                  {
                                    maxHeight: 30,
                                    paddingHorizontal: 10,

                                    paddingVertical: 18,
                                    margin: 5,
                                    marginTop: 20,
                                  },
                                ]}
                                showTextInputLabel={true}
                                label={t("settings.qa.showReasoning")}
                                displayIsTicked={true}
                                name="hideReasoning"
                                value={values?.hideReasoning}
                                description={t("settings.qa.showReasoningDesc")}
                                descriptionIcon={false}
                                descriptionColor={
                                  currentTheme?.theme?.mediumGrey
                                }
                              />
                              {!isAgentOllama &&
                                !values?.useInterleavedReasoning && (
                                  <AppForms.AppFormSwitch
                                    wrapperStyle={[
                                      {
                                        maxHeight: 30,
                                        paddingHorizontal: 10,

                                        paddingVertical: 18,
                                        margin: 5,
                                        marginTop: 20,
                                      },
                                    ]}
                                    showTextInputLabel={true}
                                    label={t("settings.qa.dynamicPlaceholders")}
                                    displayIsTicked={true}
                                    name="dynamicPlaceholderMessageOnLoading"
                                    value={
                                      values?.dynamicPlaceholderMessageOnLoading
                                    }
                                    description={t(
                                      "settings.qa.dynamicPlaceholdersDesc",
                                    )}
                                    descriptionIcon={false}
                                    descriptionColor={
                                      currentTheme?.theme?.mediumGrey
                                    }
                                  />
                                )}
                            </View>

                            <AppForms.AppFormSwitch
                              wrapperStyle={[
                                {
                                  maxHeight: 30,
                                  paddingHorizontal: 10,

                                  paddingVertical: 18,
                                  margin: 5,
                                  marginTop: 20,
                                },
                              ]}
                              showTextInputLabel={true}
                              label={t("settings.qa.showResponseTime")}
                              displayIsTicked={true}
                              name="showTimeForResponse"
                              value={values?.showTimeForResponse}
                              description={t(
                                "settings.qa.showResponseTimeDesc",
                              )}
                              descriptionIcon={false}
                              descriptionColor={currentTheme?.theme?.mediumGrey}
                            />

                            {!isAgentOllama && (
                              <AppForms.AppFormSwitch
                                wrapperStyle={[
                                  {
                                    maxHeight: 30,
                                    paddingHorizontal: 10,

                                    paddingVertical: 18,
                                    margin: 5,
                                    marginTop: 20,
                                  },
                                ]}
                                showTextInputLabel={true}
                                label={t("settings.qa.showRoutedModel")}
                                displayIsTicked={true}
                                name="showRoutedModel"
                                value={values?.showRoutedModel}
                                description={t(
                                  "settings.qa.showRoutedModelDesc",
                                )}
                                descriptionIcon={false}
                                descriptionColor={
                                  currentTheme?.theme?.mediumGrey
                                }
                              />
                            )}
                            {!isAgentOllama && (
                              <AppForms.AppFormSwitch
                                wrapperStyle={[
                                  {
                                    maxHeight: 30,
                                    paddingHorizontal: 10,

                                    paddingVertical: 18,
                                    margin: 5,
                                    marginTop: 20,
                                  },
                                ]}
                                showTextInputLabel={true}
                                label={t("settings.qa.allowAskUserQuestion")}
                                displayIsTicked={true}
                                name="allowAskQuestion"
                                value={values?.allowAskQuestion ?? false}
                                description={t(
                                  "settings.qa.allowAskUserQuestionDesc",
                                )}
                                descriptionIcon={false}
                                descriptionColor={
                                  currentTheme?.theme?.mediumGrey
                                }
                              />
                            )}
                            {["planner"].includes(values?.mode) && (
                              <View style={{ paddingLeft: 0 }}>
                                <AppForms.AppFormSwitch
                                  wrapperStyle={[
                                    {
                                      maxHeight: 30,
                                      paddingHorizontal: 10,

                                      paddingVertical: 18,
                                      margin: 5,
                                      marginTop: 20,
                                    },
                                  ]}
                                  showTextInputLabel={true}
                                  label={t(
                                    "settings.qa.structuredPlanPredefined",
                                  )}
                                  displayIsTicked={true}
                                  name="enableJsonOutput"
                                  value={values?.enableJsonOutput}
                                  description={t(
                                    "settings.qa.structuredPlanPredefinedDesc",
                                  )}
                                  descriptionIcon={false}
                                  descriptionColor={
                                    currentTheme?.theme?.mediumGrey
                                  }
                                />

                                {values?.enableJsonOutput && (
                                  <View
                                    style={{
                                      marginTop: 25,
                                      marginLeft: 10,
                                      marginRight: 10,
                                    }}
                                  >
                                    {/* Tab view to switch between JSON editor and Visual Builder */}
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        marginBottom: 15,
                                        padding: 4,
                                        borderRadius: 8,
                                      }}
                                    >
                                      <TouchableOpacity
                                        onPress={switchToJsonEditor}
                                        style={{
                                          flex: 1,
                                          paddingVertical: 10,
                                          paddingHorizontal: 16,
                                          borderRadius: 6,
                                          backgroundColor: !useVisualWorkflowBuilder
                                            ? currentTheme?.theme?.primary
                                            : "transparent",
                                          alignItems: "center",
                                        }}
                                      >
                                        <AppText
                                          style={{
                                            fontSize:
                                              currentTheme?.theme
                                                ?.small_font_size,
                                            fontWeight: !useVisualWorkflowBuilder
                                              ? "600"
                                              : "400",
                                            color: !useVisualWorkflowBuilder
                                              ? currentTheme?.theme?.white
                                              : currentTheme?.theme?.darkGrey,
                                          }}
                                        >
                                          {t("settings.qa.jsonEditor")}
                                        </AppText>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                        onPress={switchToVisualBuilder}
                                        style={{
                                          flex: 1,
                                          paddingVertical: 10,
                                          paddingHorizontal: 16,
                                          borderRadius: 6,
                                          backgroundColor: useVisualWorkflowBuilder
                                            ? currentTheme?.theme?.primary
                                            : "transparent",
                                          alignItems: "center",
                                        }}
                                      >
                                        <AppText
                                          style={{
                                            fontSize:
                                              currentTheme?.theme
                                                ?.small_font_size,
                                            fontWeight: useVisualWorkflowBuilder
                                              ? "600"
                                              : "400",
                                            color: useVisualWorkflowBuilder
                                              ? currentTheme?.theme?.white
                                              : currentTheme?.theme?.darkGrey,
                                          }}
                                        >
                                          {t("settings.qa.visualBuilder")}
                                        </AppText>
                                      </TouchableOpacity>
                                    </View>

                                    {useVisualWorkflowBuilder ? (
                                      <AgentWorkflowBuilder
                                        ref={workflowBuilderRef}
                                        showTextInputLabel
                                        isMandatory
                                        wrapperInputStyle={workflowWrapperStyle}
                                        label={t(
                                          "settings.qa.visualWorkflowBuilder",
                                        )}
                                        name="predictedPlanJson"
                                        darkMode={mode === "dark"}
                                        agents={_agents}
                                        value={values?.predictedPlanJson}
                                        onChange={handleWorkflowChange}
                                        onValidate={validateWorkflow}
                                      />
                                    ) : (
                                      <AppForms.AppFormFieldJSONV2
                                        showTextInputLabel
                                        isMandatory
                                        wrapperContainer={{
                                          height: 450,
                                          paddingTop: 10,
                                        }}
                                        textInputLabelStyle={{
                                          marginLeft: 10,
                                        }}
                                        json={values?.predictedPlanJson}
                                        mode="code"
                                        label={t(
                                          "settings.qa.staticPlanJsonStructure",
                                        )}
                                        name="predictedPlanJson"
                                        darkMode={mode === "dark"}
                                        onValidate={(json) => {
                                          const errors = [];
                                          if (
                                            !json ||
                                            typeof json !== "object"
                                          ) {
                                            errors.push({
                                              message: t(
                                                "settings.qa.planMustBeJsonObjectOrArray",
                                              ),
                                            });
                                            return errors;
                                          }
                                          let planSteps = [];
                                          if (Array.isArray(json)) {
                                            planSteps = json;
                                          } else if (
                                            json.plan &&
                                            Array.isArray(json.plan)
                                          ) {
                                            planSteps = json.plan;
                                          } else {
                                            errors.push({
                                              message: t(
                                                "settings.qa.planMustBeArrayOfSteps",
                                              ),
                                            });
                                            return errors;
                                          }
                                          if (planSteps.length === 0) {
                                            errors.push({
                                              message: t(
                                                "settings.qa.planMustContainStep",
                                              ),
                                            });
                                            return errors;
                                          }
                                          const requiredFields = [
                                            "step_id",
                                            "agent_id",
                                            "task_description",
                                          ];
                                          planSteps.forEach((step, index) => {
                                            requiredFields.forEach((field) => {
                                              if (
                                                step[field] === undefined ||
                                                step[field] === null ||
                                                step[field] === ""
                                              ) {
                                                errors.push({
                                                  message: t(
                                                    "settings.qa.stepMissingRequiredField",
                                                    { step: index + 1, field },
                                                  ),
                                                });
                                              }
                                            });
                                            if (
                                              step.step_id !== undefined &&
                                              (typeof step.step_id !==
                                                "number" ||
                                                !Number.isInteger(step.step_id))
                                            ) {
                                              errors.push({
                                                message: t(
                                                  "settings.qa.stepIdMustBeInteger",
                                                  { step: index + 1 },
                                                ),
                                              });
                                            }
                                            if (
                                              step.depends_on !== undefined &&
                                              !Array.isArray(step.depends_on)
                                            ) {
                                              errors.push({
                                                message: t(
                                                  "settings.qa.stepDependsOnMustBeArray",
                                                  { step: index + 1 },
                                                ),
                                              });
                                            }
                                            if (
                                              step.authorisation_required !==
                                                undefined &&
                                              typeof step.authorisation_required !==
                                                "boolean"
                                            ) {
                                              errors.push({
                                                message: t(
                                                  "settings.qa.stepAuthorisationMustBeBoolean",
                                                  { step: index + 1 },
                                                ),
                                              });
                                            }
                                            if (
                                              step.computer_required !==
                                                undefined &&
                                              typeof step.computer_required !==
                                                "boolean"
                                            ) {
                                              errors.push({
                                                message: t(
                                                  "settings.qa.stepComputerMustBeBoolean",
                                                  { step: index + 1 },
                                                ),
                                              });
                                            }
                                          });
                                          return errors;
                                        }}
                                      />
                                    )}

                                    {values?.predictedPlanJson &&
                                      JSON.stringify(values?.predictedPlanJson)
                                        .length > 12000 && (
                                        <AppText
                                          style={{
                                            color: currentTheme?.theme?.red,
                                            fontSize:
                                              currentTheme?.theme
                                                ?.extra_small_font_size,
                                          }}
                                          wrapperStyle={{
                                            marginTop: 5,
                                            marginLeft: 0,
                                          }}
                                        >
                                          JSON exceeds 12000 character limit (
                                          {
                                            JSON.stringify(
                                              values?.predictedPlanJson,
                                            ).length
                                          }{" "}
                                          characters)
                                        </AppText>
                                      )}
                                  </View>
                                )}
                              </View>
                            )}
                            {["retriever", "coder", "chatter"].includes(
                              values?.mode,
                            ) &&
                              values?.agenticRAG != true &&
                              !isAgentOllama && (
                                <View>
                                  <AppForms.AppFormSwitch
                                    wrapperStyle={[
                                      {
                                        maxHeight: 30,
                                        paddingHorizontal: 10,
                                        paddingVertical: 18,
                                        margin: 5,
                                        marginTop: 20,
                                      },
                                    ]}
                                    showTextInputLabel={true}
                                    label={t("settings.qa.jsonOutputStructure")}
                                    displayIsTicked={true}
                                    name="enableJsonOutput"
                                    value={values?.enableJsonOutput}
                                    description={t(
                                      "settings.qa.jsonOutputStructureDesc",
                                    )}
                                    descriptionIcon={false}
                                    descriptionColor={
                                      currentTheme?.theme?.mediumGrey
                                    }
                                  />

                                  {values?.enableJsonOutput && (
                                    <View
                                      style={{
                                        marginTop: 25,
                                        marginLeft: 5,
                                        marginRight: 10,
                                      }}
                                    >
                                      <AppForms.AppFormFieldJSONV2
                                        showTextInputLabel
                                        isMandatory
                                        wrapperContainer={{
                                          height: 450,
                                          paddingTop: 10,
                                        }}
                                        textInputLabelStyle={{
                                          marginLeft: 10,
                                        }}
                                        mode="code"
                                        label={t("settings.qa.jsonStructure")}
                                        name="jsonOutputStructure"
                                        darkMode={mode === "dark"}
                                        onValidate={(json) => {
                                          const errors = [];
                                          if (
                                            !json ||
                                            typeof json !== "object"
                                          ) {
                                            errors.push({
                                              message:
                                                "JSON Schema must be an object",
                                            });
                                            return errors;
                                          }
                                          // Validate required JSON Schema structure
                                          if (!json.type) {
                                            errors.push({
                                              message:
                                                'JSON Schema must have a "type" property',
                                            });
                                          }
                                          if (
                                            json.type === "object" &&
                                            !json.properties
                                          ) {
                                            errors.push({
                                              message:
                                                'Object type must have a "properties" field',
                                            });
                                          }
                                          if (
                                            json.properties &&
                                            typeof json.properties !== "object"
                                          ) {
                                            errors.push({
                                              message:
                                                '"properties" must be an object',
                                            });
                                          }
                                          if (
                                            json.required &&
                                            !Array.isArray(json.required)
                                          ) {
                                            errors.push({
                                              message:
                                                '"required" must be an array',
                                            });
                                          }
                                          // Validate that type is a valid JSON Schema type
                                          const validTypes = [
                                            "object",
                                            "array",
                                            "string",
                                            "number",
                                            "integer",
                                            "boolean",
                                            "null",
                                          ];
                                          if (
                                            json.type &&
                                            !validTypes.includes(json.type)
                                          ) {
                                            errors.push({
                                              message: `Invalid type "${
                                                json.type
                                              }". Must be one of: ${validTypes.join(
                                                ", ",
                                              )}`,
                                            });
                                          }
                                          return errors;
                                        }}
                                      />

                                      {values?.jsonOutputStructure &&
                                        JSON.stringify(
                                          values?.jsonOutputStructure,
                                        ).length > 12000 && (
                                          <AppText
                                            style={{
                                              color: currentTheme?.theme?.red,
                                              fontSize:
                                                currentTheme?.theme
                                                  ?.extra_small_font_size,
                                            }}
                                            wrapperStyle={{
                                              marginTop: 5,
                                              marginLeft: 5,
                                            }}
                                          >
                                            JSON exceeds 12000 character limit (
                                            {
                                              JSON.stringify(
                                                values?.jsonOutputStructure,
                                              ).length
                                            }{" "}
                                            characters)
                                          </AppText>
                                        )}
                                    </View>
                                  )}
                                </View>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </View>
                </View>
              </SectionRenderer>
            </View>
          )}

          {_showBuilderSection("access") && (
            <View style={{ zIndex: 0 }}>
              <SectionRenderer
                style={{ minWidth: 250 }}
                isExpandedOnLoad={builderActiveSection != null}
                title={t("settings.qa.accessRestriction", {
                  defaultValue: "Access restriction",
                })}
                icon={AppAssets.icons.lock}
              >
                <View style={{ paddingLeft: 10 }}>
                  <AppForms.AppFormSwitch
                    wrapperStyle={[
                      {
                        maxHeight: 30,
                        paddingHorizontal: 10,
                        paddingVertical: 18,
                        margin: 5,
                        marginTop: 20,
                      },
                    ]}
                    showTextInputLabel={true}
                    label={t("settings.qa.restrictAccess")}
                    displayIsTicked={true}
                    name="restrictedAccess"
                    value={values?.restrictedAccess}
                    description={t("settings.qa.restrictAccessDesc")}
                    descriptionIcon={false}
                    descriptionColor={currentTheme?.theme?.mediumGrey}
                  />
                  {values?.restrictedAccess && (
                    <AppForms.AppFormMultiSelect
                      key={`restrict-${lastOpened}`}
                      name="restrictedAccessUsers"
                      topErrorOffset={-21}
                      touchedByDefault
                      placeholder={t("settings.qa.restrictAccessPlaceholder")}
                      hidePlaceholder={true}
                      items={getWorkspaceUsers()}
                      uniqueKey="id"
                      showDropDowns={true}
                      readOnlyHeadings={true}
                      style={{ maxWidth: 800 }}
                      wrapperContainerStyle={{
                        flex: !isLargeScreen() ? 1 : 0.5,
                        zIndex: 20,
                        marginLeft: 15,
                        marginTop: 35,
                      }}
                      styles={colourStyles}
                      options={getWorkspaceUsers().map((u) => {
                        return {
                          ...u,
                          value: u?.id,
                          label: u?.username || u?.user_email,
                          category: "user",
                          backgroundColor: AppUtilities.stringToHslColor(
                            u?.username || u?.user_email,
                            71,
                          ),
                        };
                      })}
                    />
                  )}
                </View>
              </SectionRenderer>
            </View>
          )}

          {isChatBot &&
            !isAgentOllama &&
            _showBuilderSection("evaluate") &&
            !["computer"]?.includes(values?.mode) &&
            (!["voice", "planner"]?.includes(values?.mode) ? (
              <View style={{ zIndex: -1 }}>
                <SectionRenderer
                  style={{ minWidth: 250, zIndex: -1 }}
                  isExpandedOnLoad={builderActiveSection != null}
                  title={t("settings.qa.agentBenchmarksScoring")}
                  icon={AppAssets.icons.benchmark}
                >
                  {benchmarks?.length > 0 ? (
                    <AppTable
                      style={{
                        zIndex: -1,
                        height:
                          benchmarks?.length >= 50 ? height - 95 : height - 75,
                        paddingRight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                      headers={columns.map((col) => ({
                        ...col,
                        title: t(col.title),
                      }))}
                      items={benchmarks?.slice(0, 20)?.map((u) => {
                        return {
                          name: u?.benchmark?.name || "",
                          score:
                            parseFloat(
                              u?.batch?.batchMeta?.scoring_summary
                                ?.average_overall_score || 0,
                            )
                              ?.toFixed(2)
                              ?.toString() || "NA",
                          datetime:
                            dayjs(u?.batch?._lastChangedAt)?.format(
                              "DD-MM-YYYY HH:mm",
                            ) || " ",
                        };
                      })}
                    />
                  ) : (
                    <View
                      style={{
                        paddingLeft: 0,
                      }}
                    >
                      <AppPlaceholderView
                        wrapperStyle={{
                          width: "80%",
                          height: "100%",
                          marginVertical: "auto",
                          marginHorizontal: "auto",
                          alignItems: "center",
                        }}
                        size={30}
                        icon={AppAssets.icons.orbit}
                        mainCaption={t("settings.qa.nothingToShowYet")}
                        subCaption={t("settings.qa.runFirstBenchmark")}
                      />
                    </View>
                  )}
                  {isBuilderLayout && (
                    <View
                      style={{
                        alignItems: "center",
                        paddingLeft: 20,
                        paddingBottom: 16,
                      }}
                    >
                      <ToothFairyButton
                        title={t("settings.qa.runBenchmark", "Run benchmark")}
                        onPress={() =>
                          history.push(`/workspaces/${workspaceid}/batch`)
                        }
                        wrapperStyle={{ minWidth: 150, height: 36 }}
                      />
                    </View>
                  )}
                </SectionRenderer>
              </View>
            ) : (
              <View style={{ width: "100%" }}>
                <SectionUnavailable
                  icon={AppAssets.icons.benchmark}
                  title={t("settings.qa.evaluationUnavailableTitle", {
                    defaultValue:
                      "Evaluation history isn't available for this agent",
                  })}
                  message={
                    values?.mode === "planner"
                      ? t("settings.qa.evaluationUnavailableMessagePlanner", {
                          defaultValue:
                            "Planner agents can't be scored with benchmark evaluations. Switch the agent to a mode that supports them to run benchmarks and see evaluation history.",
                        })
                      : t("settings.qa.evaluationUnavailableMessage", {
                          defaultValue:
                            "Voice agents can't be scored with benchmark evaluations. Switch the agent to a mode that supports them to run benchmarks and see evaluation history.",
                        })
                  }
                  suggestedModes={[
                    t("settings.qa.modeRetriever", {
                      defaultValue: "Retriever",
                    }),
                    t("settings.qa.modeCoder", { defaultValue: "Coder" }),
                  ]}
                />
              </View>
            ))}
          {_showBuilderSection("triggers") &&
            (values?.mode != "voice" ? (
              <View style={{ zIndex: -1000, marginTop: -10 }}>
                <AgentSchedulingPanel
                  jobs={agentJobs}
                  triggers={agentTriggers}
                  isEdit={isEdit}
                  onItemClick={handleSchedulingChipPress}
                />
              </View>
            ) : (
              <View style={{ width: "100%" }}>
                <SectionUnavailable
                  icon={AppAssets.icons.orbit}
                  title={t("settings.qa.triggersUnavailableTitle", {
                    defaultValue:
                      "Trigger jobs aren't available for this agent",
                  })}
                  message={t("settings.qa.triggersUnavailableMessage", {
                    defaultValue:
                      "Voice agents can't be invoked by scheduled jobs or event triggers. Switch the agent to a mode that supports them to bind jobs and triggers.",
                  })}
                  suggestedModes={[
                    t("settings.qa.modeRetriever", {
                      defaultValue: "Retriever",
                    }),
                    t("settings.qa.modeCoder", { defaultValue: "Coder" }),
                  ]}
                />
              </View>
            ))}
          {_showBuilderSection("versions") && (
            <View style={{ zIndex: -1000 }}>
              <SectionRenderer
                style={{ minWidth: 250, zIndex: -1000 }}
                title={t("settings.qa.agentVersionsHistory")}
                icon={AppAssets.icons.history}
                onExpandedChange={handleHistoryExpandChange}
                isExpandedOnLoad={historyExpanded}
                isFullWidth={isBuilderLayout}
              >
                {_showBuilderSection("versions") && (
                  <View
                    style={{
                      zIndex: -1000,
                      marginHorizontal: 10,
                      marginBottom: 16,
                      paddingVertical: 4,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <AppText
                        preventTranslation
                        style={{ fontSize: 14, fontWeight: "600" }}
                        color={currentTheme?.theme?.font_color}
                      >
                        {t("settings.qa.versionField")}
                      </AppText>
                      {initialMapValues?.agentVersion ? (
                        <View
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 2,
                            borderRadius: 12,
                            backgroundColor: currentTheme?.theme?.orange,
                          }}
                        >
                          <AppText
                            preventTranslation
                            style={{ fontSize: 11, fontWeight: "600" }}
                            color={currentTheme?.theme?.white}
                          >
                            v{initialMapValues?.agentVersion} (
                            {t("settings.qa.versionCurrentLabel")})
                          </AppText>
                        </View>
                      ) : null}
                    </View>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <View style={{ flex: 1, maxWidth: 200, marginLeft: -5 }}>
                        <AppForms.AppFormField
                          showTextInputLabel={false}
                          keyboardtype="default"
                          autoCorrect={false}
                          name="agentVersion"
                          placeholder="1.2.3"
                        />
                      </View>
                      <Popable
                        numberOfLines={5}
                        backgroundColor={
                          currentTheme?.theme?.tooltipBackgroundColor
                        }
                        style={[
                          {
                            zIndex: 20,
                            maxWidth: 600,
                          },
                        ]}
                        content={t("settings.qa.useSuggestedVersion", {
                          version: semverHelper.suggestNext(
                            initialMapValues?.agentVersion,
                          ),
                        })}
                        action="hover"
                        position="bottom"
                      >
                        <AppIcon
                          icon={AppAssets?.icons["planner-auto"]}
                          isClickable
                          onPress={() =>
                            setFieldValue(
                              "agentVersion",
                              semverHelper.suggestNext(
                                initialMapValues?.agentVersion,
                              ),
                            )
                          }
                          wrapperStyle={{
                            minWidth: 120,
                            height: Constants.appButtonHeight - 10,
                            marginTop: -10,
                          }}
                        />
                      </Popable>
                    </View>
                    <AppText
                      preventTranslation
                      style={{
                        fontSize: 11,
                        marginTop: -8,
                        fontStyle: "italic",
                      }}
                      color={currentTheme?.theme?.mediumGrey}
                    >
                      {t("settings.qa.versionSuggestedHint")}
                    </AppText>
                  </View>
                )}
                {isLoadingSnapshots ? (
                  <View style={{ padding: 20, alignItems: "center" }}>
                    <AppText>{t("settings.qa.loadingHistory")}</AppText>
                  </View>
                ) : agentSnapshots?.length > 0 &&
                  getActiveWorkspace()?.subscriptionType != "base" &&
                  getActiveWorkspace()?.subscriptionType != "pro" ? (
                  <>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingHorizontal: 10,
                        paddingVertical: 8,
                        marginBottom: 8,
                        backgroundColor:
                          currentTheme?.theme?.softBackgroundColor || "#f5f5f5",
                        borderRadius: 8,
                      }}
                    >
                      <AppText
                        style={{
                          fontSize: 13,
                          color: currentTheme?.theme?.fontColor,
                        }}
                      >
                        {t("dashboard.versioning.totalVersions", {
                          count: agentSnapshots.length,
                        })}
                      </AppText>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <AppText
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: currentTheme?.theme?.primary,
                          }}
                        >
                          {t("dashboard.versioning.totalChanges", {
                            count: Object.values(snapshotChangeCounts).reduce(
                              (sum, count) => sum + count,
                              0,
                            ),
                          })}
                        </AppText>
                      </View>
                    </View>
                    <AppTable
                      showPagination
                      itemsPerPage={50}
                      onRowPress={handleVersionPress}
                      style={{
                        zIndex: -1,
                        width: "100%",
                        paddingRight: 0,
                      }}
                      headers={[
                        {
                          key: "datetime",
                          title: t("settings.qa.date"),
                          width: 0.25,
                        },
                        {
                          key: "changes",
                          title: t("settings.qa.changes"),
                          width: 0.1,
                        },
                        {
                          key: "user",
                          title: t("settings.qa.changedBy"),
                          width: 0.3,
                        },
                        {
                          key: "semantic",
                          title: t("settings.qa.versionLabel"),
                          width: 0.2,
                        },
                        {
                          key: "version",
                          title: "#",
                          width: 0.1,
                        },
                      ]}
                      items={agentSnapshots.map((snapshot, index) => {
                        let semanticVersion = "-";
                        try {
                          const parsed = snapshot?.snapshotData
                            ? JSON.parse(snapshot.snapshotData)
                            : null;
                          if (parsed?.agentVersion) {
                            semanticVersion = `v${parsed.agentVersion}`;
                          }
                        } catch (e) {
                          semanticVersion = "-";
                        }
                        return {
                          datetime:
                            dayjs(snapshot?.updatedAt)?.format(
                              "DD-MM-YYYY HH:mm",
                            ) || " ",
                          changes: `${snapshotChangeCounts[index] || 0}`,
                          user:
                            getUserName(snapshot?.updatedBy) ||
                            getUserName(snapshot?.createdBy) ||
                            "System",
                          semantic: semanticVersion,
                          version: snapshot?.snapshotVersion?.toString() || "-",
                        };
                      })}
                    />
                  </>
                ) : (
                  <View
                    style={{
                      paddingLeft: 0,
                      alignItems: "center",
                    }}
                  >
                    <AppPlaceholderView
                      wrapperStyle={{
                        width: "80%",
                        height: "100%",
                        marginVertical: "auto",
                        marginHorizontal: "auto",
                        alignItems: "center",
                      }}
                      mainCaption={t("settings.qa.noChangesRecordedYet")}
                      subCaption={t("settings.qa.noChangesRecordedYetSub")}
                      size={30}
                      icon={AppAssets.icons.orbit}
                    />
                  </View>
                )}
              </SectionRenderer>
            </View>
          )}
          <ToothFairyChoice
            showChoice={showRollbackChoice}
            setShowChoice={setShowRollbackChoice}
            caption={t("settings.qa.rollbackToVersion", {
              version: selectedSnapshot?.snapshotVersion || "1",
            })}
            subCaption={t("settings.qa.rollbackSubCaption")}
            confirmCaption={t("settings.qa.clickConfirmToLoadVersion")}
            cancelCaption={t("settings.qa.clickCancelToAbort")}
            onConfirmButtonPress={handleRollbackConfirm}
            onCancelButtonPress={() => {
              setShowRollbackChoice(false);
              setRollbackData(null);
              setSelectedSnapshot(null);
            }}
          />
          <VersionDiffSummary
            isVisible={showDiffSummary}
            onClose={handleDiffSummaryClose}
            onViewFullDiff={handleViewFullDiff}
            onRollback={handleDiffSummaryRollback}
            snapshotVersion={selectedSnapshot?.snapshotVersion || 1}
            changes={currentDiffChanges}
            datetime={
              dayjs(selectedSnapshot?.updatedAt)?.format("DD-MM-YYYY HH:mm") ||
              ""
            }
            changedBy={
              getUserName(selectedSnapshot?.updatedBy) ||
              getUserName(selectedSnapshot?.createdBy) ||
              "System"
            }
          />

          <VersionDiffView
            isVisible={showDiffView}
            onClose={() => {
              setShowDiffView(false);
              setRollbackData(null);
              setSelectedSnapshot(null);
              setCurrentDiffChanges(null);
            }}
            changes={currentDiffChanges}
            snapshotVersion={selectedSnapshot?.snapshotVersion || 1}
          />
        </View>

        <View style={{ alignItems: "center", zIndex: -1000 }}>
          {!isBuilderLayout && (
            <AppForms.AppSubmitButton
              isDisabled={isAsyncProcessInProgress}
              isEnabledOnFilled={isAIGenerated}
              title={_title()}
              awesomeIconColor={currentTheme?.theme?.white}
              backgroundColor={currentTheme?.theme?.primary}
              btnContentStyle={{
                paddingLeft: 0,
                paddingRight: 0,
              }}
              style={[
                {
                  height: Constants.appButtonHeight,
                  width: "auto",
                  shadowColor: currentTheme?.theme?.transparent,
                  marginHorizontal: 10,
                },
              ]}
              // btnLabelStyle={{ fontSize: 16 }}
              wrapperStyle={[
                {
                  zIndex: -1,
                  width: "fit-content",
                  minWidth: 150,
                  marginTop: 20,
                  height: Constants.appButtonHeight,
                },
              ]}
            />
          )}
          {isEdit && !isBuilderLayout && (
            <ToothFairyButton
              title={t("settings.qa.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      </View>
      <VoiceCloningModal
        visible={showVoiceCloningModal}
        onClose={() => setShowVoiceCloningModal(false)}
        onSave={(blob, deviceId) => {
          handleRecordingCompleted(blob);
          setShowVoiceCloningModal(false);
        }}
      />
    </AppToast.ToastProvider>
  );
};

export default QaSettingsAIView;
